import React from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  LoaderCircle,
  LockKeyhole,
  House,
  LogOut,
  Map as MapIcon,
  MapPin,
  MessageCircle,
  Package,
  RefreshCw,
  Route,
  Scale,
  Truck,
  Warehouse,
  Wrench,
} from "lucide-react";
import ConfirmationModal from "./ConfirmationModal.jsx";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import DriverPortalStepVisual from "./DriverPortalStepVisual.jsx";
import DriverPortalOperationsPager from "./DriverPortalOperationsPager.jsx";
import DriverPortalClientAddress from "./DriverPortalClientAddress.jsx";
import DriverPortalNextStop from "./DriverPortalNextStop.jsx";
import DriverPortalDateMenu from "./DriverPortalDateMenu.jsx";
import "./DriverPortalPage.css";
import "./DriverRoutePreview.css";
import { formatStreetCityAddress } from "../utils/fleetSegmentPresentation.js";
import RouteMapPanel from "./RouteMapPanel.jsx";
import useAccess from "../hooks/useAccess.jsx";
import { markSharedCompartmentAllocations } from "../utils/compartmentStock.js";
import useDriverPortalLiveSync from "../hooks/useDriverPortalLiveSync.js";
import useDriverRoutePreview, { DRIVER_ROUTE_PREVIEW_DURATION_MS } from "../hooks/useDriverRoutePreview.js";
import useSessionPresenceHeartbeat from "../hooks/useSessionPresenceHeartbeat.js";
import useSupabaseInactivityTimeout from "../hooks/useSupabaseInactivityTimeout.js";
import { DRIVER_PORTAL_LIVE_SYNC_POLL_INTERVAL_MS } from "../utils/driverPortalLiveSync.js";
import {
  buildDriverPortalQuantityDefaults,
  countDriverPortalOrders,
  groupConsecutiveDriverStops,
  isDriverPortalQuantityConfirmationCurrent,
  isDriverPortalVisitComplete,
  reconcileDriverPortalQuantityDrafts,
} from "../utils/driverPortalJourney.js";
import {
  DRIVER_PORTAL_PUBLICATION_WITHDRAWN_CODE,
  DRIVER_PORTAL_PUBLICATION_WITHDRAWN_MESSAGE,
  DRIVER_PORTAL_ROUTE_UPDATE_PENDING_MESSAGE,
  buildDriverPortalHomePath,
  buildDriverPortalDirectionsUrl,
  buildDriverPortalOriginOnlyTripFromAssignment,
  buildDriverPortalRouteTripFromAssignment,
  collectDriverPortalExecutionRevisions,
  resolveDriverPortalRouteDistanceKm,
  resolveDriverPortalRouteOriginAddress,
  resolveDriverPortalRouteSegmentIndex,
  normalizeDriverPortalMapsAddress,
  confirmDriverDelivery,
  confirmDriverLoading,
  loadDriverDispatchDayContext,
  loadDriverDeliveries,
  formatDriverPortalDispatchDateLabel,
  normalizeDriverPortalPublicationUnavailability,
  readDriverDispatchDateFromSearch,
  readDriverDispatchPeriodFromSearch,
  readDriverPreviewDriverUserIdFromSearch,
  resetDriverConfirmations,
  unconfirmDriverDelivery,
  unconfirmDriverLoading,
} from "../lib/driverPortal.js";
import { invalidatePageDataCacheEntries } from "../hooks/usePageData.js";
import useDriverDepartureAction from "../hooks/useDriverDepartureAction.js";
import DriverPortalDepartureStatus from "./DriverPortalDepartureStatus.jsx";
import DriverDepartureSimulation, { DriverDepartureTestToggle } from "./DriverDepartureSimulation.jsx";
import { departureForStep, departureForGroup } from "../utils/driverDepartures.js";
import { buildResultsLatestApiInvalidationUrls } from "../utils/dispatchDateUrls.js";
import { dispatchPlanningStateChange } from "../utils/planningCrossViewSync.js";
import { signOutSupabaseSession } from "../lib/supabaseClient.js";

function invalidateDriverPortalMutationCaches(dispatchDate = "", dispatchPeriod = "day") {
  const safeDispatchDate = typeof dispatchDate === "string" ? dispatchDate.trim() : "";
  const safeDispatchPeriod = typeof dispatchPeriod === "string" && dispatchPeriod.trim().toLowerCase() === "evening"
    ? "evening"
    : "day";
  const resultSearchParams = new URLSearchParams();
  if (safeDispatchDate) {
    resultSearchParams.set("dispatch_date", safeDispatchDate);
  }
  if (safeDispatchPeriod === "evening") {
    resultSearchParams.set("dispatch_period", "evening");
  }

  invalidatePageDataCacheEntries([
    "/api/data-manager/state",
    ...buildResultsLatestApiInvalidationUrls({
      resultUrl: safeDispatchDate ? `/results?${resultSearchParams.toString()}` : "",
    }),
  ]);
}

function notifyDispatchPlanningPortalMutation(action, meta = {}) {
  dispatchPlanningStateChange("portail", {
    action,
    ...(meta && typeof meta === "object" ? meta : {}),
  });
}

const quantityFormatter = new Intl.NumberFormat("fr-CA", {
  maximumFractionDigits: 3,
  minimumFractionDigits: 0,
});

const wholeNumberFormatter = new Intl.NumberFormat("fr-CA", {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

const timeFormatter = new Intl.DateTimeFormat("fr-CA", {
  hour: "numeric",
  minute: "2-digit",
});

const DRIVER_TWO_LINE_CLAMP_STYLE = {
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  display: "-webkit-box",
  overflow: "hidden",
};

const DRIVER_PORTAL_ROUTE_UPDATE_PENDING_TITLE = "Itinéraire en cours de mise à jour";
const DRIVER_PORTAL_ROUTE_UPDATE_PENDING_GUIDANCE_TITLE = "Une mise à jour est en cours";
const DRIVER_PORTAL_ROUTE_UPDATE_PENDING_GUIDANCE = "Au besoin, vous pouvez contacter votre répartiteur.";
const DRIVER_PORTAL_PUBLICATION_WITHDRAWN_TITLE = "Journée retirée du portail";
const DRIVER_PORTAL_PUBLICATION_WITHDRAWN_GUIDANCE = "Il pourra publier de nouveau cette journée lorsqu’elle sera prête.";
const DRIVER_PORTAL_BRAND_BLOB_CLASS_NAME = "absolute -right-12 -top-16 h-40 w-40 rounded-full bg-accent/35 blur-2xl";
const DRIVER_PORTAL_BRAND_PILL_CLASS_NAME = "flex shrink-0 items-center justify-center border border-white/20 bg-accent text-white";
const DRIVER_PORTAL_BRAND_ACTION_CLASS_NAME = "bg-accent text-white shadow-[0_16px_30px_rgba(15,92,87,0.22)] hover:bg-accent-strong";
const DRIVER_PORTAL_BRAND_CONFIRMED_BUTTON_CLASS_NAME = `border border-accent ${DRIVER_PORTAL_BRAND_ACTION_CLASS_NAME}`;

function normalizeDriverPortalTruckIds(values = []) {
  return Array.from(new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => typeof value === "string" ? value.trim() : "")
      .filter(Boolean),
  ));
}

const DRIVER_PORTAL_STEP_THEME_BY_TYPE = {
  delivery: {
    clientClassName: "text-[var(--dispatch-segment-delivery-ink)]",
    panelClassName: "driver-stop-tone driver-stop-tone--delivery",
    toneClassName: "text-[var(--dispatch-segment-delivery-ink)]",
  },
  loading: {
    clientClassName: "text-[var(--dispatch-segment-loading-ink)]",
    panelClassName: "driver-stop-tone driver-stop-tone--loading",
    toneClassName: "text-[var(--dispatch-segment-loading-ink)]",
  },
  default: {
    clientClassName: "text-slate-500",
    panelClassName: "border-slate-200 bg-white",
    toneClassName: "text-slate-500",
  },
};

const DRIVER_PORTAL_SIGNAL_TONE_CLASS_NAMES = {
  aspiration: "border-[rgba(234,88,12,0.22)] bg-[rgba(255,247,237,0.98)] text-[rgb(194,65,12)]",
  priority: "border-[rgba(180,83,9,0.18)] bg-[rgba(255,251,235,0.96)] text-[rgb(180,83,9)]",
  productChange: "border-[rgba(15,92,87,0.22)] bg-[rgba(240,253,250,0.98)] text-[rgb(15,118,110)]",
};

function normalizeDriverPortalPriorityLabel(value) {
  const normalizedValue = typeof value === "string"
    ? value.trim().toUpperCase()
    : Number.isFinite(Number(value))
      ? `P${Number(value)}`
      : "";

  return /^(P1|P2|P3)$/.test(normalizedValue) ? normalizedValue : "";
}

function resolveDriverPortalBooleanFlag(value) {
  if (value === true || value === 1) {
    return true;
  }

  if (value === false || value === 0 || value == null) {
    return false;
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) {
      return false;
    }
    if (["false", "0", "no", "non", "none", "aucun", "aucune"].includes(normalizedValue)) {
      return false;
    }
    if (["true", "1", "yes", "oui", "required", "requis", "requise"].includes(normalizedValue)) {
      return true;
    }
  }

  return Boolean(value);
}

function getDriverPortalStepTheme(stepType = "") {
  return DRIVER_PORTAL_STEP_THEME_BY_TYPE[stepType] || DRIVER_PORTAL_STEP_THEME_BY_TYPE.default;
}

function renderDriverPortalProductBadge(productLabel = "", stepType = "") {
  const normalizedLabel = typeof productLabel === "string" ? productLabel.trim() : "";
  if (!normalizedLabel) {
    return null;
  }

  const className = stepType === "loading"
    ? "max-w-[9.5rem] truncate rounded-full border border-[rgba(var(--route-loading-rgb),0.28)] bg-[rgba(var(--route-loading-rgb),0.14)] px-3 py-1 text-[0.72rem] font-black uppercase tracking-[0.12em] text-[var(--route-loading-strong)]"
    : "max-w-[9.5rem] truncate rounded-full border border-[rgba(var(--route-delivery-rgb),0.28)] bg-[rgba(var(--route-delivery-rgb),0.14)] px-3 py-1 text-[0.72rem] font-black uppercase tracking-[0.12em] text-[var(--route-delivery-strong)]";

  return (
    <span className={`driver-product-badge ${className}`} title={normalizedLabel}>
      {normalizedLabel}
    </span>
  );
}

function buildDriverPortalCommandSignalItems(command) {
  const signalItems = [];

  if (command?.requiresAspirator) {
    signalItems.push({
      key: `${command?.key || "command"}:aspiration`,
      label: command?.aspiratorRequirementLabel || "Aspiration requise",
      tone: "aspiration",
      title: command?.aspiratorRequirementLabel || "Aspiration requise",
    });
  }

  if (command?.hasManualMixBoundaryBefore || command?.manualMixCurrentProductLabel || command?.manualMixBoundaryTitle) {
    signalItems.push({
      key: `${command?.key || "command"}:product-change`,
      label: command?.manualMixCurrentProductLabel || "Mix produit",
      tone: "productChange",
      title: command?.manualMixBoundaryTitle || "Changement de produit",
    });
  }

  return signalItems;
}

function renderDriverPortalSignalBadges(command, {
  badgeClassName = "",
  className = "mt-3 flex flex-wrap gap-2",
} = {}) {
  const signalItems = buildDriverPortalCommandSignalItems(command);
  if (!signalItems.length) {
    return null;
  }

  return (
    <div className={className}>
      {signalItems.map((signalItem) => (
        <span
          key={signalItem.key}
          title={signalItem.title}
          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-widest ${DRIVER_PORTAL_SIGNAL_TONE_CLASS_NAMES[signalItem.tone] || "border-slate-200 bg-slate-100 text-slate-700"} ${badgeClassName}`}
        >
          {signalItem.label}
        </span>
      ))}
    </div>
  );
}

function formatQuantity(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "--";
  }

  return `${quantityFormatter.format(numericValue)} t`;
}

function hasDriverPortalQuantityDifference(plannedQuantityTons, actualQuantityTons) {
  return actualQuantityTons != null
    && Number.isFinite(Number(actualQuantityTons))
    && plannedQuantityTons != null
    && Number.isFinite(Number(plannedQuantityTons))
    && Math.abs(Math.round(Number(actualQuantityTons) * 1000) - Math.round(Number(plannedQuantityTons) * 1000)) > 1;
}

function resolveDriverStopCommandEffectiveQuantityTons(command, stepType) {
  const plannedQuantityTons = Number.isFinite(Number(command?.quantityTons)) ? Number(command.quantityTons) : null;
  if (stepType === "loading") {
    if (command?.isConfirmed && command?.actualLoadedQuantityTons != null && Number.isFinite(Number(command.actualLoadedQuantityTons))) {
      return Number(command.actualLoadedQuantityTons);
    }
    return plannedQuantityTons;
  }

  if (command?.isConfirmed && command?.actualDeliveredQuantityTons != null && Number.isFinite(Number(command.actualDeliveredQuantityTons))) {
    return Number(command.actualDeliveredQuantityTons);
  }

  if (command?.actualLoadedQuantityTons != null && Number.isFinite(Number(command.actualLoadedQuantityTons))) {
    return Number(command.actualLoadedQuantityTons);
  }

  return plannedQuantityTons;
}

function resolveDriverPortalDeliveryPlannedDisplayQuantity(command) {
  const plannedQuantityTons = Number.isFinite(Number(command?.quantityTons)) ? Number(command.quantityTons) : null;
  const actualDeliveredQuantityTons = command?.actualDeliveredQuantityTons != null && Number.isFinite(Number(command.actualDeliveredQuantityTons))
    ? Number(command.actualDeliveredQuantityTons)
    : null;
  const actualLoadedQuantityTons = command?.actualLoadedQuantityTons != null && Number.isFinite(Number(command.actualLoadedQuantityTons))
    ? Number(command.actualLoadedQuantityTons)
    : null;

  if (command?.isConfirmed && hasDriverPortalQuantityDifference(plannedQuantityTons, actualDeliveredQuantityTons)) {
    return {
      plannedQuantityTons,
      actualQuantityTons: actualDeliveredQuantityTons,
      showAdjustedQuantity: true,
    };
  }

  if (!command?.isConfirmed && hasDriverPortalQuantityDifference(plannedQuantityTons, actualLoadedQuantityTons)) {
    return {
      plannedQuantityTons,
      actualQuantityTons: actualLoadedQuantityTons,
      showAdjustedQuantity: true,
    };
  }

  return {
    plannedQuantityTons,
    actualQuantityTons: null,
    showAdjustedQuantity: false,
  };
}

function resolveDriverPortalDeliveryLoadedQuantityTons(command) {
  if (command?.actualLoadedQuantityTons != null && Number.isFinite(Number(command.actualLoadedQuantityTons))) {
    return Number(command.actualLoadedQuantityTons);
  }
  if (command?.quantityTons != null && Number.isFinite(Number(command.quantityTons))) {
    return Number(command.quantityTons);
  }
  return null;
}

function doesDriverPortalDeliveryExceedLoadedQuantity(command) {
  if (!command?.isConfirmed) {
    return false;
  }
  const deliveredQuantity = command?.actualDeliveredQuantityTons != null && Number.isFinite(Number(command.actualDeliveredQuantityTons))
    ? Number(command.actualDeliveredQuantityTons)
    : null;
  const loadedQuantity = resolveDriverPortalDeliveryLoadedQuantityTons(command);
  return deliveredQuantity != null
    && loadedQuantity != null
    && deliveredQuantity > loadedQuantity
    && hasDriverPortalQuantityDifference(loadedQuantity, deliveredQuantity);
}

function sumDriverStopGroupQuantityTons(commandRows, stepType, { effective = false } = {}) {
  return (Array.isArray(commandRows) ? commandRows : []).reduce((total, command) => {
    const quantityTons = effective
      ? resolveDriverStopCommandEffectiveQuantityTons(command, stepType)
      : (Number.isFinite(Number(command?.quantityTons)) ? Number(command.quantityTons) : null);
    return Number.isFinite(quantityTons) ? total + quantityTons : total;
  }, 0);
}

function renderDriverPortalPlannedQuantityRow({
  showAdjustedQuantity = false,
  plannedQuantityTons = null,
  actualQuantityTons = null,
  className = "min-w-0 truncate",
}) {
  if (plannedQuantityTons == null || !Number.isFinite(Number(plannedQuantityTons))) {
    return (
      <p className={className}>
        <span className="font-semibold text-slate-700">Prévu :</span> --
      </p>
    );
  }

  const showActualQuantity = showAdjustedQuantity && hasDriverPortalQuantityDifference(plannedQuantityTons, actualQuantityTons);

  return (
    <p className={className}>
      <span className="font-semibold text-slate-700">Prévu :</span>
      {showActualQuantity ? (
        <span className="ml-1 inline-flex items-center gap-2">
          <span className="text-[1.08rem] font-black text-amber-700">{formatQuantity(actualQuantityTons)}</span>
          <span className="relative inline-block text-[1.08rem] font-black text-slate-400">
            {formatQuantity(plannedQuantityTons)}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage: 'linear-gradient(to top right, transparent calc(50% - 0.5px), #94a3b8 calc(50% - 0.5px), #94a3b8 calc(50% + 0.5px), transparent calc(50% + 0.5px))',
              }}
            />
          </span>
        </span>
      ) : (
        <span className="ml-1 text-[1.08rem] font-black text-slate-900">{formatQuantity(plannedQuantityTons)}</span>
      )}
    </p>
  );
}

function renderDriverPortalOrderReferenceRow({ workOrderNumber = "", orderId = "" } = {}) {
  return (
    <div className="driver-order-reference">
      <p title={orderId || undefined}>
        <span className="font-semibold text-slate-700">Bon Cmd :</span> {orderId || "--"}
      </p>
      {workOrderNumber ? (
        <p title={workOrderNumber}>
          <span className="font-semibold text-slate-700">Bon travail :</span> {workOrderNumber}
        </p>
      ) : null}
    </div>
  );
}

function formatCompartmentAllocations(compartmentAllocations = []) {
  const normalizedAllocations = (Array.isArray(compartmentAllocations) ? compartmentAllocations : [])
    .map((allocation) => ({
      position: Number(allocation?.position),
      quantityTons: Number(allocation?.quantity_tons ?? allocation?.quantityTons),
    }))
    .filter((allocation) => Number.isFinite(allocation.position) && allocation.position > 0 && Number.isFinite(allocation.quantityTons) && allocation.quantityTons > 0)
    .sort((left, right) => left.position - right.position);

  if (!normalizedAllocations.length) {
    return "";
  }

  return normalizedAllocations.map((allocation) => `C${allocation.position} ${quantityFormatter.format(allocation.quantityTons)} t`).join(" · ");
}

function renderCompartmentAllocations(compartmentAllocations = [], { isDelivery = false } = {}) {
  const normalizedAllocations = (Array.isArray(compartmentAllocations) ? compartmentAllocations : [])
    .map((allocation) => ({
      position: Number(allocation?.position),
      quantityTons: Number(allocation?.quantity_tons ?? allocation?.quantityTons),
      key: [allocation?.compartment_id, allocation?.order_id, allocation?.loading_step_id, allocation?.delivery_id].filter(Boolean).join(":"),
      isShared: allocation?.is_shared === true,
    }))
    .filter((allocation) => Number.isFinite(allocation.position) && allocation.position > 0 && Number.isFinite(allocation.quantityTons) && allocation.quantityTons > 0)
    .sort((left, right) => left.position - right.position);

  if (!normalizedAllocations.length) {
    return null;
  }

  return normalizedAllocations.map((allocation, index) => (
    <div key={`${allocation.key || allocation.position}:${index}`} className="driver-compartment">
      <dt><span className="sr-only">Compartiment </span>C{allocation.position}</dt>
      <dd>
        {`${quantityFormatter.format(allocation.quantityTons)} t`}
        {allocation.isShared ? (isDelivery
          ? ` — décharger seulement ${quantityFormatter.format(allocation.quantityTons)} t (compartiment partagé)`
          : " (partagé)") : null}
      </dd>
    </div>
  ));
}

function parseQuantityInput(value) {
  const normalizedValue = String(value || "").trim().replace(",", ".");
  if (!normalizedValue) {
    return null;
  }

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : Number.NaN;
}

function resolveDeliveryState(delivery) {
  if (!delivery || typeof delivery !== "object") {
    return {
      badgeClassName: "border-slate-200 bg-slate-100 text-slate-700",
      helperText: "Chargement de l'état",
      kind: "neutral",
      title: "Livraison",
    };
  }

  if (delivery.is_dispatcher_delivered || delivery.dispatcher_execution_status === "delivered") {
    return {
      badgeClassName: "border-emerald-200 bg-emerald-50 text-emerald-700",
      helperText: delivery.actual_delivered_quantity_tons != null
        ? `Tonnage confirmé : ${formatQuantity(delivery.actual_delivered_quantity_tons)}`
        : "Livraison confirmée par le chauffeur.",
      kind: "success",
      title: "Livrée",
    };
  }

  // Portail: confirmation_portail prend priorité sur le gel pour l'affichage côté chauffeur
  const portalConfirmation = typeof delivery?.confirmation_portail === "string"
    ? delivery.confirmation_portail.trim().toLowerCase()
    : "";
  if (portalConfirmation === "confirmée" || portalConfirmation === "confirmee") {
    return {
      badgeClassName: "border-sky-200 bg-sky-50 text-sky-700",
      helperText: "Confirmée par vos soins. Le répartiteur peut la dégeler au besoin.",
      kind: "ready",
      title: "Confirmée",
    };
  }
  if (portalConfirmation === "à_revalider" || portalConfirmation === "a_revalider") {
    return {
      badgeClassName: "border-amber-200 bg-amber-50 text-amber-700",
      helperText: "Le répartiteur a dégelé cette commande. Revalidez la livraison.",
      kind: "warning",
      title: "À revalider",
    };
  }

  if (delivery.is_delivery_blocked) {
    return {
      badgeClassName: "border-amber-200 bg-amber-50 text-amber-700",
      helperText: delivery.blocking_reason || "Le chargement doit être confirmé avant la livraison.",
      kind: "warning",
      title: "En attente",
    };
  }

  const gelOrigine = typeof delivery?.gel_origine === "string"
    ? delivery.gel_origine.trim().toLowerCase()
    : "";
  const isPortalDispatcherFreeze = gelOrigine === "portail";
  if (
    isPortalDispatcherFreeze
    && (delivery.is_dispatcher_frozen || delivery.dispatcher_execution_status === "frozen")
  ) {
    return {
      badgeClassName: "border-amber-200 bg-amber-50 text-amber-700",
      helperText: "Cette livraison est gelée côté dispatch.",
      kind: "warning",
      title: "Gelée",
    };
  }

  return {
    badgeClassName: "border-sky-200 bg-sky-50 text-sky-700",
    helperText: "Prête à être confirmée après la livraison.",
    kind: "ready",
    title: "À confirmer",
  };
}

function formatPercent(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "";
  }

  return `${Math.round(numericValue)} %`;
}

function formatStepTime(value) {
  if (typeof value === "string" && /^\d{1,2}:\d{2}/.test(value.trim())) {
    return value.trim().slice(0, 5);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return timeFormatter.format(date);
}

function formatDriverPortalScheduleTime(value) {
  if (typeof value === "string" && /^\d{1,2}:\d{2}/.test(value.trim())) {
    const [hours, minutes] = value.trim().slice(0, 5).split(":");
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function normalizeDriverPortalAddress(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeDriverPortalDispatchDate(value) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  return /^\d{4}-\d{2}-\d{2}$/.test(normalizedValue) ? normalizedValue : "";
}

function normalizeDriverPortalDispatchPeriod(value, { allowEmpty = false } = {}) {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (normalizedValue === "evening" || normalizedValue === "soir") {
    return "evening";
  }
  if (normalizedValue === "day" || normalizedValue === "jour") {
    return "day";
  }
  return allowEmpty ? "" : "day";
}

function normalizeDriverPortalPublicationRevision(value) {
  const numericValue = Number(value);
  return Number.isInteger(numericValue) && numericValue > 0 ? numericValue : null;
}

function buildDriverPortalPublicationCursorSignature(publicationRevision, publicationRevisions = {}) {
  const truckSignature = Object.entries(publicationRevisions && typeof publicationRevisions === "object" ? publicationRevisions : {})
    .map(([truckId, revision]) => [
      String(truckId || "").trim(),
      normalizeDriverPortalPublicationRevision(revision),
    ])
    .filter(([truckId, revision]) => Boolean(truckId) && revision !== null)
    .sort(([leftTruckId], [rightTruckId]) => leftTruckId.localeCompare(rightTruckId))
    .map(([truckId, revision]) => `${truckId}:${revision}`)
    .join("|");
  const normalizedRevision = normalizeDriverPortalPublicationRevision(publicationRevision);
  if (normalizedRevision === null && !truckSignature) {
    return "";
  }
  return `${normalizedRevision || ""}::${truckSignature}`;
}

function shouldResetDriverPortalFocusAfterPublicationChange(previousSignature, nextSignature) {
  return Boolean(previousSignature && nextSignature && previousSignature !== nextSignature);
}

function buildDriverPortalDispatchSelectionKey(dispatchDate = "", dispatchPeriod = "day") {
  const normalizedDispatchDate = normalizeDriverPortalDispatchDate(dispatchDate);
  if (!normalizedDispatchDate) {
    return "";
  }
  return `${normalizedDispatchDate}::${normalizeDriverPortalDispatchPeriod(dispatchPeriod)}`;
}

function parseDriverPortalDispatchSelectionKey(value = "") {
  const [dispatchDate = "", dispatchPeriod = ""] = String(value || "").split("::", 2);
  return {
    dispatchDate: normalizeDriverPortalDispatchDate(dispatchDate),
    dispatchPeriod: normalizeDriverPortalDispatchPeriod(dispatchPeriod),
  };
}

const DRIVER_PORTAL_TORONTO_DATE_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  day: "2-digit",
  month: "2-digit",
  timeZone: "America/Toronto",
  year: "numeric",
});

function buildCurrentDriverPortalDispatchDate() {
  const formattedParts = DRIVER_PORTAL_TORONTO_DATE_FORMATTER.formatToParts(new Date());
  const year = formattedParts.find((part) => part.type === "year")?.value || "";
  const month = formattedParts.find((part) => part.type === "month")?.value || "";
  const day = formattedParts.find((part) => part.type === "day")?.value || "";
  return `${year}-${month}-${day}`;
}

function buildDriverDispatchDayOptionLabel(dispatchDay, dispatchDate, todayDispatchDate) {
  const normalizedDispatchDate = normalizeDriverPortalDispatchDate(dispatchDate);
  if (!normalizedDispatchDate) {
    return "";
  }

  const baseLabel = typeof dispatchDay?.label === "string" && dispatchDay.label.trim()
    ? dispatchDay.label.trim()
    : formatDriverPortalDispatchDateLabel(normalizedDispatchDate);

  const dispatchPeriodLabel = normalizeDriverPortalDispatchPeriod(dispatchDay?.dispatch_period) === "evening"
    ? "Soir"
    : "Jour";
  const labelParts = [baseLabel, dispatchPeriodLabel];
  if (normalizedDispatchDate === todayDispatchDate) {
    labelParts.push("Aujourd'hui");
  }
  return labelParts.join(" · ");
}

function buildDriverDispatchDateOptions(dispatchDays = [], todayDispatchDate = "") {
  const optionBySelection = new Map();

  (Array.isArray(dispatchDays) ? dispatchDays : []).forEach((dispatchDay) => {
    const normalizedDispatchDate = normalizeDriverPortalDispatchDate(dispatchDay?.dispatch_date);
    const normalizedDispatchPeriod = normalizeDriverPortalDispatchPeriod(dispatchDay?.dispatch_period);
    const selectionKey = buildDriverPortalDispatchSelectionKey(normalizedDispatchDate, normalizedDispatchPeriod);
    if (!selectionKey || optionBySelection.has(selectionKey)) {
      return;
    }

    optionBySelection.set(selectionKey, {
      label: buildDriverDispatchDayOptionLabel(dispatchDay, normalizedDispatchDate, todayDispatchDate),
      value: selectionKey,
    });
  });

  return Array.from(optionBySelection.values()).sort((leftOption, rightOption) => rightOption.value.localeCompare(leftOption.value));
}

function resolveDriverPortalDefaultDispatchSelection(payload = null) {
  const dispatchDays = Array.isArray(payload?.dispatch_days) ? payload.dispatch_days : [];
  const selectionKeys = dispatchDays
    .map((dispatchDay) => buildDriverPortalDispatchSelectionKey(
      dispatchDay?.dispatch_date,
      dispatchDay?.dispatch_period,
    ))
    .filter(Boolean);
  const preferredDate = normalizeDriverPortalDispatchDate(payload?.default_dispatch_date);
  const preferredPeriod = normalizeDriverPortalDispatchPeriod(payload?.default_dispatch_period);
  const preferredSelectionKey = buildDriverPortalDispatchSelectionKey(preferredDate, preferredPeriod);
  const selectedKey = selectionKeys.includes(preferredSelectionKey)
    ? preferredSelectionKey
    : selectionKeys.find((selectionKey) => selectionKey.startsWith(`${preferredDate}::`))
      || selectionKeys[0]
      || "";

  return parseDriverPortalDispatchSelectionKey(selectedKey);
}

function selectLatestDriverPortalPublicationUnavailability(values = []) {
  return (Array.isArray(values) ? values : [])
    .map((value) => normalizeDriverPortalPublicationUnavailability(value))
    .filter(Boolean)
    .sort((leftNotice, rightNotice) => {
      const leftWithdrawnAt = Date.parse(leftNotice.withdrawnAt || "") || 0;
      const rightWithdrawnAt = Date.parse(rightNotice.withdrawnAt || "") || 0;
      return rightWithdrawnAt - leftWithdrawnAt;
    })[0] || null;
}

async function loadAllDriverPublishedDispatchPeriods({
  dispatchDate = "",
  previewDriverUserId = "",
} = {}) {
  const periodResults = await Promise.all(
    ["day", "evening"].map(async (dispatchPeriod) => {
      try {
        const payload = await loadDriverDispatchDayContext({
          dispatchDate,
          dispatchPeriod,
          previewDriverUserId,
        });
        return { dispatchPeriod, error: null, payload };
      } catch (error) {
        return { dispatchPeriod, error, payload: null };
      }
    }),
  );
  const availableResults = periodResults.filter((result) => result.payload && typeof result.payload === "object");
  if (availableResults.length === 0) {
    throw periodResults.find((result) => normalizeDriverPortalPublicationUnavailability(result.error))?.error
      || periodResults.find((result) => result.error)?.error
      || new Error("Impossible de charger les journées publiées.");
  }

  const dispatchDaysBySelection = new Map();
  availableResults.forEach(({ dispatchPeriod, payload }) => {
    (Array.isArray(payload.dispatch_days) ? payload.dispatch_days : []).forEach((dispatchDay) => {
      const normalizedDispatchDate = normalizeDriverPortalDispatchDate(dispatchDay?.dispatch_date);
      const normalizedDispatchPeriod = normalizeDriverPortalDispatchPeriod(
        dispatchDay?.dispatch_period || dispatchPeriod,
      );
      const selectionKey = buildDriverPortalDispatchSelectionKey(
        normalizedDispatchDate,
        normalizedDispatchPeriod,
      );
      if (!selectionKey || dispatchDaysBySelection.has(selectionKey)) {
        return;
      }
      dispatchDaysBySelection.set(selectionKey, {
        ...dispatchDay,
        dispatch_period: normalizedDispatchPeriod,
      });
    });
  });

  const preferredResult = availableResults.find(({ payload }) => Array.isArray(payload.dispatch_days) && payload.dispatch_days.length > 0)
    || availableResults[0];
  const preferredDefaultDate = normalizeDriverPortalDispatchDate(preferredResult.payload?.default_dispatch_date)
    || normalizeDriverPortalDispatchDate(dispatchDate)
    || normalizeDriverPortalDispatchDate(dispatchDaysBySelection.values().next().value?.dispatch_date);
  const publicationUnavailability = dispatchDaysBySelection.size === 0
    ? selectLatestDriverPortalPublicationUnavailability(
        periodResults.flatMap(({ error, payload }) => [payload?.publication_unavailability, error]),
      )
    : null;

  return {
    ...preferredResult.payload,
    default_dispatch_date: preferredDefaultDate || null,
    default_dispatch_period: preferredResult.dispatchPeriod,
    dispatch_days: Array.from(dispatchDaysBySelection.values()),
    publication_unavailability: publicationUnavailability,
  };
}

function normalizeDriverPortalPreviewDriverUserId(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function readDriverPortalScreenFromSearch(search = "") {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  const screen = params.get("screen")?.trim() || "";
  return screen === "journey" || screen === "stops" ? screen : "";
}

function normalizeDriverPortalFocusDeliveryId(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function normalizeDriverPortalFocusGroupKey(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function readDriverPortalFocusDeliveryIdFromSearch(search = "") {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  return normalizeDriverPortalFocusDeliveryId(params.get("focus_delivery_id"));
}

function readDriverPortalFocusGroupKeyFromSearch(search = "") {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  return normalizeDriverPortalFocusGroupKey(params.get("focus_group_key"));
}

function buildDriverPortalScreenPath(screen = "", {
  dispatchDate = "",
  dispatchPeriod = "",
  focusDeliveryId = "",
  focusGroupKey = "",
  previewDriverUserId = "",
} = {}) {
  const basePath = buildDriverPortalHomePath({
    dispatchDate,
    dispatchPeriod,
    previewDriverUserId,
  });

  const resolvedUrl = new URL(basePath, "https://grain.local");
  if (screen) {
    resolvedUrl.searchParams.set("screen", screen);
  } else {
    resolvedUrl.searchParams.delete("screen");
  }

  const normalizedFocusDeliveryId = normalizeDriverPortalFocusDeliveryId(focusDeliveryId);
  if (normalizedFocusDeliveryId) {
    resolvedUrl.searchParams.set("focus_delivery_id", normalizedFocusDeliveryId);
  } else {
    resolvedUrl.searchParams.delete("focus_delivery_id");
  }

  const normalizedFocusGroupKey = normalizeDriverPortalFocusGroupKey(focusGroupKey);
  if (normalizedFocusGroupKey) {
    resolvedUrl.searchParams.set("focus_group_key", normalizedFocusGroupKey);
  } else {
    resolvedUrl.searchParams.delete("focus_group_key");
  }

  return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
}

function buildDriverPortalSearchUrl(address = "") {
  const normalizedAddress = normalizeDriverPortalMapsAddress(address);
  if (!normalizedAddress) {
    return "";
  }

  const searchParams = new URLSearchParams({
    api: "1",
    query: normalizedAddress,
  });

  return `https://www.google.com/maps/search/?${searchParams.toString()}`;
}

function buildDriverPortalStepMapsUrl(step, { originAddressOverride = "" } = {}) {
  if (!step || typeof step !== "object") {
    return "";
  }

  const normalizedOriginOverride = normalizeDriverPortalMapsAddress(originAddressOverride);
  const linkedLoadingAddress = normalizeDriverPortalAddress(step?.linked_delivery?.loading_address || step?.loading_address);
  const deliveryAddress = normalizeDriverPortalAddress(step?.linked_delivery?.address || step?.address);

  // Pour un chargement, l'adresse courante est le site de chargement, pas l'adresse de livraison liée.
  // Évite le décalage d'index quand step.address est absent et que linked_delivery.address
  // pointe vers la destination (arrêt suivant) au lieu du site de chargement.
  const currentAddress = step?.step_type === "loading"
    ? normalizeDriverPortalAddress(step?.address || step?.linked_delivery?.loading_address || step?.loading_address || step?.linked_delivery?.address)
    : normalizeDriverPortalAddress(step?.address || step?.linked_delivery?.address || step?.linked_delivery?.loading_address);

  // L'origine utilisée dans le lien Google Maps :
  // - Si un originAddressOverride est fourni (ex: adresse du groupe précédent dans la tournée),
  //   l'utiliser pour que le lien corresponde au segment visible sur la carte routière.
  // - Sinon, utiliser le site de chargement lié à la livraison.
  const originAddress = normalizedOriginOverride || (step?.step_type === "delivery" ? linkedLoadingAddress : "");

  if (step?.step_type === "loading" && (step?.is_initial_preload || step?.is_preloaded_previous_day)) {
    return buildDriverPortalSearchUrl(currentAddress);
  }

  if (step?.step_type === "loading") {
    return buildDriverPortalDirectionsUrl(originAddress, currentAddress) || buildDriverPortalSearchUrl(currentAddress);
  }

  if (step?.step_type === "delivery") {
    return buildDriverPortalDirectionsUrl(originAddress, currentAddress) || buildDriverPortalSearchUrl(currentAddress);
  }

  return buildDriverPortalSearchUrl(currentAddress);
}

function resolveDriverPortalDisplayStopKey(stop) {
  return normalizeDriverPortalAddress(
    stop?.linked_delivery_id || stop?.delivery_id || stop?.order_id || stop?.same_site_group_key || stop?.step_id,
  );
}
function getOperationalStepTypeLabel(step) {
  if (step?.step_type === "loading") {
    return step?.is_initial_preload || step?.is_preloaded_previous_day ? "Préchargement" : "Chargement";
  }

  return "Livraison";
}

function getOperationalStepLabel(step) {
  return step?.client_name || step?.label || (step?.step_type === "loading" ? "Site de chargement" : "Destination");
}

function resolveDriverStopGroupProductLabel({ commandRows = [], loadingActions = [], primaryStep = null } = {}) {
  const productLabels = [];
  const seenLabels = new Set();

  const appendProductLabel = (value) => {
    const normalizedValue = typeof value === "string" ? value.trim() : "";
    const dedupeKey = normalizedValue.toLowerCase();
    if (!normalizedValue || seenLabels.has(dedupeKey)) {
      return;
    }

    seenLabels.add(dedupeKey);
    productLabels.push(normalizedValue);
  };

  commandRows.forEach((command) => appendProductLabel(command?.grainType));
  loadingActions.forEach((action) => appendProductLabel(action?.grainType));
  appendProductLabel(primaryStep?.grain_type);
  appendProductLabel(primaryStep?.linked_delivery?.grain_type);

  return productLabels.join(" · ");
}

function getOperationalStepAddress(step) {
  // Pour un chargement, prioritiser loading_address avant linked_delivery.address
  // qui pointe vers la livraison (arrêt suivant) au lieu du site de chargement.
  if (step?.step_type === "loading") {
    return step?.address || step?.linked_delivery?.loading_address || step?.loading_address || step?.linked_delivery?.address || "Adresse non renseignée";
  }

  return step?.address || step?.linked_delivery?.address || step?.linked_delivery?.loading_address || "Adresse non renseignée";
}

function isInitialPreloadStep(step) {
  return Boolean(step && step.step_type === "loading" && (step.is_initial_preload || step.is_preloaded_previous_day));
}

const DRIVER_PORTAL_PRELOAD_LABEL = "Préchargement";
const DRIVER_PORTAL_PRELOAD_TITLE = "Le camion part déjà préchargé pour cette mission.";

function DriverPortalPreloadBanner({
  label = DRIVER_PORTAL_PRELOAD_LABEL,
  title = DRIVER_PORTAL_PRELOAD_TITLE,
}) {
  return (
    <span
      title={title}
      className="driver-preload-note"
    >
      {label}
    </span>
  );
}

function getOperationalStepTruckLabel(step) {
  return step?.truck_name || step?.linked_delivery?.truck_name || step?.truck_id || step?.linked_delivery?.current_truck_id || "Camion non renseigné";
}

function hasDriverPortalDeliveredQuantity(value) {
  return value != null && Number.isFinite(Number(value));
}

function isDriverPortalLoadingConfirmed(step) {
  return Boolean(step?.is_confirmed || step?.is_loading_confirmed);
}

function resolveDriverPortalDeliveryActionState(command) {
  const isConfirmed = Boolean(command?.isConfirmed);

  if (isConfirmed) {
    return {
      bottomButtonClassName: `min-h-10 rounded-lg px-4 text-[0.82rem] ${DRIVER_PORTAL_BRAND_CONFIRMED_BUTTON_CLASS_NAME}`,
      buttonClassName: `shrink-0 whitespace-nowrap ${DRIVER_PORTAL_BRAND_CONFIRMED_BUTTON_CLASS_NAME}`,
      buttonLabel: "Déconfirmer",
      buttonTitle: "Cliquer pour déconfirmer",
      status: "confirmed",
    };
  }

  return {
    bottomButtonClassName: "driver-confirm-delivery",
    buttonClassName: "driver-confirm-delivery",
    buttonLabel: "Confirmer",
    buttonTitle: "Cliquer pour confirmer",
    status: "pending",
  };
}

function resolveDriverPortalLoadingActionState(step) {
  const isExplicitlyConfirmed = isDriverPortalLoadingConfirmed(step);
  const isSettledByDelivery = Boolean(isDriverPortalDeliveryCompleted(step));

  if (isSettledByDelivery) {
    return {
      bottomButtonClassName: "min-h-10 rounded-lg border border-slate-200 bg-slate-50 px-4 text-[0.82rem] text-slate-400 shadow-none",
      buttonClassName: "shrink-0 whitespace-nowrap border border-slate-200 bg-slate-50 text-slate-400 shadow-none",
      buttonLabel: "Livrée",
      buttonTitle: "La livraison liée a déjà été confirmée.",
      isExplicitlyConfirmed,
      isSettled: true,
      status: "settled",
    };
  }

  if (isExplicitlyConfirmed) {
    return {
      bottomButtonClassName: `min-h-10 rounded-lg px-4 text-[0.82rem] ${DRIVER_PORTAL_BRAND_CONFIRMED_BUTTON_CLASS_NAME}`,
      buttonClassName: `shrink-0 whitespace-nowrap ${DRIVER_PORTAL_BRAND_CONFIRMED_BUTTON_CLASS_NAME}`,
      buttonLabel: "Déconfirmer",
      buttonTitle: "Cliquer pour déconfirmer",
      isExplicitlyConfirmed: true,
      isSettled: true,
      status: "confirmed",
    };
  }

  return {
    bottomButtonClassName: "driver-confirm-loading",
    buttonClassName: "driver-confirm-loading",
    buttonLabel: "Confirmer",
    buttonTitle: "Cliquer pour confirmer",
    isExplicitlyConfirmed: false,
    isSettled: false,
    status: "pending",
  };
}

function isDriverPortalDeliveryCompleted(step) {
  return Boolean(
    step?.is_delivery_confirmed
    || step?.linked_delivery?.is_delivery_confirmed
    || step?.is_dispatcher_delivered
    || step?.dispatcher_execution_status === "delivered"
    || step?.linked_delivery?.is_dispatcher_delivered
    || step?.linked_delivery?.dispatcher_execution_status === "delivered"
  );
}

function isDriverPortalDeliveryResettable(step) {
  return Boolean(
    isDriverPortalDeliveryCompleted(step)
    || step?.is_delivery_confirmed
    || step?.is_confirmed
    || step?.linked_delivery?.is_delivery_confirmed
    || step?.linked_delivery?.is_confirmed
    || hasDriverPortalDeliveredQuantity(step?.actual_delivered_quantity_tons)
    || hasDriverPortalDeliveredQuantity(step?.linked_delivery?.actual_delivered_quantity_tons)
  );
}

function isDriverPortalStepCompleted(step) {
  if (!step || typeof step !== "object") {
    return false;
  }

  if (step?.step_type === "loading") {
    return isDriverPortalLoadingConfirmed(step);
  }

  return isDriverPortalDeliveryCompleted(step);
}

function resolveOperationalStepState(step) {
  if (!step || typeof step !== "object") {
    return {
      badgeClassName: "border-slate-200 bg-slate-100 text-slate-700",
      helperText: "Arrêt indisponible.",
      title: "Arrêt",
      tone: "neutral",
    };
  }

  if (isDriverPortalStepCompleted(step)) {
    return {
      badgeClassName: "border-[#214d30] bg-[#214d30] text-white",
      helperText: step.step_type === "loading"
        ? "Le chargement lié à cet arrêt est confirmé."
        : "La livraison liée à cet arrêt est confirmée.",
      title: step.step_type === "loading" ? "Chargé" : "Livré",
      tone: "success",
    };
  }

  if (step.is_blocked || step.linked_delivery?.is_delivery_blocked) {
    return {
      badgeClassName: "border-amber-200 bg-amber-50 text-amber-700",
      helperText: step.blocking_reason || step.linked_delivery?.blocking_reason || "Une étape précédente doit être validée avant cet arrêt.",
      title: "En attente",
      tone: "warning",
    };
  }

  if (step.step_type === "loading") {
    return {
      badgeClassName: "border-[#d9e6d8] bg-[#eef5ee] text-[#1f4d2f]",
      helperText: step.is_initial_preload || step.is_preloaded_previous_day
        ? "Le camion part déjà préchargé pour cette mission."
        : "Commencez la journée par ce chargement avant de poursuivre la tournée.",
      title: step.is_initial_preload || step.is_preloaded_previous_day ? "Préchargement" : "À charger",
      tone: "ready",
    };
  }

  return {
    badgeClassName: "border-[#d9e6d8] bg-[#eef5ee] text-[#1f4d2f]",
    helperText: "Cette destination est prête à être confirmée après l'opération sur le terrain.",
    title: "À livrer",
    tone: "ready",
  };
}

function compareOperationalSteps(leftStep, rightStep) {
  const leftTruckId = String(leftStep?.truck_id || leftStep?.linked_delivery?.current_truck_id || "").trim();
  const rightTruckId = String(rightStep?.truck_id || rightStep?.linked_delivery?.current_truck_id || "").trim();
  const leftTripIndex = Number.isFinite(leftStep?.trip_index) ? leftStep.trip_index : 10 ** 6;
  const rightTripIndex = Number.isFinite(rightStep?.trip_index) ? rightStep.trip_index : 10 ** 6;
  const leftSequence = Number.isFinite(leftStep?.sequence) ? leftStep.sequence : 10 ** 6;
  const rightSequence = Number.isFinite(rightStep?.sequence) ? rightStep.sequence : 10 ** 6;
  const leftStepId = String(leftStep?.step_id || leftStep?.delivery_id || leftStep?.order_id || "").trim();
  const rightStepId = String(rightStep?.step_id || rightStep?.delivery_id || rightStep?.order_id || "").trim();

  return leftTruckId.localeCompare(rightTruckId)
    || leftTripIndex - rightTripIndex
    || leftSequence - rightSequence
    || leftStepId.localeCompare(rightStepId);
}

function resolveDriverPortalTimelineMinutes(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))) {
      return Number(value);
    }
    if (typeof value !== "string" || !value.trim()) {
      continue;
    }
    const normalizedValue = value.trim();
    const timeMatch = /^(\d{2}):(\d{2})/.exec(normalizedValue);
    if (timeMatch) {
      const hour = Number(timeMatch[1]);
      const minute = Number(timeMatch[2]);
      if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
        return hour * 60 + minute;
      }
    }
  }
	return null;
}

function formatDriverPortalProjectedMinutes(value) {
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) {
    return "";
  }
  const roundedMinutes = Math.round(minutes);
  const wrappedMinutes = ((roundedMinutes % 1440) + 1440) % 1440;
  const dayOffset = Math.floor(roundedMinutes / 1440);
  const hours = Math.floor(wrappedMinutes / 60);
  const minute = wrappedMinutes % 60;
  const dayOffsetLabel = dayOffset === 0
    ? ""
    : ` (${dayOffset > 0 ? "+" : ""}${dayOffset} j)`;
  return `${String(hours).padStart(2, "0")}:${String(minute).padStart(2, "0")}${dayOffsetLabel}`;
}

function formatDriverPortalProjectedStepTime(step = null, linkedDelivery = null, fallbackFormatter = formatStepTime) {
  const displayMinutes = resolveDriverPortalTimelineMinutes(
    step?.display_scheduled_minutes,
    step?.display_arrival_minutes,
    step?.display_start_minutes,
    linkedDelivery?.display_scheduled_minutes,
    linkedDelivery?.display_arrival_minutes,
    linkedDelivery?.display_start_minutes,
  );
  if (displayMinutes != null) {
    return formatDriverPortalProjectedMinutes(displayMinutes);
  }
  const displayValue = step?.display_scheduled_at
    || step?.display_arrival_at
    || step?.display_start_at
    || linkedDelivery?.display_scheduled_at
    || linkedDelivery?.display_arrival_at
    || linkedDelivery?.display_start_at;
  return fallbackFormatter(
    displayValue
    || step?.scheduled_at
    || step?.arrival_at
    || linkedDelivery?.scheduled_at
    || linkedDelivery?.arrival_at,
  );
}

function normalizeDriverPortalTruckIncident(incident, assignment = null) {
  if (!incident || typeof incident !== "object") {
    return null;
  }
  const id = typeof incident.id === "string" ? incident.id.trim() : "";
  const truckId = typeof incident.truck_id === "string" && incident.truck_id.trim()
    ? incident.truck_id.trim()
    : typeof assignment?.truck_id === "string"
      ? assignment.truck_id.trim()
      : "";
  const startTime = typeof incident.start_time === "string" ? incident.start_time.trim() : "";
  const endTime = typeof incident.end_time === "string" ? incident.end_time.trim() : "";
  const startMinutes = resolveDriverPortalTimelineMinutes(
    incident.display_start_minutes,
    incident.start_minutes,
    startTime,
  );
  const endMinutes = resolveDriverPortalTimelineMinutes(
    incident.display_end_minutes,
    incident.end_minutes,
    endTime,
  );
  if (!id || !truckId || !startTime || !endTime || startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
    return null;
  }
  // Sélection explicite des seuls champs chauffeur : ne jamais propager la description interne.
  return {
    displayEndMinutes: endMinutes,
    displayStartMinutes: startMinutes,
    endTime,
    id,
    startTime,
    truckId,
  };
}

function collectDriverPortalTruckIncidents(assignments = []) {
  const incidentsById = new Map();
  (Array.isArray(assignments) ? assignments : []).forEach((assignment) => {
    const assignmentIncidents = Array.isArray(assignment?.truck_incidents) ? assignment.truck_incidents : [];
    assignmentIncidents.forEach((incident) => {
      const normalizedIncident = normalizeDriverPortalTruckIncident(incident, assignment);
      if (normalizedIncident) {
        incidentsById.set(normalizedIncident.id, normalizedIncident);
      }
    });
  });
  return Array.from(incidentsById.values()).sort((left, right) => (
    left.displayStartMinutes - right.displayStartMinutes
    || left.displayEndMinutes - right.displayEndMinutes
    || left.id.localeCompare(right.id)
  ));
}

function resolveDriverStopGroupTimelineMinutes(group = null) {
  const step = group?.primaryStep || (Array.isArray(group?.steps) ? group.steps[0] : null);
  return resolveDriverPortalTimelineMinutes(
    step?.display_scheduled_minutes,
    step?.display_arrival_minutes,
    step?.display_start_minutes,
    step?.scheduled_at,
    step?.arrival_at,
  );
}

function buildDriverPortalChronologicalItems(stopGroups = [], truckIncidents = []) {
  const stopItems = (Array.isArray(stopGroups) ? stopGroups : []).map((group, index) => ({
    group,
    key: `stop:${group?.groupKey || group?.primaryStep?.step_id || index}`,
    sortIndex: index,
    sortMinutes: resolveDriverStopGroupTimelineMinutes(group),
    stopNumber: index + 1,
    type: "stop",
  }));
  const incidentItems = (Array.isArray(truckIncidents) ? truckIncidents : []).map((incident, index) => ({
    incident,
    key: `incident:${incident.id || index}`,
    sortIndex: index,
    sortMinutes: incident.displayStartMinutes,
    type: "incident",
  }));
  return [...stopItems, ...incidentItems].sort((left, right) => {
    const leftMinutes = Number.isFinite(left.sortMinutes) ? left.sortMinutes : Number.MAX_SAFE_INTEGER;
    const rightMinutes = Number.isFinite(right.sortMinutes) ? right.sortMinutes : Number.MAX_SAFE_INTEGER;
    return leftMinutes - rightMinutes
      || (left.type === right.type ? left.sortIndex - right.sortIndex : left.type === "incident" ? -1 : 1)
      || left.key.localeCompare(right.key);
  });
}

function buildDriverOperationalSteps(assignments = [], deliveries = []) {
  const deliveryById = new Map();
  const primaryDeliveryIdByOrderId = new Map();

  deliveries.forEach((delivery) => {
    if (!delivery || typeof delivery !== "object") {
      return;
    }

    const deliveryId = typeof delivery.delivery_id === "string" ? delivery.delivery_id.trim() : "";
    const orderId = typeof delivery.order_id === "string" ? delivery.order_id.trim() : "";
    if (deliveryId) {
      deliveryById.set(deliveryId, delivery);
    }
    if (orderId && deliveryId && !primaryDeliveryIdByOrderId.has(orderId)) {
      primaryDeliveryIdByOrderId.set(orderId, deliveryId);
    }
  });

  const flattenedSteps = assignments.flatMap((assignment) => {
    const assignmentSteps = markSharedCompartmentAllocations(
      Array.isArray(assignment?.operational_steps) ? assignment.operational_steps.filter(Boolean) : [],
    );
    return assignmentSteps.map((step, index) => {
      const directDeliveryId = typeof step?.delivery_id === "string" ? step.delivery_id.trim() : "";
      const orderId = typeof step?.order_id === "string" ? step.order_id.trim() : "";
      const linkedDeliveryId = directDeliveryId || primaryDeliveryIdByOrderId.get(orderId) || "";
      const linkedDelivery = linkedDeliveryId ? deliveryById.get(linkedDeliveryId) || null : null;
      return {
        ...step,
        linked_delivery: linkedDelivery,
        linked_delivery_id: linkedDeliveryId || null,
        publication_revision: normalizeDriverPortalPublicationRevision(
          step?.publication_revision
          ?? linkedDelivery?.publication_revision
          ?? assignment?.publication_revision,
        ),
        truck_id: assignment?.truck_id || linkedDelivery?.current_truck_id || "",
        truck_name: assignment?.truck_name || linkedDelivery?.truck_name || "",
        capacity_tons: assignment?.capacity_tons,
        total_compartments: Number.isFinite(assignment?.total_compartments) ? assignment.total_compartments : 0,
        compartments_used: Number.isFinite(assignment?.compartments_used) ? assignment.compartments_used : 0,
        peak_trip_load_tons: Number.isFinite(assignment?.peak_trip_load_tons) ? assignment.peak_trip_load_tons : 0,
        peak_load_fill_rate_pct: assignment?.peak_load_fill_rate_pct,
        assignment_distance_label: assignment?.total_estimated_distance_label || null,
        assignment_duration_label: assignment?.total_estimated_duration_label || null,
        sequence: Number.isFinite(step?.sequence) ? step.sequence : index + 1,
      };
    });
  });

  if (flattenedSteps.length > 0) {
    return flattenedSteps.sort(compareOperationalSteps);
  }

  return deliveries
    .filter(Boolean)
    .map((delivery, index) => ({
      address: delivery.address,
      client_name: delivery.client_name,
      delivery_id: delivery.delivery_id,
      grain_type: delivery.grain_type,
      is_blocked: delivery.is_delivery_blocked,
      is_confirmed: isDriverPortalStepCompleted(delivery),
      linked_delivery: delivery,
      linked_delivery_id: delivery.delivery_id,
      order_id: delivery.order_id || delivery.delivery_id || `order-${index + 1}`,
      quantity_tons: delivery.quantity_tons,
      publication_revision: normalizeDriverPortalPublicationRevision(delivery.publication_revision),
      sequence: Number.isFinite(delivery.display_rank) ? delivery.display_rank : index + 1,
      step_id: delivery.delivery_id || `delivery-${index + 1}`,
      step_type: "delivery",
      truck_id: delivery.current_truck_id,
      truck_name: delivery.truck_name,
      trip_index: Number.isFinite(delivery.source_trip_index) ? delivery.source_trip_index : 1,
    }))
    .sort(compareOperationalSteps);
}

function normalizeDriverStopGroupValue(value) {
  return typeof value === "string"
    ? value.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    : "";
}

function buildDriverStopGroupKey(step) {
  const sameSiteGroupKey = typeof step?.same_site_group_key === "string" ? step.same_site_group_key.trim() : "";
  if (sameSiteGroupKey) {
    return [step?.truck_id || "", step?.trip_id || "", step?.step_type || "", `same-site:${sameSiteGroupKey}`].join("::");
  }

  const labelKey = normalizeDriverStopGroupValue(getOperationalStepLabel(step));
  const addressKey = normalizeDriverStopGroupValue(normalizeDriverPortalMapsAddress(getOperationalStepAddress(step)));
  const fallbackKey = normalizeDriverStopGroupValue(step?.loading_step_id || step?.delivery_id || step?.order_id || step?.step_id);
  return [step?.truck_id || "", step?.trip_id || "", step?.step_type || "", addressKey || labelKey || fallbackKey].join("::");
}

function buildDriverStopCommandKey(step) {
  if (step?.step_type === "delivery") {
    const deliveryIdentity = step?.linked_delivery_id || step?.delivery_id || step?.order_id || step?.step_id || "unknown";
    const stepIdentity = step?.step_id || step?.delivery_id || step?.order_id || "step";
    return `delivery:${stepIdentity}:${deliveryIdentity}`;
  }

  return `loading:${step?.loading_step_id || step?.step_id || step?.order_id || "unknown"}:${step?.order_id || step?.step_id || "order"}`;
}

function buildDriverStopCommand(step) {
  const linkedDelivery = step?.linked_delivery || null;
  const quantityTons = linkedDelivery?.quantity_tons != null && Number.isFinite(Number(linkedDelivery.quantity_tons))
    ? Number(linkedDelivery.quantity_tons)
    : step?.quantity_tons != null && Number.isFinite(Number(step.quantity_tons))
      ? Number(step.quantity_tons)
      : null;
  const actualDeliveredQuantityTons = linkedDelivery?.actual_delivered_quantity_tons != null && Number.isFinite(Number(linkedDelivery.actual_delivered_quantity_tons))
    ? Number(linkedDelivery.actual_delivered_quantity_tons)
    : null;
  const actualLoadedQuantityTons = step?.actual_loaded_quantity_tons != null && Number.isFinite(Number(step.actual_loaded_quantity_tons))
    ? Number(step.actual_loaded_quantity_tons)
    : linkedDelivery?.actual_loaded_quantity_tons != null && Number.isFinite(Number(linkedDelivery.actual_loaded_quantity_tons))
      ? Number(linkedDelivery.actual_loaded_quantity_tons)
      : null;
  const priorityLabel = normalizeDriverPortalPriorityLabel(
    step?.dominant_priority
    || step?.dominantPriority
    || linkedDelivery?.dominant_priority
    || linkedDelivery?.dominantPriority
    || step?.loading_priority
    || step?.delivery_priority
    || linkedDelivery?.loading_priority
    || linkedDelivery?.delivery_priority,
  );
	const scheduledAtLabel = formatDriverPortalProjectedStepTime(step, linkedDelivery, formatStepTime);
  const requiresAspirator = resolveDriverPortalBooleanFlag(
    step?.requires_aspirator
    ?? step?.requiresAspirator
    ?? step?.aspirator
    ?? linkedDelivery?.requires_aspirator
    ?? linkedDelivery?.requiresAspirator
    ?? linkedDelivery?.aspirator,
  );
  const aspiratorRequirementLabel = typeof step?.aspirator_requirement_label === "string" && step.aspirator_requirement_label.trim()
    ? step.aspirator_requirement_label.trim()
    : typeof step?.aspiratorRequirementLabel === "string" && step.aspiratorRequirementLabel.trim()
      ? step.aspiratorRequirementLabel.trim()
      : typeof linkedDelivery?.aspirator_requirement_label === "string" && linkedDelivery.aspirator_requirement_label.trim()
        ? linkedDelivery.aspirator_requirement_label.trim()
        : typeof linkedDelivery?.aspiratorRequirementLabel === "string" && linkedDelivery.aspiratorRequirementLabel.trim()
          ? linkedDelivery.aspiratorRequirementLabel.trim()
          : requiresAspirator
            ? "Aspiration requise"
            : "";
  const manualMixBoundaryTitle = typeof step?.manual_mix_boundary_title === "string" && step.manual_mix_boundary_title.trim()
    ? step.manual_mix_boundary_title.trim()
    : typeof step?.manualMixBoundaryTitle === "string" && step.manualMixBoundaryTitle.trim()
      ? step.manualMixBoundaryTitle.trim()
      : typeof linkedDelivery?.manual_mix_boundary_title === "string" && linkedDelivery.manual_mix_boundary_title.trim()
        ? linkedDelivery.manual_mix_boundary_title.trim()
        : typeof linkedDelivery?.manualMixBoundaryTitle === "string" && linkedDelivery.manualMixBoundaryTitle.trim()
          ? linkedDelivery.manualMixBoundaryTitle.trim()
          : "";
  const manualMixCurrentProductLabel = typeof step?.manual_mix_current_product_label === "string" && step.manual_mix_current_product_label.trim()
    ? step.manual_mix_current_product_label.trim()
    : typeof step?.manualMixCurrentProductLabel === "string" && step.manualMixCurrentProductLabel.trim()
      ? step.manualMixCurrentProductLabel.trim()
      : typeof linkedDelivery?.manual_mix_current_product_label === "string" && linkedDelivery.manual_mix_current_product_label.trim()
        ? linkedDelivery.manual_mix_current_product_label.trim()
        : typeof linkedDelivery?.manualMixCurrentProductLabel === "string" && linkedDelivery.manualMixCurrentProductLabel.trim()
          ? linkedDelivery.manualMixCurrentProductLabel.trim()
          : "";
  const hasManualMixBoundaryBefore = resolveDriverPortalBooleanFlag(
    step?.has_manual_mix_boundary_before
    ?? step?.hasManualMixBoundaryBefore
    ?? linkedDelivery?.has_manual_mix_boundary_before
    ?? linkedDelivery?.hasManualMixBoundaryBefore,
  ) || Boolean(manualMixBoundaryTitle) || Boolean(manualMixCurrentProductLabel);
  const loadingActionState = step?.step_type === "loading"
    ? resolveDriverPortalLoadingActionState(step)
    : null;

  return {
    key: buildDriverStopCommandKey(step),
    stepId: typeof step?.step_id === "string" ? step.step_id : "",
    stepType: step?.step_type === "loading" ? "loading" : "delivery",
    orderId: typeof step?.order_id === "string" ? step.order_id : "",
    deliveryId: typeof linkedDelivery?.delivery_id === "string"
      ? linkedDelivery.delivery_id
      : typeof step?.delivery_id === "string"
        ? step.delivery_id
        : "",
    loadingStepId: typeof step?.loading_step_id === "string"
      ? step.loading_step_id
      : typeof step?.step_id === "string"
        ? step.step_id
        : "",
    publicationRevision: normalizeDriverPortalPublicationRevision(
      step?.publication_revision ?? linkedDelivery?.publication_revision,
    ),
    executionRevision: typeof step?.execution_revision === "string" && step.execution_revision.trim()
      ? step.execution_revision.trim()
      : typeof linkedDelivery?.execution_revision === "string"
        ? linkedDelivery.execution_revision.trim()
        : "",
    truckId: typeof step?.truck_id === "string"
      ? step.truck_id.trim()
      : typeof linkedDelivery?.current_truck_id === "string"
        ? linkedDelivery.current_truck_id.trim()
        : "",
    loadingClientName: typeof linkedDelivery?.loading_client_name === "string" && linkedDelivery.loading_client_name.trim()
      ? linkedDelivery.loading_client_name.trim()
      : typeof step?.loading_client_name === "string" && step.loading_client_name.trim()
        ? step.loading_client_name.trim()
        : "",
    clientName: linkedDelivery?.client_name || step?.client_name || "",
    address: linkedDelivery?.address || step?.address || "",
    building: linkedDelivery?.building || step?.building || "",
    silo: linkedDelivery?.silo || linkedDelivery?.loading_silo || step?.silo || step?.loading_silo || "",
    comment: linkedDelivery?.comment || linkedDelivery?.commentaire || linkedDelivery?.commentaires || step?.comment || step?.commentaire || step?.commentaires || "",
    quantityTons,
    actualDeliveredQuantityTons,
    actualLoadedQuantityTons,
    grainType: linkedDelivery?.grain_type || step?.grain_type || "",
    grainCode: linkedDelivery?.grain_code || step?.grain_code || "",
    workOrderNumber: linkedDelivery?.work_order_number || step?.work_order_number || "",
    priorityLabel,
    scheduledAtLabel,
    requiresAspirator,
    aspiratorRequirementLabel,
    hasManualMixBoundaryBefore,
    manualMixBoundaryTitle,
    manualMixCurrentProductLabel,
    compartmentAllocations: Array.isArray(step?.compartment_allocations) ? step.compartment_allocations.filter(Boolean) : [],
    isConfirmed: isDriverPortalStepCompleted(step),
    // Dégelée par le répartiteur : le chauffeur confirme de nouveau.
    isRevalidation: [step?.confirmation_portail, linkedDelivery?.confirmation_portail].includes("à_revalider"),
    loadingActionState,
    isBlocked: Boolean(step?.is_blocked || linkedDelivery?.is_delivery_blocked),
    blockingReason: step?.blocking_reason || linkedDelivery?.blocking_reason || "",
  };
}

function finalizeDriverStopGroup(group, groupIndex) {
  const primaryStep = group?.steps?.[0] || null;
  const commandRows = [];
  const seenCommandKeys = new Set();
  const loadingActions = [];
  const seenLoadingActionIds = new Set();
  const occurrenceCount = Array.isArray(group?.steps) ? group.steps.length : 0;
  const sequenceNumbers = Array.from(
    new Set(
      (Array.isArray(group?.steps) ? group.steps : [])
        .map((step) => (Number.isFinite(Number(step?.sequence)) ? Number(step.sequence) : null))
        .filter((sequence) => Number.isFinite(sequence)),
    ),
  ).sort((left, right) => left - right);
  const sequenceSummaryLabel = sequenceNumbers.length > 0 ? sequenceNumbers.join(" · ") : "";

  (Array.isArray(group?.steps) ? group.steps : []).forEach((step) => {
    const command = buildDriverStopCommand(step);
    if (!seenCommandKeys.has(command.key)) {
      seenCommandKeys.add(command.key);
      commandRows.push(command);
    }

    if (command.stepType === "loading") {
      const loadingActionId = command.loadingStepId || command.stepId;
      if (loadingActionId && !seenLoadingActionIds.has(loadingActionId)) {
        seenLoadingActionIds.add(loadingActionId);
        loadingActions.push({
          key: `loading-action:${loadingActionId}`,
          loadingStepId: loadingActionId,
          deliveryId: command.deliveryId,
          publicationRevision: command.publicationRevision,
          executionRevision: command.executionRevision,
          truckId: command.truckId,
          isConfirmed: command.isConfirmed,
          isEffectivelyCompleted: Boolean(command.loadingActionState?.isSettled),
          building: command.building,
          grainCode: command.grainCode,
          grainType: command.grainType,
          workOrderNumber: command.workOrderNumber,
          orderId: command.orderId,
          quantityTons: command.quantityTons,
          actualLoadedQuantityTons: command.actualLoadedQuantityTons,
          compartmentAllocations: command.compartmentAllocations,
          clientName: command.clientName,
          address: command.address,
          silo: command.silo,
          comment: command.comment,
          priorityLabel: command.priorityLabel,
          scheduledAtLabel: command.scheduledAtLabel,
          requiresAspirator: command.requiresAspirator,
          aspiratorRequirementLabel: command.aspiratorRequirementLabel,
          hasManualMixBoundaryBefore: command.hasManualMixBoundaryBefore,
          manualMixBoundaryTitle: command.manualMixBoundaryTitle,
          manualMixCurrentProductLabel: command.manualMixCurrentProductLabel,
          loadingActionState: command.loadingActionState,
          label: group?.steps?.length > 1 ? `Confirmer le chargement ${loadingActions.length + 1}` : "Confirmer le chargement",
        });
      }
    }
  });

  const groupStepType = primaryStep?.step_type === "loading" ? "loading" : "delivery";
  const plannedTotalQuantityTons = sumDriverStopGroupQuantityTons(commandRows, groupStepType, { effective: false });
  const totalQuantityTons = sumDriverStopGroupQuantityTons(commandRows, groupStepType, { effective: true });
  const uniqueOrderCount = new Set(commandRows.map((command) => command.orderId).filter(Boolean)).size;
  const uniqueCompartments = new Set();
  commandRows.forEach((command) => {
    command.compartmentAllocations.forEach((allocation) => {
      if (Number.isFinite(allocation?.position)) {
        uniqueCompartments.add(allocation.position);
      }
    });
  });
  const allConfirmed = commandRows.length > 0 && commandRows.every((command) => command.isConfirmed);
  const hasBlockingCommand = commandRows.some((command) => command.isBlocked);
  const allLoadingActionsSettled = primaryStep?.step_type === "loading"
    && loadingActions.length > 0
    && loadingActions.every((action) => action.isEffectivelyCompleted);
  const groupState = allConfirmed
    ? {
        badgeClassName: "border-[#214d30] bg-[#214d30] text-white",
        title: primaryStep?.step_type === "loading" ? "Chargé" : "Livré",
        tone: "success",
      }
    : hasBlockingCommand
      ? resolveOperationalStepState({
          ...primaryStep,
          is_blocked: true,
          blocking_reason: commandRows.find((command) => command.isBlocked)?.blockingReason || primaryStep?.blocking_reason,
        })
      : allLoadingActionsSettled
        ? {
            badgeClassName: "border-[#214d30] bg-[#214d30] text-white",
            title: "Soldé",
            tone: "success",
          }
      : resolveOperationalStepState(primaryStep);

  const primaryLinkedDelivery = primaryStep?.linked_delivery || null;
  const priorityLabel = normalizeDriverPortalPriorityLabel(
    primaryStep?.dominant_priority
    || primaryStep?.dominantPriority
    || primaryLinkedDelivery?.dominant_priority
    || primaryLinkedDelivery?.dominantPriority
    || primaryStep?.loading_priority
    || primaryStep?.delivery_priority
    || primaryLinkedDelivery?.loading_priority
    || primaryLinkedDelivery?.delivery_priority,
  );
  const productLabel = resolveDriverStopGroupProductLabel({
    commandRows,
    loadingActions,
    primaryStep,
  });

  return {
    groupIndex,
    groupKey: group.groupKey,
    occurrenceCount,
    sequenceSummaryLabel,
    steps: group.steps,
    primaryStep,
    stepType: primaryStep?.step_type === "loading" ? "loading" : "delivery",
    title: getOperationalStepLabel(primaryStep),
    address: getOperationalStepAddress(primaryStep),
    truckLabel: getOperationalStepTruckLabel(primaryStep),
    routeDistanceLabel: typeof primaryStep?.route_distance_label === "string" && primaryStep.route_distance_label.trim()
      ? primaryStep.route_distance_label.trim()
      : "",
    routeDistanceKm: Number.isFinite(Number(primaryStep?.route_distance_km)) ? Number(primaryStep.route_distance_km) : null,
		scheduledLabel: formatDriverPortalProjectedStepTime(
			primaryStep,
			primaryLinkedDelivery,
			formatDriverPortalScheduleTime,
		),
    priorityLabel,
    productLabel,
    commandRows,
    loadingActions,
    plannedTotalQuantityTons,
    totalQuantityTons,
    uniqueOrderCount,
    usedCompartmentCount: uniqueCompartments.size,
    state: groupState,
  };
}

function buildDriverStopGroups(operationalStops = [], departures = []) {
  return groupConsecutiveDriverStops(operationalStops, (step) => {
    const departure = departureForStep(departures, step);
    return departure?.visit_key ? `${departure.truck_id}::departure:${departure.visit_key}` : buildDriverStopGroupKey(step);
  }).map(finalizeDriverStopGroup);
}

function doesDriverStopMatchDeliveryId(step, deliveryId = "") {
  const normalizedDeliveryId = normalizeDriverPortalFocusDeliveryId(deliveryId);
  if (!normalizedDeliveryId) {
    return false;
  }

  return [
    step?.linked_delivery_id,
    step?.delivery_id,
    step?.linked_delivery?.delivery_id,
  ].some((candidateId) => normalizeDriverPortalFocusDeliveryId(candidateId) === normalizedDeliveryId);
}

function findDriverStopGroupByDeliveryId(stopGroups = [], deliveryId = "") {
  const normalizedDeliveryId = normalizeDriverPortalFocusDeliveryId(deliveryId);
  if (!normalizedDeliveryId) {
    return null;
  }

  const normalizedGroups = Array.isArray(stopGroups) ? stopGroups : [];
  const exactDeliveryGroup = normalizedGroups.find((group) => {
    return Array.isArray(group?.steps) && group.steps.some((step) => step?.step_type === "delivery" && doesDriverStopMatchDeliveryId(step, normalizedDeliveryId));
  });
  if (exactDeliveryGroup) {
    return exactDeliveryGroup;
  }

  return normalizedGroups.find((group) => {
    return Array.isArray(group?.steps) && group.steps.some((step) => doesDriverStopMatchDeliveryId(step, normalizedDeliveryId));
  }) || null;
}

function buildDriverPortalHomeModel(payload) {
  const assignments = Array.isArray(payload?.assignments) ? payload.assignments.filter(Boolean) : [];
  const deliveries = Array.isArray(payload?.deliveries) ? payload.deliveries.filter(Boolean) : [];
  const trips = Array.isArray(payload?.trips) ? payload.trips.filter(Boolean) : [];
  const operationalStops = buildDriverOperationalSteps(assignments, deliveries);
  const confirmedDeliveryCount = deliveries.reduce((total, delivery) => total + (isDriverPortalDeliveryResettable(delivery) ? 1 : 0), 0);
  const confirmedLoadingStepIds = new Set();
  const resettableOrderIds = new Set();
  const displayStopKeys = [];
  const seenDisplayStopKeys = new Set();

  deliveries.forEach((delivery) => {
    if (!isDriverPortalDeliveryResettable(delivery)) {
      return;
    }

    const orderId = typeof delivery?.order_id === "string" ? delivery.order_id.trim() : "";
    if (orderId) {
      resettableOrderIds.add(orderId);
    }
  });

  assignments.forEach((assignment) => {
    const assignmentSteps = Array.isArray(assignment?.operational_steps) ? assignment.operational_steps.filter(Boolean) : [];
    assignmentSteps.forEach((step, stepIndex) => {
      if (step?.step_type !== "loading" || !isDriverPortalLoadingConfirmed(step)) {
        return;
      }

      const stepId = typeof step?.step_id === "string" ? step.step_id.trim() : "";
      confirmedLoadingStepIds.add(stepId || `${assignment?.truck_id || "truck"}:${step?.order_id || "order"}:${stepIndex}`);

      const orderId = typeof step?.order_id === "string" ? step.order_id.trim() : "";
      if (orderId) {
        resettableOrderIds.add(orderId);
      }
    });
  });

  const confirmedLoadingCount = confirmedLoadingStepIds.size;
  const hasResettableValidations = confirmedDeliveryCount > 0 || confirmedLoadingCount > 0;

  operationalStops.forEach((stop) => {
    const stopKey = resolveDriverPortalDisplayStopKey(stop);
    if (!stopKey || seenDisplayStopKeys.has(stopKey)) {
      return;
    }

    seenDisplayStopKeys.add(stopKey);
    displayStopKeys.push(stopKey);
  });

  const firstPendingStopIndex = operationalStops.findIndex((stop) => {
    const departure = departureForStep(payload?.departure_stops, stop);
    return !isDriverPortalStepCompleted(stop) || (departure && !departure.departure && !departure.locked);
  });
  const currentStopIndex = firstPendingStopIndex >= 0
    ? firstPendingStopIndex
    : operationalStops.length > 0
      ? operationalStops.length - 1
      : -1;
  const currentStop = currentStopIndex >= 0 ? operationalStops[currentStopIndex] : null;
  const currentDisplayStopKey = resolveDriverPortalDisplayStopKey(currentStop);
  const currentDisplayStopIndex = currentDisplayStopKey ? displayStopKeys.indexOf(currentDisplayStopKey) : -1;
  const previousStop = currentStopIndex > 0 ? operationalStops[currentStopIndex - 1] : null;
  const nextStop = currentStopIndex >= 0 && currentStopIndex < operationalStops.length - 1
    ? operationalStops[currentStopIndex + 1]
    : null;
  const stopGroups = buildDriverStopGroups(operationalStops, payload?.departure_stops);
  const truckIncidents = collectDriverPortalTruckIncidents(assignments);
  const chronologicalItems = buildDriverPortalChronologicalItems(stopGroups, truckIncidents);
  const currentStopGroupIndex = currentStop
    ? stopGroups.findIndex((group) => group.steps.some((step) => step?.step_id === currentStop.step_id))
    : stopGroups.length > 0
      ? 0
      : -1;
  const currentStopGroup = currentStopGroupIndex >= 0 ? stopGroups[currentStopGroupIndex] : null;
  const nextStopGroup = currentStopGroupIndex >= 0
    ? stopGroups[currentStopGroupIndex + 1] || null
    : stopGroups[0] || null;
  const totalRouteQuantityTons = deliveries.reduce((total, delivery) => {
    const quantityTons = Number(delivery?.quantity_tons);
    return Number.isFinite(quantityTons) ? total + quantityTons : total;
  }, 0);

  const peakLoadAssignment = assignments.reduce((bestAssignment, assignment) => {
    const bestPeak = Number.isFinite(bestAssignment?.peak_trip_load_tons) ? bestAssignment.peak_trip_load_tons : -1;
    const currentPeak = Number.isFinite(assignment?.peak_trip_load_tons) ? assignment.peak_trip_load_tons : -1;
    return currentPeak > bestPeak ? assignment : bestAssignment;
  }, null);

  const totalCompartments = assignments.reduce((total, assignment) => {
    return total + (Number.isFinite(assignment?.total_compartments) ? assignment.total_compartments : 0);
  }, 0);
  const occupiedCompartments = assignments.reduce((total, assignment) => {
    return total + (Number.isFinite(assignment?.compartments_used) ? assignment.compartments_used : 0);
  }, 0);
  const preloadActive = operationalStops.some((stop) => stop?.is_initial_preload || stop?.is_preloaded_previous_day);
  const primaryDriverLabel = assignments.find((assignment) => typeof assignment?.driver_name === "string" && assignment.driver_name.trim())?.driver_name?.trim() || "";
  const primaryTruckLabel = assignments.length === 1
    ? assignments[0]?.truck_name || assignments[0]?.truck_id || "Camion lié"
    : Array.isArray(payload?.truck_ids) && payload.truck_ids.length > 1
      ? `${payload.truck_ids.length} camions liés`
      : assignments[0]?.truck_name || assignments[0]?.truck_id || "Camion lié";
  const upcomingStop = nextStop || currentStop || operationalStops[0] || null;
  const peakLoadValue = Number.isFinite(peakLoadAssignment?.peak_trip_load_tons)
    ? formatQuantity(peakLoadAssignment.peak_trip_load_tons)
    : "--";
  const peakLoadHelper = peakLoadAssignment
    ? [
      peakLoadAssignment?.truck_name || peakLoadAssignment?.truck_id || "Camion lié",
      peakLoadAssignment?.peak_load_fill_rate_pct != null ? formatPercent(peakLoadAssignment.peak_load_fill_rate_pct) : "",
    ].filter(Boolean).join(" · ")
    : "Aucun pic détecté";

  return {
    assignments,
    chronologicalItems,
    currentStop,
    currentStopGroup,
    currentStopGroupIndex,
    currentDisplayStopIndex,
    currentStopIndex,
    deliveries,
    confirmedDeliveryCount,
    confirmedLoadingCount,
    hasResettableValidations,
    nextStop,
    nextStopGroup,
    occupiedCompartments,
    operationalStops,
    trips,
    stopGroups,
    peakLoadHelper,
    peakLoadValue,
    preloadActive,
    primaryDriverLabel,
    previousStop,
    primaryTruckLabel,
    totalCompartments,
    totalOperationCount: operationalStops.length,
    totalOrderCount: countDriverPortalOrders(operationalStops),
    truckIncidents,
    totalRouteQuantityTons,
    resettableOrderCount: resettableOrderIds.size,
    totalStopCount: stopGroups.length || operationalStops.length,
    upcomingStop,
  };
}

function resolveDriverPortalRouteContext(payload, fallbackTripId = "", fallbackTruckId = "") {
  const normalizedTripId = typeof fallbackTripId === "string" ? fallbackTripId.trim() : "";
  const normalizedTruckId = typeof fallbackTruckId === "string" ? fallbackTruckId.trim() : "";
  const assignments = Array.isArray(payload?.assignments) ? payload.assignments.filter(Boolean) : [];
  const trips = Array.isArray(payload?.trips) ? payload.trips.filter(Boolean) : [];

  const assignment = assignments.find((candidate) => candidate?.trip_id === normalizedTripId)
    || assignments.find((candidate) => normalizedTripId && candidate?.trips?.some((trip) => trip.trip_id === normalizedTripId))
    || assignments.find((candidate) => normalizedTruckId && (candidate?.truck_id === normalizedTruckId || candidate?.current_truck_id === normalizedTruckId))
    || assignments.find(Boolean)
    || null;

  const trip = trips.find((candidate) => candidate?.trip_id === normalizedTripId)
    || (Array.isArray(assignment?.trips) ? assignment.trips.find((candidate) => candidate?.trip_id === normalizedTripId) : null)
    || (!normalizedTripId ? trips.find((candidate) => normalizedTruckId && (candidate?.truck_id === normalizedTruckId || candidate?.current_truck_id === normalizedTruckId)) : null)
    || (payload?.trip && (!normalizedTripId || payload.trip.trip_id === normalizedTripId) ? payload.trip : null)
    || null;

  return {
    assignment,
    trip,
  };
}

function DriverPortalBusyOverlay({ message = "" }) {
  const resolvedMessage = typeof message === "string" ? message.trim() : "";
  const isOpen = Boolean(resolvedMessage);

  React.useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      aria-hidden="false"
      className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/35 p-5"
      data-driver-portal-busy-overlay="true"
    >
      <section
        aria-atomic="true"
        aria-busy="true"
        aria-live="polite"
        data-grain-floating-surface="driver-busy"
        className="relative grid w-full max-w-sm gap-3 overflow-hidden rounded-[4px] border border-[#d8e2d4] bg-white px-4 py-3 shadow-[0_28px_64px_rgba(22,32,24,0.22)]"
        role="status"
      >
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 h-11 w-11 rounded-2xl ${DRIVER_PORTAL_BRAND_PILL_CLASS_NAME}`}>
            <LoaderCircle className="animate-spin" size={20} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-[0.68rem] font-black uppercase tracking-[0.14em] text-accent">
              Traitement en cours
            </p>
            <p className="mt-1.5 text-lg font-semibold tracking-[-0.03em] text-slate-950">
              {resolvedMessage}
            </p>
            <p className="mt-1.5 text-sm leading-6 text-slate-600">
              Merci de patienter pendant le traitement.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function DriverPortalShell({
  actions = null,
  busyMessage = "",
  children,
  datePanel = null,
  datePanelKey = "",
  dateAccessory = null,
  eyebrow,
  headerContent = null,
  inlineHeaderActions = false,
  iconHeaderActions = false,
  showHeaderText = true,
  title,
  subtitle,
}) {

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const previousScrollbarGutter = document.documentElement.style.scrollbarGutter;
    document.documentElement.style.scrollbarGutter = "auto";

    return () => {
      document.documentElement.style.scrollbarGutter = previousScrollbarGutter;
    };
  }, []);

  return (
    <div className="driver-portal min-h-dvh overflow-x-clip text-slate-950">
      <DriverPortalBusyOverlay message={busyMessage} />
      <div className="driver-portal-shell mx-auto flex min-h-dvh w-full flex-col">
        <div className={`driver-portal-header ${dateAccessory ? "driver-portal-header--test" : ""} ${inlineHeaderActions ? "driver-portal-header--inline" : ""} ${iconHeaderActions ? "driver-portal-header--icons" : ""} min-w-0 max-w-full`}>
          <WorkspacePageHeader
            icon={Truck}
            title={showHeaderText ? (datePanel ? (
              <span className={dateAccessory ? "driver-date-with-test" : undefined}>
                <DriverPortalDateMenu key={datePanelKey} label={title || "Ma journée"}>{datePanel}</DriverPortalDateMenu>
                {dateAccessory}
              </span>
            ) : title || eyebrow || "Portail chauffeur") : ""}
            titleAs="h1"
            eyebrow={showHeaderText && title ? eyebrow : ""}
            description={showHeaderText ? subtitle : ""}
            actions={inlineHeaderActions ? null : actions}
            titleSuffix={inlineHeaderActions ? actions : null}
          >
            {showHeaderText ? headerContent : null}
          </WorkspacePageHeader>
        </div>

        <div className="driver-portal-content min-w-0 max-w-full flex-1">{children}</div>
      </div>
    </div>
  );
}

function DriverPortalScenicPanel({
  children,
  contentClassName = "",
}) {
  return (
    <section
      className="relative overflow-hidden rounded-[34px] border border-[#d8e2d4] bg-[linear-gradient(180deg,#f6f1e7_0%,#ecf2ea_55%,#ffffff_100%)] px-4 py-4 shadow-[0_28px_64px_rgba(22,32,24,0.12)]"
      data-driver-portal-background
    >
      <div className="absolute inset-0 bg-[url('/page-accueil.jpg')] bg-cover bg-center opacity-55" aria-hidden="true" />
      <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(246,241,231,0.94)_0%,rgba(236,242,234,0.8)_50%,rgba(255,255,255,0.96)_100%)]" aria-hidden="true" />
      <div className={`relative min-h-[min(76dvh,46rem)] ${contentClassName}`}>{children}</div>
    </section>
  );
}

function DriverStateCard({
  action = null,
  guidance = "",
  message,
  title,
  tone = "loading",
}) {
  const isError = tone === "error";
  const isEmpty = tone === "empty";

  if (isEmpty) {
    return (
      <section
        className="relative overflow-hidden rounded-[28px] border border-border-accent-subtle bg-gradient-to-br from-white via-white to-accent/10 px-5 py-7 shadow-[0_22px_52px_rgba(15,92,87,0.10)]"
        data-driver-state="empty"
      >
        <span aria-hidden="true" className={DRIVER_PORTAL_BRAND_BLOB_CLASS_NAME} />
        <div className="relative flex flex-col items-center text-center">
          <span className={`h-14 w-14 rounded-[20px] shadow-[0_12px_28px_rgba(15,92,87,0.18)] ${DRIVER_PORTAL_BRAND_PILL_CLASS_NAME}`}>
            <CalendarDays size={24} strokeWidth={1.8} aria-hidden="true" />
          </span>
          <p className="mt-5 text-xl font-semibold tracking-[-0.04em] text-slate-950">{title}</p>
          <p className="mt-2 max-w-sm text-sm leading-6 text-slate-600">{message}</p>
          {guidance ? (
            <div className="mt-5 flex w-full items-start gap-3 rounded-[20px] border border-border-accent-subtle bg-white/90 px-4 py-3.5 text-left shadow-[0_10px_26px_rgba(15,23,42,0.05)]">
              <span className={`mt-0.5 h-9 w-9 rounded-xl ${DRIVER_PORTAL_BRAND_PILL_CLASS_NAME}`}>
                <MessageCircle size={17} strokeWidth={2} aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">Contactez votre répartiteur</p>
                <p className="mt-1 text-xs leading-5 text-slate-600">{guidance}</p>
              </div>
            </div>
          ) : null}
          {action ? <div className="mt-5">{action}</div> : null}
        </div>
      </section>
    );
  }

  return (
    <section
      aria-busy={!isError}
      className={`rounded-[28px] border px-5 py-6 shadow-[0_18px_44px_rgba(15,23,42,0.06)] ${isError ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white"}`}
      data-driver-state={tone}
    >
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${isError ? "bg-rose-100 text-rose-600" : "bg-slate-100 text-slate-500"}`}>
          {isError ? <AlertTriangle size={18} aria-hidden="true" /> : <LoaderCircle className="animate-spin" size={18} aria-hidden="true" />}
        </span>
        <div className="min-w-0">
          <p className="text-lg font-semibold tracking-[-0.03em] text-slate-950">{title}</p>
          <p className="mt-2 text-sm leading-6 text-slate-600">{message}</p>
          {action ? <div className="mt-4">{action}</div> : null}
        </div>
      </div>
    </section>
  );
}

function DriverPortalUpdatePendingIcon({ size = 17 }) {
  return (
    <RefreshCw
      aria-hidden="true"
      className="motion-safe:animate-driver-portal-update-spin"
      size={size}
      strokeWidth={size >= 20 ? 1.8 : 2}
    />
  );
}

function DriverPortalScenicStateCard({
  action = null,
  guidance = "",
  guidanceBusy = false,
  guidanceTitle = "Contactez votre répartiteur",
  icon = null,
  message,
  title,
  tone = "empty",
}) {
  return (
    <DriverPortalScenicPanel contentClassName="flex flex-col justify-between gap-8 py-2">
      <section
        className="relative overflow-hidden rounded-[28px] border border-border-accent-subtle bg-white/92 px-5 py-5 shadow-[0_18px_42px_rgba(15,92,87,0.10)] backdrop-blur"
        data-driver-state={tone}
      >
        <span aria-hidden="true" className={DRIVER_PORTAL_BRAND_BLOB_CLASS_NAME} />
        <div className="relative flex flex-col items-center text-center">
          <span className={`h-12 w-12 rounded-[18px] shadow-[0_10px_24px_rgba(15,92,87,0.18)] ${DRIVER_PORTAL_BRAND_PILL_CLASS_NAME}`}>
            {icon ?? <CalendarDays size={21} strokeWidth={1.8} aria-hidden="true" />}
          </span>
          <p className="mt-3 text-xl font-semibold tracking-[-0.04em] text-slate-950">{title}</p>
          <p className="mt-2 max-w-sm text-sm leading-6 text-slate-600">{message}</p>
        </div>
      </section>

      <div className="flex flex-col gap-3">
        {guidance ? (
          <section
            className="flex items-start gap-3 rounded-[24px] border border-border-accent-subtle bg-white/92 px-4 py-3.5 text-left shadow-[0_14px_32px_rgba(15,23,42,0.07)] backdrop-blur"
            data-driver-portal-contact-dispatcher
            data-driver-portal-update-pending={guidanceBusy ? "true" : undefined}
          >
            <span
              className={`mt-0.5 h-9 w-9 rounded-xl ${DRIVER_PORTAL_BRAND_PILL_CLASS_NAME} ${guidanceBusy ? "motion-safe:animate-driver-portal-update-pulse" : ""}`}
            >
              {guidanceBusy
                ? <DriverPortalUpdatePendingIcon size={17} />
                : <MessageCircle size={17} strokeWidth={2} aria-hidden="true" />}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">{guidanceTitle}</p>
              <p className="mt-1 text-xs leading-5 text-slate-600">{guidance}</p>
            </div>
          </section>
        ) : null}
        {action ? <div>{action}</div> : null}
      </div>
    </DriverPortalScenicPanel>
  );
}

function DriverRouteUpdatePendingCard({ action = null, message = DRIVER_PORTAL_ROUTE_UPDATE_PENDING_MESSAGE }) {
  return (
    <DriverPortalScenicStateCard
      action={action}
      tone="empty"
      title={DRIVER_PORTAL_ROUTE_UPDATE_PENDING_TITLE}
      message={message}
      icon={<DriverPortalUpdatePendingIcon size={21} />}
      guidanceTitle={DRIVER_PORTAL_ROUTE_UPDATE_PENDING_GUIDANCE_TITLE}
      guidance={DRIVER_PORTAL_ROUTE_UPDATE_PENDING_GUIDANCE}
      guidanceBusy
    />
  );
}

function DriverPublicationWithdrawnCard({ action = null }) {
  return (
    <DriverPortalScenicStateCard
      action={action}
      tone="empty"
      title={DRIVER_PORTAL_PUBLICATION_WITHDRAWN_TITLE}
      message={DRIVER_PORTAL_PUBLICATION_WITHDRAWN_MESSAGE}
      guidance={DRIVER_PORTAL_PUBLICATION_WITHDRAWN_GUIDANCE}
    />
  );
}

function DriverPublicationUnavailabilityCard({ action = null, publicationUnavailability = null }) {
  const normalizedUnavailability = normalizeDriverPortalPublicationUnavailability(publicationUnavailability);
  return normalizedUnavailability?.code === DRIVER_PORTAL_PUBLICATION_WITHDRAWN_CODE
    ? <DriverPublicationWithdrawnCard action={action} />
    : <DriverRouteUpdatePendingCard action={action} message={normalizedUnavailability?.message} />;
}

function DriverTruckOutOfServiceCard({ truckLabels = [], truckCount = 0 }) {
  const labels = normalizeDriverPortalTruckIds(truckLabels);
  const hasMultipleTrucks = Math.max(labels.length, Number(truckCount) || 0) > 1;
  const truckList = labels.join(", ");
  return (
    <DriverPortalScenicStateCard
      tone="empty"
      title={hasMultipleTrucks ? "Camions hors service" : "Camion hors service"}
      message={hasMultipleTrucks
        ? "Ces camions sont hors dispatch pour cette journée. Aucune tournée n’est à effectuer."
        : "Ce camion est hors dispatch pour cette journée. Aucune tournée n’est à effectuer."}
      guidance={truckList
        ? `${truckList} ${hasMultipleTrucks ? "seront réactivés" : "sera réactivé"} dans le dispatch par votre répartiteur.`
        : hasMultipleTrucks
          ? "Votre répartiteur pourra vous informer lorsque ces camions seront réactivés dans le dispatch."
          : "Il pourra vous informer lorsque le camion sera réactivé dans le dispatch."}
    />
  );
}

function DriverDispatchDisabledNotice({ truckLabels = [], truckCount = 0 }) {
  const labels = normalizeDriverPortalTruckIds(truckLabels);
  const hasMultipleTrucks = Math.max(labels.length, Number(truckCount) || 0) > 1;
  const truckList = labels.join(", ");
  return (
    <section
      className="rounded-[24px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800 shadow-[0_14px_32px_rgba(217,119,6,0.08)]"
      data-driver-dispatch-disabled-notice
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-amber-700">
          <Truck size={18} aria-hidden="true" />
        </span>
        <div>
          <p className="font-semibold">Camion{hasMultipleTrucks ? "s" : ""} hors dispatch</p>
          <p className="mt-1 text-amber-800/90">
            {truckList ? `${truckList} : ` : ""}les anciennes étapes sont masquées. Les tournées actives restent disponibles ci-dessous.
          </p>
        </div>
      </div>
    </section>
  );
}

function DriverInfoRow({
  icon: Icon,
  label,
  value,
}) {
  return (
    <div className="flex items-start gap-3 rounded-[22px] border border-slate-200 bg-slate-50/80 px-4 py-3">
      <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-accent shadow-page-loading-spinner">
        <Icon size={18} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="text-[0.68rem] font-black uppercase tracking-[0.14em] text-slate-500">{label}</p>
        <p className="mt-1 text-sm font-semibold leading-6 text-slate-900">{value || "--"}</p>
      </div>
    </div>
  );
}

function DriverActionButton({
  children,
  className = "",
  compact = false,
  ...props
}) {
  const baseClassName = compact
    ? "inline-flex min-h-6 w-fit items-center justify-center gap-1 rounded-[13px] px-2 text-[0.66rem] font-semibold leading-none transition duration-200 disabled:cursor-not-allowed disabled:opacity-60"
    : "inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-[22px] px-5 text-base font-semibold transition duration-200 disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <button
      className={`driver-action ${baseClassName} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

function DriverToolbarButton({
  children,
  className = "",
  compact = false,
  ...props
}) {
  const baseClassName = compact
    ? "inline-flex min-h-7 items-center justify-center gap-0.5 rounded-[4px] border border-slate-200 bg-white px-1.5 py-0.5 text-[0.64rem] font-semibold leading-none text-slate-700 transition duration-200 hover:border-slate-300 hover:text-slate-950"
    : "inline-flex min-h-11 items-center justify-center gap-2 rounded-[4px] border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition duration-200 hover:border-slate-300 hover:text-slate-950";

  return (
    <button
      className={`driver-toolbar-action ${baseClassName} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

function DriverDispatchDateForm({
  dispatchDate,
  dispatchDateOptions,
  hasSelectedDispatchDate,
  onChange,
  onReset,
}) {
  return (
    <div className="grid gap-3 rounded-[24px] border border-slate-200 bg-slate-50/75 px-4 py-4">
      <label className="grid gap-2">
        <span className="text-[0.68rem] font-black uppercase tracking-[0.14em] text-slate-500">Journée dispatch</span>
        <select
          className="min-h-12 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 outline-hidden transition duration-200 focus:border-accent"
          value={dispatchDate}
          onChange={onChange}
        >
          {dispatchDateOptions.map((dispatchDateOption) => (
            <option key={dispatchDateOption.value} value={dispatchDateOption.value}>
              {dispatchDateOption.label}
            </option>
          ))}
        </select>
      </label>
      {hasSelectedDispatchDate ? (
        <div className="flex flex-wrap gap-2">
          <button
            className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition duration-200 hover:border-slate-300 hover:text-slate-950"
            type="button"
            onClick={onReset}
          >
            Revenir à la journée active
          </button>
        </div>
      ) : null}
      <p className="text-sm leading-6 text-slate-600">
        Choisissez une autre journée si aucune optimisation n&apos;est disponible aujourd&apos;hui pour votre camion.
      </p>
    </div>
  );
}

function buildPreviewTargetLabel(target) {
  if (!target || typeof target !== "object") {
    return "Chauffeur indisponible";
  }

  const driverLabel = typeof target.driver_name === "string" && target.driver_name.trim()
    ? target.driver_name.trim()
    : "Chauffeur sans nom";
  const truckLabels = Array.isArray(target.truck_labels)
    ? target.truck_labels.filter(Boolean)
    : [];

  if (!truckLabels.length) {
    return driverLabel;
  }

  return `${driverLabel} · ${truckLabels.join(" / ")}`;
}

function buildPreviewTargetHelperText(target) {
  const truckIds = Array.isArray(target?.truck_ids) ? target.truck_ids.filter(Boolean) : [];
  if (!truckIds.length) {
    return "Affiche le portail de ce chauffeur.";
  }

  return `Aperçu sur ${truckIds.length} camion${truckIds.length > 1 ? "s" : ""} lié${truckIds.length > 1 ? "s" : ""}.`;
}

function DriverPreviewSelectionForm({
  canApply,
  dispatchDate = "",
  dispatchDateErrorMessage = "",
  dispatchDateOptions = [],
  dispatchDateStatus = "idle",
  onApply,
  onChange,
  onDispatchDateChange,
  onReset,
  onRetryDispatchDates,
  publicationUnavailability = null,
  previewDriverUserId,
  previewTargets,
  showDispatchDate = false,
}) {
  const hasDispatchDateOptions = dispatchDateOptions.length > 0;
  const normalizedPublicationUnavailability = normalizeDriverPortalPublicationUnavailability(publicationUnavailability);
  const hasPublicationUnavailabilityNotice = Boolean(normalizedPublicationUnavailability);
  const isDispatchDateLoading = dispatchDateStatus === "loading" || dispatchDateStatus === "refreshing";
  const dispatchDatePlaceholder = hasPublicationUnavailabilityNotice
    ? normalizedPublicationUnavailability.code === DRIVER_PORTAL_PUBLICATION_WITHDRAWN_CODE
      ? DRIVER_PORTAL_PUBLICATION_WITHDRAWN_TITLE
      : DRIVER_PORTAL_ROUTE_UPDATE_PENDING_TITLE
    : isDispatchDateLoading
      ? "Chargement des journées publiées..."
    : dispatchDateStatus === "error"
      ? "Journées indisponibles"
      : "Aucune journée publiée";

  return (
    <form className="grid gap-3 rounded-[24px] border border-slate-200 bg-slate-50/75 px-4 py-4" onSubmit={onApply}>
      <label className="grid gap-2">
        <span className="text-[0.68rem] font-black uppercase tracking-[0.14em] text-slate-500">Aperçu chauffeur</span>
        <select
          className="min-h-12 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 outline-hidden transition duration-200 focus:border-accent"
          value={previewDriverUserId}
          onChange={onChange}
        >
          <option value="">Choisir un chauffeur lié</option>
          {previewTargets.map((target) => (
            <option key={target.driver_user_id} value={target.driver_user_id}>
              {buildPreviewTargetLabel(target)}
            </option>
          ))}
        </select>
      </label>
      {showDispatchDate && previewDriverUserId ? (
        <label className="grid gap-2">
          <span className="text-[0.68rem] font-black uppercase tracking-[0.14em] text-slate-500">Journée dispatch</span>
          <select
            className="min-h-12 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 outline-hidden transition duration-200 focus:border-accent disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
            value={hasDispatchDateOptions ? dispatchDate : ""}
            onChange={onDispatchDateChange}
            disabled={!hasDispatchDateOptions}
          >
            {!hasDispatchDateOptions ? <option value="">{dispatchDatePlaceholder}</option> : null}
            {dispatchDateOptions.map((dispatchDateOption) => (
              <option key={dispatchDateOption.value} value={dispatchDateOption.value}>
                {dispatchDateOption.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          className="inline-flex min-h-11 items-center justify-center rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white shadow-[0_14px_28px_rgba(15,23,42,0.14)] transition duration-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-55"
          type="submit"
          disabled={!canApply}
        >
          Continuer
        </button>
        <button
          className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition duration-200 hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-55"
          type="button"
          onClick={onReset}
          disabled={!previewDriverUserId}
        >
          Effacer la sélection
        </button>
      </div>
      {hasPublicationUnavailabilityNotice && previewDriverUserId ? (
        <DriverPublicationUnavailabilityCard publicationUnavailability={normalizedPublicationUnavailability} />
      ) : dispatchDateStatus === "error" && previewDriverUserId ? (
        <div className="grid gap-2 text-sm leading-6 text-rose-700">
          <p>{dispatchDateErrorMessage || "Impossible de charger les journées publiées de ce chauffeur."}</p>
          <button
            className="w-fit font-semibold underline decoration-rose-300 underline-offset-4"
            type="button"
            onClick={onRetryDispatchDates}
          >
            Réessayer
          </button>
        </div>
      ) : (
        <p className="text-sm leading-6 text-slate-600">
          {previewDriverUserId
            ? "Choisissez maintenant une de ses journées publiées, puis continuez."
            : "Sélectionnez un chauffeur lié pour voir ses journées publiées sans utiliser son mot de passe."}
        </p>
      )}
    </form>
  );
}

function DriverSectionHeading({
  action = null,
  eyebrow,
  subtitle,
  title,
}) {
  const hasEyebrow = Boolean(eyebrow);

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        {hasEyebrow ? <p className="text-[0.68rem] font-black uppercase tracking-[0.16em] text-[#537160]">{eyebrow}</p> : null}
        <h2 className={hasEyebrow ? "mt-2 text-xl font-semibold tracking-[-0.04em] text-slate-950" : "text-xl font-semibold tracking-[-0.04em] text-slate-950"}>{title}</h2>
        {subtitle ? <p className="mt-2 text-sm leading-6 text-slate-600">{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}

function DriverMetricCard({
  accentClassName = `${DRIVER_PORTAL_BRAND_PILL_CLASS_NAME} rounded-lg`,
  helper,
  icon: Icon,
  label,
  value,
}) {
  return (
    <section className="rounded-[26px] border border-white/80 bg-white/88 px-4 py-4 shadow-[0_18px_40px_rgba(17,24,39,0.06)] backdrop-blur">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[0.68rem] font-black uppercase tracking-[0.14em] text-slate-500">{label}</p>
          <p className="mt-2 text-xl font-semibold tracking-[-0.04em] text-slate-950">{value}</p>
        </div>
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${accentClassName}`}>
          <Icon size={18} aria-hidden="true" />
        </span>
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-600">{helper}</p>
    </section>
  );
}

function DriverPortalStopMetricsGrid({
  group,
  totalCompartments = 0,
  hideRouteDistance = false,
  routeDistanceKmOverride = null,
}) {
  if (!group) return null;
  const distance = routeDistanceKmOverride != null && Number.isFinite(Number(routeDistanceKmOverride))
    ? Number(routeDistanceKmOverride) : group.routeDistanceKm;
  const hasQuantityDifference = hasDriverPortalQuantityDifference(group.plannedTotalQuantityTons, group.totalQuantityTons);
  const metrics = [
    group.scheduledLabel ? { key: "time", icon: Clock3, label: "Heure", value: `${group.priorityLabel ? `${group.priorityLabel} · ` : ""}${group.scheduledLabel}`, priority: Boolean(group.priorityLabel) } : null,
    !hideRouteDistance ? { key: "distance", icon: Route, label: "Km", value: distance != null ? `${wholeNumberFormatter.format(Math.round(distance))} km` : group.routeDistanceLabel || "--" } : null,
    { key: "weight", icon: Scale, label: "Tonnage", value: formatQuantity(group.totalQuantityTons) },
    { key: "orders", icon: Package, label: "Commandes", value: `${group.uniqueOrderCount} cmd` },
    { key: "compartments", icon: Warehouse, label: "Compartiments", value: totalCompartments > 0 ? `${group.usedCompartmentCount}/${totalCompartments}` : `${group.usedCompartmentCount || "--"}` },
  ].filter(Boolean);

  return (
    <div className="driver-stop-metrics">
      {metrics.map(({ key, icon: Icon, label, value, priority }) => (
        <div key={key} className={`driver-stop-metric driver-stop-metric--${key}${priority ? " driver-stop-metric--priority" : ""}`} title={label}>
          <Icon aria-hidden="true" />
          <span className="sr-only">{label}</span>
          <span>{value}</span>
          {key === "weight" && hasQuantityDifference ? <span className="sr-only">Tonnage ajusté</span> : null}
        </div>
      ))}
    </div>
  );
}

function DriverRoutePreviewPanel({
  assignment = null,
  className = "",
  heading = "",
  address = "",
  addressLabel = "",
  mapsUrl = "",
  routeDistanceLabel = "",
  trip = null,
  autoPreview = false,
  onPreviewConsumed,
}) {
  const mapId = React.useId();
  const canShowMap = Boolean(assignment || trip);
  const preview = useDriverRoutePreview({ autoPreview, onPreviewConsumed, canShowMap });
  const { isExpanded } = preview;

  return (
    <div className={`driver-destination-block ${className}`}>
      <div className="driver-destination-heading">
        <h2 className="min-w-0 font-semibold text-slate-950">{heading}</h2>
        <div className="driver-destination-actions">
          {mapsUrl ? (
            <a className="driver-maps-link" href={mapsUrl} target="_blank" rel="noreferrer noopener" aria-label="Ouvrir l’itinéraire dans Google Maps" aria-describedby={`${mapId}-address`} title="Ouvrir l’itinéraire dans Google Maps">
              <Route size={18} aria-hidden="true" />
            </a>
          ) : null}
          {canShowMap ? (
            <button ref={preview.buttonRef} className="driver-map-toggle driver-map-toggle--compact" type="button" aria-label={isExpanded ? "Masquer la carte" : "Afficher la carte"} aria-describedby={`${mapId}-address`} title={isExpanded ? "Masquer la carte" : "Afficher la carte"} aria-expanded={isExpanded} aria-controls={mapId} onClick={preview.toggle}>
              <MapIcon size={18} aria-hidden="true" /><span>Carte</span>
            </button>
          ) : null}
        </div>
      </div>
      <div id={`${mapId}-address`}>
        <DriverAddressLine
          className="driver-primary-address mt-1 text-slate-600"
          displayValue={addressLabel}
          singleLine={false}
          value={address}
        />
      </div>
      {canShowMap ? <div ref={preview.frameRef} id={mapId} className="driver-route-preview" data-phase={preview.phase}
        aria-hidden={!isExpanded} inert={!isExpanded ? "" : undefined}>
        {preview.isMounted ? <div className="driver-route-preview__space"><div ref={preview.panelRef} className="driver-map-preview driver-map-canvas driver-route-preview__map"
          onPointerDownCapture={preview.keepOpen} onWheelCapture={preview.keepOpen} onFocusCapture={preview.keepOpen}>
          <div className="driver-route-preview__summary">
            <span><Route size={18} aria-hidden="true" />{routeDistanceLabel ? "À parcourir" : "Aperçu du trajet"}</span>
            {routeDistanceLabel ? <strong>{routeDistanceLabel}</strong> : null}
          </div>
          <RouteMapPanel
            assignment={assignment}
            compact
            customMapHeight={252}
            fillAvailableHeight={false}
            isRouteViewActive
            renderInline
            renderLegend={false}
            renderObservability={false}
            onControllerReady={preview.onControllerReady}
            onMapRenderMetaChange={preview.onMapRenderMetaChange}
            routeDistanceLabel={routeDistanceLabel}
            stopBadgePresentation="icon"
            trip={trip}
          />
          <div className="driver-route-preview__progress" role={preview.isCountingDown ? "img" : undefined}
            aria-label={preview.isCountingDown ? "La carte se rétracte après 10 secondes. Touchez-la pour la garder ouverte." : undefined}>
            {preview.isCountingDown ? <span style={{ "--driver-route-duration": `${DRIVER_ROUTE_PREVIEW_DURATION_MS}ms` }} /> : null}
          </div>
        </div></div> : null}
      </div> : null}
    </div>
  );
}

function DriverLandingMetricCard({
  accentClassName = `${DRIVER_PORTAL_BRAND_PILL_CLASS_NAME} rounded-xl`,
  helper,
  icon: Icon,
  label,
  hideLabel = false,
  value,
}) {
  return (
    <section className="driver-home-metric">
      <span className={`driver-home-metric-icon ${accentClassName}`}><Icon size={17} aria-hidden="true" /></span>
      <p className={hideLabel ? "sr-only" : undefined}>{label}</p>
      <strong>{value}</strong>
      {helper ? <p className="mt-2 text-[0.68rem] leading-5 text-slate-600">{helper}</p> : null}
    </section>
  );
}

function DriverPortalLandingScreen({
  homeModel,
  onContinue,
  onOpenRoutes,
  onOpenResetConfirmations,
  resetButtonDisabled = false,
}) {
  const completedStops = homeModel.stopGroups.filter(isDriverPortalVisitComplete).length;
  const isComplete = homeModel.stopGroups.length > 0 && completedStops === homeModel.stopGroups.length;
  const currentGroup = homeModel.currentStopGroup;
  const currentTheme = getDriverPortalStepTheme(currentGroup?.stepType);
  const stopListRef = React.useRef(null);

  React.useEffect(() => {
    const list = stopListRef.current;
    const currentStop = list?.querySelector('[aria-current="step"]');
    if (currentStop) list.scrollLeft = currentStop.offsetLeft;
  }, [currentGroup?.groupKey]);

  return (
    <div className="driver-home">
      <div className="driver-home-summary" aria-label="Résumé de la journée">
        <DriverLandingMetricCard icon={Package} label="Commandes" value={String(homeModel.totalOrderCount)} />
        <DriverLandingMetricCard icon={MapPin} label="Arrêts" value={String(homeModel.totalStopCount)} />
        <DriverLandingMetricCard icon={Scale} label="Tonnage total" hideLabel value={formatQuantity(homeModel.totalRouteQuantityTons)} />
      </div>

      <div className="driver-home-feature">
        <div className="driver-home-hero">
          <img src="/page-accueil.jpg" width="1536" height="1024" alt="" loading="eager" decoding="async" />
          <div className="driver-home-truck">
            <Truck size={18} aria-hidden="true" />
            <span>Camion <strong>{homeModel.primaryTruckLabel}</strong></span>
          </div>
        </div>
        <section className={`driver-home-current ${isComplete ? "border-emerald-200 bg-white" : currentTheme.panelClassName}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="driver-eyebrow">{isComplete ? "Journée complétée" : `À faire · Arrêt ${homeModel.currentStopGroupIndex + 1} sur ${homeModel.totalStopCount}`}</p>
            {isComplete ? <CheckCircle2 size={20} className="text-accent" aria-hidden="true" /> : renderDriverPortalProductBadge(currentGroup?.productLabel, currentGroup?.stepType)}
          </div>
          <h2>{isComplete ? "Tournée terminée" : currentGroup?.title || "Aucun arrêt planifié"}</h2>
          {isComplete ? <p>Tous les arrêts de votre journée sont complétés.</p> : (
            <>
              <p className="driver-home-address">{currentGroup?.address || "Adresse non renseignée"}</p>
              <div className="driver-home-current-meta">
                <p className="driver-home-kind">{currentGroup?.stepType === "loading" ? <Truck size={16} aria-hidden="true" /> : <Package size={16} aria-hidden="true" />}{currentGroup?.stepType === "loading" ? "Chargement" : "Livraison"}{isInitialPreloadStep(currentGroup?.primaryStep) ? " · Préchargement" : ""}</p>
                {currentGroup?.scheduledLabel ? <span><Clock3 size={16} aria-hidden="true" />{currentGroup.priorityLabel ? `${currentGroup.priorityLabel} · ` : ""}{currentGroup.scheduledLabel}</span> : null}
                <span><Scale size={16} aria-hidden="true" />{formatQuantity(currentGroup?.totalQuantityTons)}</span>
                {currentGroup?.state?.title ? <span className="driver-home-status">{currentGroup.state.title}</span> : null}
              </div>
            </>
          )}
          <DriverActionButton className={`driver-home-continue ${DRIVER_PORTAL_BRAND_ACTION_CLASS_NAME}`} type="button" onClick={isComplete ? onOpenRoutes : onContinue}>
            {isComplete ? "Voir les résultats" : "Continuer la tournée"}<ArrowRight size={18} aria-hidden="true" />
          </DriverActionButton>
        </section>
      </div>

      <div className="driver-home-progress">
        <div><span>Progression de la journée</span><strong>{completedStops} / {homeModel.totalStopCount} arrêts</strong></div>
        <progress aria-label="Arrêts complétés" value={completedStops} max={Math.max(homeModel.totalStopCount, 1)} />
      </div>

      <section className="driver-home-itinerary">
        <div className="driver-home-itinerary-heading">
          <div><h2>Votre tournée</h2><p>Glissez pour voir les arrêts</p></div>
          <DriverActionButton compact className="border border-slate-200 bg-white text-slate-700" type="button" onClick={onOpenRoutes}>
            Itinéraires<Route size={18} aria-hidden="true" />
          </DriverActionButton>
        </div>
        <ol className="driver-home-stops" ref={stopListRef} tabIndex={0} aria-label="Arrêts dans l’ordre de passage">
          {homeModel.stopGroups.map((group, index) => {
            const isDone = isDriverPortalVisitComplete(group);
            const plannedQty = group.plannedTotalQuantityTons ?? group.totalQuantityTons;
            const hasDiff = hasDriverPortalQuantityDifference(plannedQty, group.totalQuantityTons);
            return (
              <li key={group.groupKey || index} className={`driver-home-stop ${getDriverPortalStepTheme(group.stepType).panelClassName}`} aria-current={!isComplete && group.groupKey === currentGroup?.groupKey ? "step" : undefined}>
                <span className="driver-home-stop-number">{isDone ? <CheckCircle2 size={18} aria-label="Complété" /> : index + 1}</span>
                <div className="min-w-0">
                  <p title={group.title || undefined}>{group.title || "Sans nom"}</p>
                  <small>{group.stepType === "loading" ? "Chargement" : "Livraison"}{group.scheduledLabel ? ` · ${group.scheduledLabel}` : ""}{isDone ? <span className="sr-only"> · Complété</span> : null}</small>
                </div>
                <span className="driver-home-stop-quantity">{formatQuantity(group.totalQuantityTons)}{hasDiff ? <del aria-label={`Prévu : ${formatQuantity(plannedQty)}`}>{formatQuantity(plannedQty)}</del> : null}</span>
              </li>
            );
          })}
        </ol>
      </section>

      <details className="driver-home-details">
        <summary>Détails du camion et de la charge</summary>
        <dl>
          <div><dt>Camion</dt><dd>{homeModel.primaryTruckLabel}</dd></div>
          <div><dt>Préchargement</dt><dd>{homeModel.preloadActive ? "Oui" : "Non"}</dd></div>
          <div><dt>Pic de charge</dt><dd>{homeModel.peakLoadValue}</dd></div>
          <div><dt>Compartiments</dt><dd>{homeModel.totalCompartments > 0 ? `${homeModel.occupiedCompartments}/${homeModel.totalCompartments}` : "--"}</dd></div>
        </dl>
      </details>
      {homeModel.hasResettableValidations ? (
        <div className="driver-home-reset">
          <button type="button" onClick={onOpenResetConfirmations} disabled={resetButtonDisabled}>
            {resetButtonDisabled ? "Réinitialisation..." : "Réinitialiser"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function DriverPortalDispatchDateEntryScreen({
  dispatchDate,
  dispatchDateOptions,
  driverDisplayName,
  onChange,
  onContinue,
}) {
  return (
    <DriverPortalScenicPanel contentClassName="flex flex-col justify-center gap-4">
        <section className="rounded-[28px] border border-white/75 bg-white/82 px-4 py-4 shadow-[0_18px_42px_rgba(17,24,39,0.07)] backdrop-blur">
          <DriverSectionHeading
            eyebrow="Journée dispatch"
            title="Choisissez la date à afficher"
            subtitle={`Sélectionnez d'abord la journée dispatch à ouvrir pour ${driverDisplayName}.`}
          />
          <div className="mt-4">
            <DriverDispatchDateForm
              dispatchDate={dispatchDate}
              dispatchDateOptions={dispatchDateOptions}
              hasSelectedDispatchDate={false}
              onChange={onChange}
              onReset={() => {}}
            />
            <div className="mt-3">
              <DriverActionButton
                className={DRIVER_PORTAL_BRAND_ACTION_CLASS_NAME}
                type="button"
                onClick={onContinue}
                disabled={!dispatchDate}
              >
                Continuer
                <ArrowRight size={18} aria-hidden="true" />
              </DriverActionButton>
            </div>
          </div>
        </section>
    </DriverPortalScenicPanel>
  );
}

function DriverAddressLine({
  className = "",
  clientName = "",
  clientNameClassName = "",
  clientRowClassName = "",
  displayValue = "",
  mapsUrl = "",
  prefixLabel = "Client",
  singleLine = true,
  value = "",
}) {
  const normalizedClientName = typeof clientName === "string" ? clientName.trim() : "";
  const normalizedPrefixLabel = typeof prefixLabel === "string" ? prefixLabel.trim() : "";
  if (!value && !normalizedClientName) {
    return null;
  }

  const clientLabel = normalizedPrefixLabel
    ? `${normalizedPrefixLabel}: ${normalizedClientName}`
    : normalizedClientName;

  if (!value && normalizedClientName) {
    return (
      <div className={`driver-address flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden ${clientRowClassName} ${className}`}>
        <MapPin aria-hidden="true" className="h-3 w-3 shrink-0 text-[rgba(100,116,139,0.8)]" strokeWidth={2.05} />
        <p
          className={`driver-command-client min-w-0 font-black uppercase leading-none ${clientNameClassName || "text-[0.68rem] tracking-[0.12em] text-slate-500"}`}
          title={clientLabel}
        >
          {clientLabel}
        </p>
      </div>
    );
  }

  if (value && !normalizedClientName) {
    const addressClassName = singleLine
      ? "min-w-0 flex-1 truncate leading-[1.2rem]"
      : "min-w-0 flex-1 overflow-hidden break-words leading-[1.2rem]";
    const addressStyle = singleLine ? undefined : DRIVER_TWO_LINE_CLAMP_STYLE;

    return (
      <div className={`driver-address flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden ${className}`}>
        <MapPin aria-hidden="true" className="h-3 w-3 shrink-0 text-[rgba(100,116,139,0.8)]" strokeWidth={2.05} />
        <p className={`${addressClassName}`} style={addressStyle} title={value}>
          {displayValue || value}
        </p>
        {mapsUrl ? (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Ouvrir l’itinéraire dans Google Maps"
            title="Ouvrir l’itinéraire dans Google Maps"
            className="driver-maps-link inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[rgba(148,163,184,0.24)] bg-white/78 text-[rgba(56,99,142,0.86)] shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition hover:border-[rgba(56,99,142,0.28)] hover:bg-white hover:text-[rgba(37,99,235,0.92)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[rgba(59,130,246,0.32)]"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Route aria-hidden="true" className="h-3.15 w-3.15" strokeWidth={2.05} />
          </a>
        ) : null}
      </div>
    );
  }

  const addressClassName = singleLine
    ? "block w-full min-w-0 max-w-full truncate leading-[1.2rem]"
    : "block w-full min-w-0 max-w-full overflow-hidden break-words leading-[1.2rem]";
  const addressStyle = singleLine ? undefined : DRIVER_TWO_LINE_CLAMP_STYLE;

  return (
    <div className={`driver-address w-full min-w-0 max-w-full overflow-hidden ${className}`}>
      <div
        className={`grid w-full min-w-0 max-w-full items-start gap-x-1.5 overflow-hidden ${mapsUrl ? "grid-cols-[auto_minmax(0,1fr)_auto]" : "grid-cols-[auto_minmax(0,1fr)]"}`}
      >
        {value ? <MapPin aria-hidden="true" className="col-start-1 row-start-1 mt-[0.12rem] h-3 w-3 shrink-0 text-[rgba(100,116,139,0.8)]" strokeWidth={2.05} /> : null}
        <div className={`col-start-2 min-w-0 max-w-full overflow-hidden ${clientRowClassName}`}>
          {normalizedClientName ? (
            <div className="flex w-full min-w-0 max-w-full items-start gap-1.5 overflow-hidden">
              <p
                className={`driver-command-client min-w-0 max-w-full flex-1 truncate font-black uppercase leading-[1.05rem] ${clientNameClassName || "text-[0.68rem] tracking-[0.12em] text-slate-500"}`}
                title={clientLabel}
              >
                {clientLabel}
              </p>
            </div>
          ) : null}
          {value ? (
            <p className={`${normalizedClientName ? "mt-0.5" : ""} max-w-full ${addressClassName}`} style={addressStyle} title={value}>
              {value}
            </p>
          ) : null}
        </div>
        {mapsUrl ? (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Ouvrir l’itinéraire dans Google Maps"
            title="Ouvrir l’itinéraire dans Google Maps"
            className="driver-maps-link col-start-3 row-start-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[rgba(148,163,184,0.24)] bg-white/78 text-[rgba(56,99,142,0.86)] shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition hover:border-[rgba(56,99,142,0.28)] hover:bg-white hover:text-[rgba(37,99,235,0.92)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[rgba(59,130,246,0.32)]"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Route aria-hidden="true" className="h-3.15 w-3.15" strokeWidth={2.05} />
          </a>
        ) : null}
      </div>
    </div>
  );
}

function DriverProgressStepCard({
  caption,
  emphasize = false,
  isTerminal = false,
  step,
}) {
  const isPreloadStep = isInitialPreloadStep(step);
  const stepAddressLabel = getOperationalStepAddress(step);

  if (!step) {
    return (
      <div className="relative pl-5">
        {!isTerminal ? <span className="absolute left-2.25 top-7 -bottom-3.5 w-px bg-[#d8e2d4]" aria-hidden="true" /> : null}
        <span className="absolute left-0 top-6 h-4.5 w-4.5 rounded-full border border-[#d8e2d4] bg-[#f7f4ec]" aria-hidden="true" />
        <div className="rounded-[24px] border border-dashed border-[#d8e2d4] bg-[#faf7f1] px-4 py-4">
          <p className="text-[0.68rem] font-black uppercase tracking-[0.14em] text-slate-500">{caption}</p>
          <p className="mt-2 text-sm font-semibold text-slate-900">Aucun arrêt</p>
          <p className="mt-1 text-sm leading-6 text-slate-600">Aucun point opérationnel n'est disponible à cette position dans la journée.</p>
        </div>
      </div>
    );
  }

  const stepState = resolveOperationalStepState(step);
	const stepTimeLabel = formatDriverPortalProjectedStepTime(step, step?.linked_delivery, formatStepTime);

  return (
    <div className="relative pl-5">
      {!isTerminal ? <span className="absolute left-2.25 top-7 -bottom-3.5 w-px bg-[#d8e2d4]" aria-hidden="true" /> : null}
      <span className={`absolute left-0 top-6 h-4.5 w-4.5 rounded-full border ${emphasize ? "border-[#1f4d2f] bg-[#1f4d2f]" : stepState.tone === "success" ? "border-emerald-300 bg-emerald-100" : "border-[#d8e2d4] bg-white"}`} aria-hidden="true" />
      <div className={`rounded-[26px] border px-4 py-4 shadow-[0_16px_36px_rgba(17,24,39,0.05)] ${emphasize ? "border-[#214d30] bg-[#214d30] text-white" : "border-slate-200 bg-white text-slate-950"}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={`text-[0.68rem] font-black uppercase tracking-[0.14em] ${emphasize ? "text-emerald-100/90" : "text-slate-500"}`}>{caption}</p>
            <h3 className="mt-2 text-base font-semibold tracking-[-0.03em]">{getOperationalStepLabel(step)}</h3>
          </div>
          <span className={`shrink-0 rounded-full border px-3 py-1 text-[0.72rem] font-black uppercase tracking-[0.12em] ${emphasize ? "border-white/15 bg-white/10 text-white" : stepState.badgeClassName}`}>
            {stepState.title}
          </span>
        </div>
        <DriverAddressLine
          className={`mt-2 text-sm leading-6 ${emphasize ? "text-emerald-50/88" : "text-slate-600"}`}
          mapsUrl={buildDriverPortalStepMapsUrl(step)}
          singleLine
          value={isPreloadStep ? stepAddressLabel : `${getOperationalStepTypeLabel(step)} · ${stepAddressLabel}`}
        />
        <div className="mt-3 flex flex-wrap gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.08em]">
          <span className={`rounded-full px-3 py-1 ${emphasize ? "bg-white/12 text-white" : "bg-[#eef5ee] text-[#1f4d2f]"}`}>{formatQuantity(step?.quantity_tons)}</span>
          <span className={`rounded-full px-3 py-1 ${emphasize ? "bg-white/12 text-white" : "bg-slate-100 text-slate-600"}`}>{getOperationalStepTruckLabel(step)}</span>
          {stepTimeLabel ? <span className={`rounded-full px-3 py-1 ${emphasize ? "bg-white/12 text-white" : "bg-slate-100 text-slate-600"}`}>{stepTimeLabel}</span> : null}
        </div>
      </div>
    </div>
  );
}

function DriverOperationalStopCard({
  isCurrent = false,
  onOpen = null,
  occurrenceCount = 1,
  displayStopNumber = null,
  sequenceSummaryLabel = "",
  group = null,
  mapsUrl = "",
  step,
}) {
  const stepState = group?.state || resolveOperationalStepState(step);
  const actionLabel = "Ouvrir l'arrêt";
  const resolvedStopNumber = Number.isFinite(Number(displayStopNumber)) && Number(displayStopNumber) > 0
    ? Number(displayStopNumber)
    : Number.isFinite(Number(step?.sequence)) && Number(step.sequence) > 0
      ? Number(step.sequence)
      : null;
  const plannedQuantityTons = group?.plannedTotalQuantityTons ?? (Number.isFinite(Number(step?.quantity_tons))
    ? Number(step.quantity_tons)
    : Number.isFinite(Number(step?.linked_delivery?.quantity_tons))
      ? Number(step.linked_delivery.quantity_tons)
      : null);
  const actualQuantityTons = group?.totalQuantityTons ?? (step?.step_type === "loading"
    ? (Number.isFinite(Number(step?.actual_loaded_quantity_tons)) ? Number(step.actual_loaded_quantity_tons) : null)
    : (Number.isFinite(Number(step?.linked_delivery?.actual_delivered_quantity_tons)) ? Number(step.linked_delivery.actual_delivered_quantity_tons) : null));
  const hasTonnageDifference = hasDriverPortalQuantityDifference(plannedQuantityTons, actualQuantityTons);
  const content = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[0.64rem] font-black uppercase tracking-[0.16em] text-[#537160]">{getOperationalStepTypeLabel(step)} · arrêt {resolvedStopNumber || "--"}</p>
          <h3 className="mt-1.5 truncate text-[1.02rem] font-semibold leading-5 tracking-[-0.03em] text-slate-950">{getOperationalStepLabel(step)}</h3>
          {occurrenceCount > 1 ? (
            <p className="mt-1 text-[0.68rem] font-semibold leading-5 text-slate-500">
              {occurrenceCount} arrêts regroupés{sequenceSummaryLabel ? ` · séquences ${sequenceSummaryLabel}` : ""}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className={`rounded-full border px-2.5 py-0.5 text-[0.66rem] font-black uppercase tracking-[0.12em] ${stepState.badgeClassName}`}>
            {stepState.title}
          </span>
          {stepState.tone === "success" && hasTonnageDifference ? (
            <p className="flex items-center gap-1.5 text-[1.12rem] font-black leading-none tracking-[-0.04em]">
              <span className="text-amber-700">{formatQuantity(actualQuantityTons)}</span>
              <span className="relative inline-block text-slate-400">
                {formatQuantity(plannedQuantityTons)}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    backgroundImage: 'linear-gradient(to top right, transparent calc(50% - 0.5px), #94a3b8 calc(50% - 0.5px), #94a3b8 calc(50% + 0.5px), transparent calc(50% + 0.5px))',
                  }}
                />
              </span>
            </p>
          ) : (
            <p className="text-[1.12rem] font-black leading-none tracking-[-0.04em] text-[#7c3aed]">
              {formatQuantity(group?.totalQuantityTons ?? step?.quantity_tons)}
            </p>
          )}
        </div>
      </div>

      <DriverAddressLine
        className="mt-2 min-w-0 max-w-full text-[0.8rem] leading-5 text-slate-600"
        mapsUrl={mapsUrl || buildDriverPortalStepMapsUrl(step)}
        singleLine
        value={getOperationalStepAddress(step)}
      />

      {typeof onOpen === "function" ? (
        <span className={`mt-2.5 inline-flex min-h-9 w-full items-center justify-center gap-2 rounded-lg px-4 text-[0.86rem] font-semibold ${DRIVER_PORTAL_BRAND_ACTION_CLASS_NAME}`}>
          {actionLabel}
          <ArrowRight size={15} aria-hidden="true" />
        </span>
      ) : null}
    </>
  );

  const className = `driver-route-stop box-border w-full min-w-0 max-w-full border text-left ${getDriverPortalStepTheme(step?.step_type).panelClassName} ${isCurrent ? "driver-route-stop--current" : ""}`;

  if (typeof onOpen === "function") {
    return (
      <button className={className} type="button" onClick={onOpen}>
        {content}
      </button>
    );
  }

  return <section className={className}>{content}</section>;
}

function formatDriverPortalIncidentHour(value, minutes = null) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  const match = /^(\d{2}):(\d{2})$/.exec(normalizedValue);
  if (match) {
    return `${Number(match[1])} h ${match[2]}`;
  }
  if (Number.isFinite(Number(minutes))) {
    const safeMinutes = Number(minutes);
    const hour = Math.floor((safeMinutes % 1440) / 60);
    const minute = Math.floor(safeMinutes % 60);
    return `${hour} h ${String(minute).padStart(2, "0")}`;
  }
  return "--";
}

function DriverTruckIncidentCard({ incident = null }) {
  if (!incident) {
    return null;
  }
  const startLabel = formatDriverPortalIncidentHour(incident.startTime, incident.displayStartMinutes);
  const endLabel = formatDriverPortalIncidentHour(incident.endTime, incident.displayEndMinutes);
  return (
    <section
      aria-label={`Bris mécanique ou contretemps de ${startLabel} à ${endLabel}`}
      className="box-border w-full min-w-0 max-w-full overflow-hidden rounded-[24px] border border-red-300 bg-[linear-gradient(135deg,#fff1f2_0%,#fee2e2_100%)] px-3.5 py-3 text-left shadow-[0_18px_40px_rgba(185,28,28,0.10)]"
      data-driver-portal-truck-incident="true"
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] border border-red-300 bg-white/80 text-red-700">
          <Wrench aria-hidden="true" className="h-5 w-5" strokeWidth={2.35} />
        </span>
        <div className="min-w-0">
          <p className="m-0 text-[0.64rem] font-black uppercase tracking-[0.14em] text-red-700">Bris mécanique / Contretemps</p>
          <p className="m-0 mt-1 flex items-center gap-2 text-[1.02rem] font-black leading-5 tracking-[-0.02em] text-red-950">
            <Clock3 aria-hidden="true" className="h-4 w-4 shrink-0 text-red-700" strokeWidth={2.4} />
            <span>{startLabel} — {endLabel}</span>
          </p>
        </div>
      </div>
    </section>
  );
}

function buildDriverPortalLoginPath(pathname = "") {
  return typeof pathname === "string" && pathname.startsWith("/static/react/")
    ? "/static/react/login"
    : "/login";
}

function useDriverSignOut() {
  const navigate = useNavigate();

  return React.useCallback(async () => {
    await signOutSupabaseSession({ scope: "local" });
    if (typeof window !== "undefined" && typeof window.location?.replace === "function") {
      window.location.replace(buildDriverPortalLoginPath(window.location.pathname));
      return;
    }

    navigate("/login", { replace: true });
  }, [navigate]);
}

export default function DriverDeliveriesPage() {
  const { deliveryId: routeDeliveryId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const signOut = useDriverSignOut();
  const redirectToExpiredSessionLogin = React.useCallback(() => {
    if (typeof window !== "undefined" && typeof window.location?.replace === "function") {
      window.location.replace(buildDriverPortalLoginPath(window.location.pathname));
      return;
    }

    navigate("/login", { replace: true });
  }, [navigate]);
  useSupabaseInactivityTimeout({
    onLoggedOut: redirectToExpiredSessionLogin,
  });
  const { access, isReady: isAccessReady, session } = useAccess();
  React.useEffect(() => {
    if (isAccessReady && !session) {
      redirectToExpiredSessionLogin();
    }
  }, [isAccessReady, redirectToExpiredSessionLogin, session]);
  const sessionUserId = typeof session?.user?.id === "string" ? session.user.id.trim() : "";
  useSessionPresenceHeartbeat({
    enabled: Boolean(sessionUserId && isAccessReady),
    section: "Portail chauffeur",
    userId: sessionUserId,
  });
  const selectedDispatchDate = React.useMemo(() => readDriverDispatchDateFromSearch(location.search), [location.search]);
  const selectedDispatchPeriod = React.useMemo(() => readDriverDispatchPeriodFromSearch(location.search), [location.search]);
  const selectedPreviewDriverUserId = React.useMemo(() => readDriverPreviewDriverUserIdFromSearch(location.search), [location.search]);
  const activeDriverPortalContextKey = `${selectedDispatchDate}::${selectedDispatchPeriod}::${selectedPreviewDriverUserId}`;
  const requestedFocusDeliveryId = React.useMemo(() => readDriverPortalFocusDeliveryIdFromSearch(location.search), [location.search]);
  const requestedFocusGroupKey = React.useMemo(() => readDriverPortalFocusGroupKeyFromSearch(location.search), [location.search]);
  const selectedPortalScreen = React.useMemo(() => readDriverPortalScreenFromSearch(location.search), [location.search]);
  const normalizedRouteDeliveryId = React.useMemo(() => normalizeDriverPortalFocusDeliveryId(routeDeliveryId), [routeDeliveryId]);
  const isDriverPortalSession = isAccessReady && (
    access?.accountType === "driver_portal"
    || (access?.dispatcherWorkspaceAccess === false && access?.driverPortalAccess === true)
  );
  const hasSelectedDriverPortalPreview = Boolean(selectedPreviewDriverUserId);
  const isActiveAdminPreview = hasSelectedDriverPortalPreview && !isDriverPortalSession;
  const shouldGateLandingOnDispatchDateSelection = (isDriverPortalSession || hasSelectedDriverPortalPreview)
    && !selectedDispatchDate
    && !requestedFocusDeliveryId
    && !requestedFocusGroupKey
    && !normalizedRouteDeliveryId
    && !selectedPortalScreen;
  const todayDispatchDate = React.useMemo(() => buildCurrentDriverPortalDispatchDate(), []);
  const [pendingDispatchDate, setPendingDispatchDate] = React.useState(selectedDispatchDate);
  const [pendingDispatchPeriod, setPendingDispatchPeriod] = React.useState(selectedDispatchPeriod);
  const [pendingPreviewDriverUserId, setPendingPreviewDriverUserId] = React.useState(selectedPreviewDriverUserId);
  const [pendingPreviewDispatchDayContextState, setPendingPreviewDispatchDayContextState] = React.useState({
    defaultDispatchDate: "",
    defaultDispatchPeriod: "",
    dispatchDays: [],
    driverUserId: "",
    errorMessage: "",
    publicationUnavailability: null,
    status: "idle",
  });
  const [dispatchDayContextState, setDispatchDayContextState] = React.useState({
    defaultDispatchDate: "",
    defaultDispatchPeriod: "",
    dispatchDays: [],
    errorMessage: "",
    publicationUnavailability: null,
    selectionKey: activeDriverPortalContextKey,
    status: "loading",
  });
  const activeDispatchDayContextState = dispatchDayContextState.selectionKey === activeDriverPortalContextKey
    ? dispatchDayContextState
    : {
        defaultDispatchDate: "",
        defaultDispatchPeriod: "",
        dispatchDays: [],
        errorMessage: "",
        publicationUnavailability: null,
        selectionKey: activeDriverPortalContextKey,
        status: "loading",
      };
  const effectiveSelectedDispatchPeriod = normalizeDriverPortalDispatchPeriod(
    selectedDispatchPeriod || activeDispatchDayContextState.defaultDispatchPeriod,
  );
  const activeDriverPortalViewKey = `${selectedDispatchDate}::${effectiveSelectedDispatchPeriod}::${selectedPreviewDriverUserId}`;
  const activeDriverPortalSelectionRef = React.useRef({
    dispatchDate: selectedDispatchDate,
    dispatchPeriod: selectedDispatchPeriod,
    previewDriverUserId: selectedPreviewDriverUserId,
  });
  activeDriverPortalSelectionRef.current = {
    dispatchDate: selectedDispatchDate,
    dispatchPeriod: selectedDispatchPeriod,
    previewDriverUserId: selectedPreviewDriverUserId,
  };
  const dispatchDayContextRequestIdRef = React.useRef(0);
  const pendingPreviewDispatchDayContextRequestIdRef = React.useRef(0);
  const driverPortalViewRequestIdRef = React.useRef(0);
  const routeUpdatePendingLandingRef = React.useRef(false);
  const routeUpdatePendingPreviewRef = React.useRef(false);
  const lastPublicationCursorSignatureRef = React.useRef("");
  const [viewState, setViewState] = React.useState({
    errorMessage: "",
    payload: null,
    publicationUnavailability: null,
    selectionKey: activeDriverPortalViewKey,
    status: "loading",
  });
  const [resetConfirmationsState, setResetConfirmationsState] = React.useState({
    isFinalOpen: false,
    isOpen: false,
    isPending: false,
    successMessage: "",
  });
  const [portalNavigationBusyMessage, setPortalNavigationBusyMessage] = React.useState("");
  const portalNavigationHistoryRef = React.useRef([]);
  const currentPortalPath = React.useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.hash, location.pathname, location.search],
  );

  const routeSuccessMessage = React.useMemo(() => {
    const nextState = location.state;
    return typeof nextState?.successMessage === "string" ? nextState.successMessage.trim() : "";
  }, [location.state]);
  const portalPublicationMessage = React.useMemo(() => {
    const nextState = location.state;
    return typeof nextState?.portalPublicationMessage === "string"
      ? nextState.portalPublicationMessage.trim()
      : "";
  }, [location.state]);

  const successMessage = resetConfirmationsState.successMessage || routeSuccessMessage;

  React.useEffect(() => {
    setPendingDispatchDate(selectedDispatchDate);
    setPendingDispatchPeriod(selectedDispatchPeriod);
  }, [selectedDispatchDate, selectedDispatchPeriod]);

  React.useEffect(() => {
    setPendingPreviewDriverUserId(selectedPreviewDriverUserId);
    if (!selectedPreviewDriverUserId && !selectedDispatchDate) {
      pendingPreviewDispatchDayContextRequestIdRef.current += 1;
      setPendingDispatchDate("");
      setPendingDispatchPeriod("");
      setPendingPreviewDispatchDayContextState({
        defaultDispatchDate: "",
        defaultDispatchPeriod: "",
        dispatchDays: [],
        driverUserId: "",
        errorMessage: "",
        publicationUnavailability: null,
        status: "idle",
      });
    }
  }, [selectedDispatchDate, selectedPreviewDriverUserId]);

  React.useEffect(() => {
    setResetConfirmationsState((currentState) => ({
      ...currentState,
      isOpen: false,
      isPending: false,
      successMessage: "",
    }));
  }, [selectedDispatchDate, selectedDispatchPeriod, selectedPreviewDriverUserId]);

  React.useEffect(() => {
    const isDriverPortalPath = currentPortalPath.startsWith("/portail") || currentPortalPath.startsWith("/driver");
    if (!isDriverPortalPath) {
      portalNavigationHistoryRef.current = [];
      return;
    }

    const nextHistory = portalNavigationHistoryRef.current;
    if (nextHistory[nextHistory.length - 1] !== currentPortalPath) {
      nextHistory.push(currentPortalPath);
    }
  }, [currentPortalPath]);

  const loadDispatchDayContext = React.useCallback(async ({
    dispatchDate = selectedDispatchDate,
    dispatchPeriod = selectedDispatchPeriod,
  } = {}) => {
    const activeSelection = activeDriverPortalSelectionRef.current;
    if (
      activeSelection.dispatchDate !== selectedDispatchDate
      || activeSelection.dispatchPeriod !== selectedDispatchPeriod
      || activeSelection.previewDriverUserId !== selectedPreviewDriverUserId
    ) {
      return {
        defaultDispatchDate: "",
        defaultDispatchPeriod: "",
        dispatchDays: [],
        errorMessage: "",
        publicationUnavailability: null,
        status: "stale",
      };
    }
    const requestId = dispatchDayContextRequestIdRef.current + 1;
    dispatchDayContextRequestIdRef.current = requestId;
    setDispatchDayContextState((currentState) => (currentState.selectionKey === activeDriverPortalContextKey
      ? {
          ...currentState,
          errorMessage: "",
          status: currentState.status === "ready" || currentState.status === "refreshing"
            ? "refreshing"
            : "loading",
        }
      : {
          defaultDispatchDate: "",
          defaultDispatchPeriod: "",
          dispatchDays: [],
          errorMessage: "",
          publicationUnavailability: null,
          selectionKey: activeDriverPortalContextKey,
          status: "loading",
    }));
    try {
      const payload = !normalizeDriverPortalDispatchPeriod(dispatchPeriod, { allowEmpty: true })
        ? await loadAllDriverPublishedDispatchPeriods({
            dispatchDate,
            previewDriverUserId: selectedPreviewDriverUserId,
          })
        : await loadDriverDispatchDayContext({
            dispatchDate,
            dispatchPeriod,
            previewDriverUserId: selectedPreviewDriverUserId,
          });
      const nextState = {
        defaultDispatchDate: normalizeDriverPortalDispatchDate(payload?.default_dispatch_date) || "",
        defaultDispatchPeriod: normalizeDriverPortalDispatchPeriod(payload?.default_dispatch_period, { allowEmpty: true }),
        dispatchDays: Array.isArray(payload?.dispatch_days) ? payload.dispatch_days.filter(Boolean) : [],
        errorMessage: "",
        publicationUnavailability: normalizeDriverPortalPublicationUnavailability(payload?.publication_unavailability),
        selectionKey: activeDriverPortalContextKey,
        status: "ready",
      };
      const currentSelection = activeDriverPortalSelectionRef.current;
      if (
        dispatchDayContextRequestIdRef.current === requestId
        && currentSelection.dispatchDate === selectedDispatchDate
        && currentSelection.dispatchPeriod === selectedDispatchPeriod
        && currentSelection.previewDriverUserId === selectedPreviewDriverUserId
      ) {
        setDispatchDayContextState(nextState);
      }
      return nextState;
    } catch (error) {
      const nextState = {
        defaultDispatchDate: "",
        defaultDispatchPeriod: "",
        dispatchDays: [],
        errorMessage: error instanceof Error ? error.message : "Impossible de charger les journées publiées.",
        publicationUnavailability: normalizeDriverPortalPublicationUnavailability(error),
        selectionKey: activeDriverPortalContextKey,
        status: "error",
      };
      const currentSelection = activeDriverPortalSelectionRef.current;
      if (
        dispatchDayContextRequestIdRef.current === requestId
        && currentSelection.dispatchDate === selectedDispatchDate
        && currentSelection.dispatchPeriod === selectedDispatchPeriod
        && currentSelection.previewDriverUserId === selectedPreviewDriverUserId
      ) {
        setDispatchDayContextState((currentState) => (
          currentState.selectionKey === activeDriverPortalContextKey
          && currentState.status === "refreshing"
          && !nextState.publicationUnavailability
            ? {
                ...currentState,
                errorMessage: "",
                status: "ready",
              }
            : nextState
        ));
      }
      return nextState;
    }
  }, [activeDriverPortalContextKey, selectedDispatchDate, selectedDispatchPeriod, selectedPreviewDriverUserId]);

  React.useEffect(() => {
    void loadDispatchDayContext();
  }, [loadDispatchDayContext]);

  const loadView = React.useCallback(async () => {
    const activeSelection = activeDriverPortalSelectionRef.current;
    if (
      activeSelection.dispatchDate !== selectedDispatchDate
      || activeSelection.dispatchPeriod !== selectedDispatchPeriod
      || activeSelection.previewDriverUserId !== selectedPreviewDriverUserId
    ) {
      return null;
    }
    const requestId = driverPortalViewRequestIdRef.current + 1;
    driverPortalViewRequestIdRef.current = requestId;
    setViewState((currentState) => (currentState.selectionKey === activeDriverPortalViewKey
      ? {
          ...currentState,
          errorMessage: "",
          status: currentState.payload || currentState.publicationUnavailability ? "refreshing" : "loading",
        }
      : {
          errorMessage: "",
          payload: null,
          publicationUnavailability: null,
          selectionKey: activeDriverPortalViewKey,
          status: "loading",
        }));

    try {
      const payload = await loadDriverDeliveries({
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: effectiveSelectedDispatchPeriod,
        previewDriverUserId: selectedPreviewDriverUserId,
      });
      const currentSelection = activeDriverPortalSelectionRef.current;
      if (
        driverPortalViewRequestIdRef.current !== requestId
        || currentSelection.dispatchDate !== selectedDispatchDate
        || currentSelection.dispatchPeriod !== selectedDispatchPeriod
        || currentSelection.previewDriverUserId !== selectedPreviewDriverUserId
      ) {
        return null;
      }
      setViewState({
        errorMessage: "",
        payload,
        publicationUnavailability: null,
        selectionKey: activeDriverPortalViewKey,
        status: "ready",
      });
      return payload;
    } catch (error) {
      const currentSelection = activeDriverPortalSelectionRef.current;
      if (
        driverPortalViewRequestIdRef.current !== requestId
        || currentSelection.dispatchDate !== selectedDispatchDate
        || currentSelection.dispatchPeriod !== selectedDispatchPeriod
        || currentSelection.previewDriverUserId !== selectedPreviewDriverUserId
      ) {
        return null;
      }
      setViewState({
        errorMessage: error instanceof Error ? error.message : "Impossible de charger la journée chauffeur.",
        payload: null,
        publicationUnavailability: normalizeDriverPortalPublicationUnavailability(error),
        selectionKey: activeDriverPortalViewKey,
        status: "error",
      });
      return null;
    }
  }, [activeDriverPortalViewKey, effectiveSelectedDispatchPeriod, selectedDispatchDate, selectedDispatchPeriod, selectedPreviewDriverUserId]);

  React.useEffect(() => {
    if (shouldGateLandingOnDispatchDateSelection && !hasSelectedDriverPortalPreview) {
      return;
    }

    void loadView();
  }, [hasSelectedDriverPortalPreview, loadView, shouldGateLandingOnDispatchDateSelection]);

  const handlePublishedDispatchUnavailable = React.useCallback(async (error = null) => {
    const remotePublicationUnavailability = normalizeDriverPortalPublicationUnavailability(error);
    const nextContext = await loadDispatchDayContext({ dispatchDate: "", dispatchPeriod: "" });
    const publicationUnavailability = normalizeDriverPortalPublicationUnavailability(
      nextContext.publicationUnavailability,
    ) || remotePublicationUnavailability;
    const activeSelection = activeDriverPortalSelectionRef.current;
    if (
      activeSelection.dispatchDate !== selectedDispatchDate
      || activeSelection.dispatchPeriod !== selectedDispatchPeriod
      || activeSelection.previewDriverUserId !== selectedPreviewDriverUserId
    ) {
      return;
    }
    const selectedDateIsStillPublished = nextContext.dispatchDays.some(
      (dispatchDay) => (
        normalizeDriverPortalDispatchDate(dispatchDay?.dispatch_date) === selectedDispatchDate
        && normalizeDriverPortalDispatchPeriod(dispatchDay?.dispatch_period) === effectiveSelectedDispatchPeriod
      ),
    );
    if (!selectedDispatchDate || selectedDateIsStillPublished) {
      return;
    }

    const applyAdminPreviewUnavailableState = () => {
      setViewState((currentState) => (currentState.selectionKey === activeDriverPortalViewKey
        ? {
            errorMessage: publicationUnavailability
              ? ""
              : (nextContext.errorMessage || "Cette journée n'est plus publiée pour ce chauffeur."),
            payload: null,
            publicationUnavailability,
            selectionKey: activeDriverPortalViewKey,
            status: "error",
          }
        : currentState));
    };

    if (nextContext.status !== "ready" && !publicationUnavailability) {
      if (!isDriverPortalSession) {
        applyAdminPreviewUnavailableState();
      }
      return;
    }

    if (!isDriverPortalSession) {
      applyAdminPreviewUnavailableState();
      return;
    }

    navigate(buildDriverPortalHomePath({
      dispatchDate: "",
      dispatchPeriod: "",
      previewDriverUserId: selectedPreviewDriverUserId,
    }), {
      replace: true,
      state: publicationUnavailability
        ? null
        : { portalPublicationMessage: "Cette journée a été retirée du portail chauffeur." },
    });
  }, [
    activeDriverPortalViewKey,
    isDriverPortalSession,
    loadDispatchDayContext,
    navigate,
    selectedDispatchDate,
    effectiveSelectedDispatchPeriod,
    selectedPreviewDriverUserId,
  ]);

  const refreshPublishedPortalView = React.useCallback(async () => {
    const nextContext = await loadDispatchDayContext();
    if (nextContext.status !== "ready") {
      if (selectedDispatchDate) {
        await handlePublishedDispatchUnavailable(nextContext.publicationUnavailability);
      }
      return null;
    }
    const selectedDateIsStillPublished = !selectedDispatchDate || nextContext.dispatchDays.some(
      (dispatchDay) => (
        normalizeDriverPortalDispatchDate(dispatchDay?.dispatch_date) === selectedDispatchDate
        && normalizeDriverPortalDispatchPeriod(dispatchDay?.dispatch_period) === effectiveSelectedDispatchPeriod
      ),
    );
    if (!selectedDateIsStillPublished) {
      await handlePublishedDispatchUnavailable(nextContext.publicationUnavailability);
      return null;
    }
    return loadView();
  }, [
    handlePublishedDispatchUnavailable,
    loadDispatchDayContext,
    loadView,
    selectedDispatchDate,
    effectiveSelectedDispatchPeriod,
  ]);

  React.useEffect(() => {
    if (
      !isDriverPortalSession
      || !selectedDispatchDate
      || activeDispatchDayContextState.status !== "ready"
    ) {
      return;
    }
    const selectedPublicationExists = activeDispatchDayContextState.dispatchDays.some((dispatchDay) => (
      normalizeDriverPortalDispatchDate(dispatchDay?.dispatch_date) === selectedDispatchDate
      && normalizeDriverPortalDispatchPeriod(dispatchDay?.dispatch_period) === effectiveSelectedDispatchPeriod
    ));
    if (!selectedPublicationExists) {
      void handlePublishedDispatchUnavailable();
    }
  }, [
    activeDispatchDayContextState.dispatchDays,
    activeDispatchDayContextState.status,
    handlePublishedDispatchUnavailable,
    isDriverPortalSession,
    selectedDispatchDate,
    effectiveSelectedDispatchPeriod,
  ]);

  const refreshDriverPortalLandingContext = React.useCallback(
    () => loadDispatchDayContext({ dispatchDate: "", dispatchPeriod: "" }),
    [loadDispatchDayContext],
  );

  React.useEffect(() => {
    if (!shouldGateLandingOnDispatchDateSelection || typeof window === "undefined") {
      return undefined;
    }
    const probePublishedDispatchDays = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        void refreshDriverPortalLandingContext();
      }
    };
    const intervalId = window.setInterval(
      probePublishedDispatchDays,
      DRIVER_PORTAL_LIVE_SYNC_POLL_INTERVAL_MS,
    );
    document.addEventListener("visibilitychange", probePublishedDispatchDays);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", probePublishedDispatchDays);
    };
  }, [refreshDriverPortalLandingContext, shouldGateLandingOnDispatchDateSelection]);

  React.useEffect(() => {
    if (!shouldGateLandingOnDispatchDateSelection || selectedDispatchDate) {
      routeUpdatePendingLandingRef.current = false;
      return;
    }

    const publicationUnavailability = normalizeDriverPortalPublicationUnavailability(
      activeDispatchDayContextState.publicationUnavailability,
    );
    if (publicationUnavailability) {
      routeUpdatePendingLandingRef.current = true;
      return;
    }

    if (
      !routeUpdatePendingLandingRef.current
      || (activeDispatchDayContextState.status !== "ready" && activeDispatchDayContextState.status !== "refreshing")
      || activeDispatchDayContextState.dispatchDays.length === 0
    ) {
      return;
    }

    const nextSelection = resolveDriverPortalDefaultDispatchSelection({
      default_dispatch_date: activeDispatchDayContextState.defaultDispatchDate,
      default_dispatch_period: activeDispatchDayContextState.defaultDispatchPeriod,
      dispatch_days: activeDispatchDayContextState.dispatchDays,
    });
    if (!nextSelection.dispatchDate) {
      return;
    }

    routeUpdatePendingLandingRef.current = false;
    navigate(buildDriverPortalHomePath({
      dispatchDate: nextSelection.dispatchDate,
      dispatchPeriod: nextSelection.dispatchPeriod,
      previewDriverUserId: selectedPreviewDriverUserId,
    }), { replace: true, state: null });
  }, [
    activeDispatchDayContextState.defaultDispatchDate,
    activeDispatchDayContextState.defaultDispatchPeriod,
    activeDispatchDayContextState.dispatchDays,
    activeDispatchDayContextState.publicationUnavailability,
    activeDispatchDayContextState.status,
    navigate,
    selectedDispatchDate,
    selectedPreviewDriverUserId,
    shouldGateLandingOnDispatchDateSelection,
  ]);

  useDriverPortalLiveSync({
    currentPayload: viewState.selectionKey === activeDriverPortalViewKey ? viewState.payload : null,
    dispatchDate: selectedDispatchDate,
    dispatchPeriod: effectiveSelectedDispatchPeriod,
    enabled: isAccessReady,
    onRemoteChange: shouldGateLandingOnDispatchDateSelection
      ? refreshDriverPortalLandingContext
      : refreshPublishedPortalView,
    onUnavailable: handlePublishedDispatchUnavailable,
    previewDriverUserId: selectedPreviewDriverUserId,
    recoverWhenAvailable: Boolean(
      normalizeDriverPortalPublicationUnavailability(
        viewState.selectionKey === activeDriverPortalViewKey ? viewState.publicationUnavailability : null,
      ) || normalizeDriverPortalPublicationUnavailability(
        activeDispatchDayContextState.publicationUnavailability,
      ),
    ),
  });

  const refreshAfterDriverPortalPublicationConflict = React.useCallback(async (error) => {
    const status = Number(error?.status);
    if (status !== 404 && status !== 409) {
      return;
    }
    await refreshPublishedPortalView();
  }, [refreshPublishedPortalView]);

  React.useEffect(() => {
    if (!normalizedRouteDeliveryId) {
      return;
    }

    navigate(buildDriverPortalScreenPath("journey", {
      dispatchDate: selectedDispatchDate,
      dispatchPeriod: effectiveSelectedDispatchPeriod,
      focusDeliveryId: normalizedRouteDeliveryId,
      previewDriverUserId: selectedPreviewDriverUserId,
    }), { replace: true, state: null });
  }, [effectiveSelectedDispatchPeriod, navigate, normalizedRouteDeliveryId, selectedDispatchDate, selectedPreviewDriverUserId]);

  const activeViewState = viewState.selectionKey === activeDriverPortalViewKey
    ? viewState
    : {
        errorMessage: "",
        payload: null,
        publicationUnavailability: null,
        selectionKey: activeDriverPortalViewKey,
        status: "loading",
      };
  const activePublicationUnavailability = normalizeDriverPortalPublicationUnavailability(
    activeViewState.publicationUnavailability,
  ) || normalizeDriverPortalPublicationUnavailability(
    activeDispatchDayContextState.publicationUnavailability,
  );
  const payload = activeViewState.payload;
  const executionRevisions = React.useMemo(
    () => collectDriverPortalExecutionRevisions(payload),
    [payload],
  );
  const publicationRevisions = React.useMemo(() => {
    const source = payload?.publication_revisions && typeof payload.publication_revisions === "object"
      ? payload.publication_revisions
      : {};
    return Object.fromEntries(
      Object.entries(source)
        .map(([truckId, revision]) => [
          String(truckId || "").trim(),
          normalizeDriverPortalPublicationRevision(revision),
        ])
        .filter(([truckId, revision]) => Boolean(truckId) && revision !== null),
    );
  }, [payload?.publication_revisions]);
  const payloadPublicationRevision = normalizeDriverPortalPublicationRevision(payload?.publication_revision);
  const publicationCursorSignature = React.useMemo(
    () => buildDriverPortalPublicationCursorSignature(payloadPublicationRevision, publicationRevisions),
    [payloadPublicationRevision, publicationRevisions],
  );

  React.useEffect(() => {
    if (!publicationCursorSignature) {
      return;
    }

    const previousSignature = lastPublicationCursorSignatureRef.current;
    lastPublicationCursorSignatureRef.current = publicationCursorSignature;
    if (!shouldResetDriverPortalFocusAfterPublicationChange(previousSignature, publicationCursorSignature)) {
      return;
    }
    if (!requestedFocusDeliveryId && !requestedFocusGroupKey && !normalizedRouteDeliveryId) {
      return;
    }

    const nextScreen = selectedPortalScreen === "stops"
      ? "stops"
      : selectedPortalScreen === "journey" || normalizedRouteDeliveryId || requestedFocusDeliveryId || requestedFocusGroupKey
        ? "journey"
        : selectedPortalScreen;
    navigate(buildDriverPortalScreenPath(nextScreen, {
      dispatchDate: selectedDispatchDate,
      dispatchPeriod: effectiveSelectedDispatchPeriod,
      previewDriverUserId: selectedPreviewDriverUserId,
    }), { replace: true, state: null });
  }, [
    effectiveSelectedDispatchPeriod,
    navigate,
    normalizedRouteDeliveryId,
    publicationCursorSignature,
    requestedFocusDeliveryId,
    requestedFocusGroupKey,
    selectedDispatchDate,
    selectedPortalScreen,
    selectedPreviewDriverUserId,
  ]);

  const resolvePublicationRevision = React.useCallback((subject = null) => {
    const subjectRevision = normalizeDriverPortalPublicationRevision(subject?.publicationRevision ?? subject?.publication_revision);
    if (subjectRevision !== null) {
      return subjectRevision;
    }
    const subjectTruckId = typeof subject?.truckId === "string"
      ? subject.truckId.trim()
      : typeof subject?.truck_id === "string"
        ? subject.truck_id.trim()
        : "";
    return (subjectTruckId && publicationRevisions[subjectTruckId]) || payloadPublicationRevision;
  }, [payloadPublicationRevision, publicationRevisions]);
  const dispatchDays = React.useMemo(
    () => (Array.isArray(activeDispatchDayContextState.dispatchDays) ? activeDispatchDayContextState.dispatchDays.filter(Boolean) : []),
    [activeDispatchDayContextState.dispatchDays],
  );
  const fallbackDispatchDate = React.useMemo(
    () => normalizeDriverPortalDispatchDate(selectedDispatchDate)
      || normalizeDriverPortalDispatchDate(activeDispatchDayContextState.defaultDispatchDate),
    [activeDispatchDayContextState.defaultDispatchDate, selectedDispatchDate],
  );
  const fallbackDispatchPeriod = React.useMemo(
    () => normalizeDriverPortalDispatchPeriod(selectedDispatchPeriod, { allowEmpty: true })
      || normalizeDriverPortalDispatchPeriod(activeDispatchDayContextState.defaultDispatchPeriod, { allowEmpty: true })
      || "day",
    [activeDispatchDayContextState.defaultDispatchPeriod, selectedDispatchPeriod],
  );
  const homeModel = React.useMemo(() => buildDriverPortalHomeModel(payload), [payload]);
  const deliveries = Array.isArray(payload?.deliveries) ? payload.deliveries : [];
  const previewTargets = Array.isArray(payload?.preview_targets) ? payload.preview_targets : [];
  const selectedPreviewTarget = React.useMemo(
    () => previewTargets.find((target) => target?.driver_user_id === selectedPreviewDriverUserId) || null,
    [previewTargets, selectedPreviewDriverUserId],
  );
  const publishedTruckIds = React.useMemo(
    () => normalizeDriverPortalTruckIds(payload?.truck_ids),
    [payload?.truck_ids],
  );
  const dispatchDisabledTruckIds = React.useMemo(
    () => normalizeDriverPortalTruckIds(payload?.dispatch_disabled_truck_ids),
    [payload?.dispatch_disabled_truck_ids],
  );
  const dispatchDisabledTruckIdSet = React.useMemo(
    () => new Set(dispatchDisabledTruckIds),
    [dispatchDisabledTruckIds],
  );
  const areAllPublishedTrucksDispatchDisabled = publishedTruckIds.length > 0
    && publishedTruckIds.every((truckId) => dispatchDisabledTruckIdSet.has(truckId));
  const hasMixedDispatchDisabledScope = dispatchDisabledTruckIds.length > 0
    && !areAllPublishedTrucksDispatchDisabled;
  const dispatchDisabledTruckLabels = React.useMemo(() => {
    const targetTruckIds = Array.isArray(selectedPreviewTarget?.truck_ids) ? selectedPreviewTarget.truck_ids : [];
    const targetTruckLabels = Array.isArray(selectedPreviewTarget?.truck_labels) ? selectedPreviewTarget.truck_labels : [];
    const labelByTruckId = new Map();
    const addTruckLabel = (truckId, truckLabel) => {
      const normalizedTruckId = typeof truckId === "string" ? truckId.trim() : "";
      const normalizedTruckLabel = typeof truckLabel === "string" ? truckLabel.trim() : "";
      if (normalizedTruckId && normalizedTruckLabel) {
        labelByTruckId.set(normalizedTruckId, normalizedTruckLabel);
      }
    };

    targetTruckIds.forEach((truckId, index) => addTruckLabel(truckId, targetTruckLabels[index]));
    (Array.isArray(payload?.assignments) ? payload.assignments : []).forEach((assignment) => {
      addTruckLabel(assignment?.truck_id, assignment?.truck_name);
    });
    (Array.isArray(payload?.deliveries) ? payload.deliveries : []).forEach((delivery) => {
      addTruckLabel(delivery?.current_truck_id, delivery?.truck_name);
    });

    return dispatchDisabledTruckIds
      .map((truckId) => labelByTruckId.get(truckId) || truckId);
  }, [dispatchDisabledTruckIds, payload?.assignments, payload?.deliveries, selectedPreviewTarget]);
  const driverSessionDisplayName = React.useMemo(() => {
    const userMetadata = session?.user?.user_metadata;
    const fullName = typeof userMetadata?.full_name === "string" ? userMetadata.full_name.trim() : "";
    if (fullName) {
      return fullName;
    }

    const displayName = typeof userMetadata?.name === "string" ? userMetadata.name.trim() : "";
    if (displayName) {
      return displayName;
    }

    return "";
  }, [session]);
  const dispatchDateLabel = formatDriverPortalDispatchDateLabel(selectedDispatchDate || payload?.dispatch_date);
  const driverDisplayName = selectedPreviewTarget?.driver_name || homeModel.primaryDriverLabel || driverSessionDisplayName || "Camionneur";
  const heroStop = homeModel.currentStop || homeModel.upcomingStop || null;
  const heroStopState = resolveOperationalStepState(heroStop);
  const heroMapsUrl = React.useMemo(() => buildDriverPortalStepMapsUrl(heroStop), [heroStop]);
  const focusedDeliveryId = requestedFocusDeliveryId || normalizedRouteDeliveryId;
  const effectivePortalScreen = selectedPortalScreen || (focusedDeliveryId ? "journey" : "");
  const isLandingScreen = effectivePortalScreen === "";
  const isJourneyScreen = effectivePortalScreen === "journey";
  const isStopsScreen = effectivePortalScreen === "stops";
  const isOutOfServicePortalState = areAllPublishedTrucksDispatchDisabled;
  const outOfServiceTitle = dispatchDisabledTruckIds.length > 1 ? "Camions hors service" : "Camion hors service";
  const focusedJourneyGroup = React.useMemo(
    () => homeModel.stopGroups.find((group) => normalizeDriverPortalFocusGroupKey(group?.groupKey) === requestedFocusGroupKey)
      || findDriverStopGroupByDeliveryId(homeModel.stopGroups, focusedDeliveryId),
    [focusedDeliveryId, homeModel.stopGroups, requestedFocusGroupKey],
  );
  const journeyCurrentGroup = focusedJourneyGroup || homeModel.currentStopGroup || null;
  const journeyCurrentGroupIndex = focusedJourneyGroup ? focusedJourneyGroup.groupIndex : homeModel.currentStopGroupIndex;
  const journeyPreviousGroup = journeyCurrentGroupIndex > 0
    ? homeModel.stopGroups[journeyCurrentGroupIndex - 1] || null
    : null;
  const journeyNextGroup = journeyCurrentGroupIndex >= 0
    ? homeModel.stopGroups[journeyCurrentGroupIndex + 1] || null
    : homeModel.stopGroups[0] || null;
  const journeyRouteTripId = journeyCurrentGroup?.primaryStep?.trip_id
    || journeyCurrentGroup?.primaryStep?.linked_delivery?.trip_id
    || journeyCurrentGroup?.steps?.[0]?.linked_delivery?.trip_id
    || journeyCurrentGroup?.steps?.[0]?.trip_id
    || journeyNextGroup?.primaryStep?.linked_delivery?.trip_id
    || journeyNextGroup?.primaryStep?.trip_id
    || "";
  const journeyRouteTruckId = journeyCurrentGroup?.primaryStep?.linked_delivery?.truck_id
    || journeyCurrentGroup?.primaryStep?.linked_delivery?.current_truck_id
    || journeyCurrentGroup?.primaryStep?.truck_id
    || journeyCurrentGroup?.primaryStep?.current_truck_id
    || "";
  const journeyRouteContext = React.useMemo(() => resolveDriverPortalRouteContext(payload, journeyRouteTripId, journeyRouteTruckId), [journeyRouteTripId, journeyRouteTruckId, payload]);
  const journeyCurrentGroupAddress = React.useMemo(
    () => journeyCurrentGroup?.address || getOperationalStepAddress(journeyCurrentGroup?.primaryStep) || "",
    [journeyCurrentGroup?.address, journeyCurrentGroup?.primaryStep],
  );
  const journeyCurrentGroupIsInitialPreload = isInitialPreloadStep(journeyCurrentGroup?.primaryStep);
  const journeyRouteOriginAddress = React.useMemo(() => {
    if (journeyCurrentGroupIsInitialPreload) {
      return "";
    }

    const routeOrigin = resolveDriverPortalRouteOriginAddress(journeyRouteContext.assignment, journeyCurrentGroup?.primaryStep);
    const previousInTrip = journeyPreviousGroup?.primaryStep?.trip_id === journeyCurrentGroup?.primaryStep?.trip_id
      && journeyPreviousGroup?.primaryStep?.truck_id === journeyCurrentGroup?.primaryStep?.truck_id;
    return routeOrigin
      || (previousInTrip ? normalizeDriverPortalMapsAddress(journeyPreviousGroup?.address) : "")
      || normalizeDriverPortalMapsAddress(journeyRouteContext.assignment?.departure_address);
  }, [journeyCurrentGroup?.primaryStep, journeyCurrentGroupIsInitialPreload, journeyPreviousGroup, journeyRouteContext.assignment]);
  const journeyResolvedRouteDistanceKm = React.useMemo(() => {
    if (journeyCurrentGroupIsInitialPreload) {
      return null;
    }

    const resolvedKm = resolveDriverPortalRouteDistanceKm(
      journeyRouteContext.assignment,
      journeyCurrentGroup?.primaryStep,
    );
    if (resolvedKm != null && Number.isFinite(resolvedKm)) {
      return resolvedKm;
    }

    return journeyCurrentGroup?.routeDistanceKm != null && Number.isFinite(Number(journeyCurrentGroup.routeDistanceKm))
      ? Number(journeyCurrentGroup.routeDistanceKm)
      : null;
  }, [
    journeyCurrentGroup?.primaryStep,
    journeyCurrentGroup?.routeDistanceKm,
    journeyCurrentGroupIsInitialPreload,
    journeyRouteContext.assignment,
  ]);
  const journeyRoutePreviewDistanceLabel = React.useMemo(() => {
    if (journeyCurrentGroupIsInitialPreload) {
      return "";
    }

    if (journeyResolvedRouteDistanceKm != null && Number.isFinite(journeyResolvedRouteDistanceKm)) {
      return `${wholeNumberFormatter.format(Math.round(journeyResolvedRouteDistanceKm))} km`;
    }

    return typeof journeyCurrentGroup?.routeDistanceLabel === "string" ? journeyCurrentGroup.routeDistanceLabel.trim() : "";
  }, [journeyCurrentGroup?.routeDistanceLabel, journeyCurrentGroupIsInitialPreload, journeyResolvedRouteDistanceKm]);
  const journeyRoutePreviewTrip = React.useMemo(() => {
    const primaryStep = journeyCurrentGroup?.primaryStep;
    const assignment = journeyRouteContext.assignment;
    const resolved = resolveDriverPortalRouteSegmentIndex(assignment, primaryStep);
    if (resolved.mode === "origin-only") {
      return buildDriverPortalOriginOnlyTripFromAssignment(assignment, journeyRouteTripId);
    }

    return buildDriverPortalRouteTripFromAssignment(assignment, resolved.routeSegmentIndex, journeyRouteTripId);
  }, [journeyCurrentGroup?.primaryStep, journeyRouteContext.assignment, journeyRouteTripId]);
  const journeyRoutePreviewMapTrip = journeyRoutePreviewTrip || (
    journeyCurrentGroupIsInitialPreload ? null : journeyRouteContext.trip
  );
  const journeyDeliveryCommands = React.useMemo(
    () => (Array.isArray(journeyCurrentGroup?.commandRows) ? journeyCurrentGroup.commandRows.filter((command) => command.stepType === "delivery") : []),
    [journeyCurrentGroup],
  );
  const journeyLoadingActions = React.useMemo(
    () => (Array.isArray(journeyCurrentGroup?.loadingActions) ? journeyCurrentGroup.loadingActions : []),
    [journeyCurrentGroup],
  );
  const journeyCurrentGroupPendingLoadingActions = React.useMemo(
    () => (journeyCurrentGroup?.stepType === "loading"
      ? journeyLoadingActions.filter((action) => !action.isEffectivelyCompleted)
      : []),
    [journeyCurrentGroup?.stepType, journeyLoadingActions],
  );
  const journeyCurrentGroupPendingDeliveryCommands = React.useMemo(
    () => (journeyCurrentGroup?.stepType === "delivery"
      ? journeyDeliveryCommands.filter((command) => !command.isConfirmed)
      : []),
    [journeyCurrentGroup?.stepType, journeyDeliveryCommands],
  );
  const journeyCurrentGroupHasPendingLoadingActions = journeyCurrentGroupPendingLoadingActions.length > 0;
  const journeyCurrentGroupHasPendingDeliveryConfirmations = journeyCurrentGroupPendingDeliveryCommands.length > 0;
  const journeyCurrentGroupHasPendingConfirmations = journeyCurrentGroupHasPendingLoadingActions
    || journeyCurrentGroupHasPendingDeliveryConfirmations;
  const isJourneyCompleted = homeModel.stopGroups.length > 0 && homeModel.stopGroups.every((group) => {
    const departure = departureForGroup(payload?.departure_stops, group);
    return isDriverPortalVisitComplete(group) && (!departure || departure.departure || departure.locked);
  });
  const currentDeparture = departureForGroup(payload?.departure_stops, journeyCurrentGroup);
  const previousDeparture = departureForGroup(payload?.departure_stops, journeyPreviousGroup);
  const departureAction = useDriverDepartureAction({ scopeKey: `${activeDriverPortalViewKey}::${journeyCurrentGroup?.groupKey}`,
    simulationScopeKey: activeDriverPortalViewKey, simulationAvailable: payload?.departure_simulation_available === true,
    dispatchDate: selectedDispatchDate, dispatchPeriod: effectiveSelectedDispatchPeriod,
    previewDriverUserId: selectedPreviewDriverUserId, refresh: loadView });
  const departureLabel = currentDeparture?.departure || currentDeparture?.locked
    ? (journeyNextGroup ? "Continuer vers la destination suivante" : "Voir les résultats")
    : `Je quitte ${journeyCurrentGroup?.title || "cet arrêt"}`;
  const journeyCurrentGroupKey = normalizeDriverPortalFocusGroupKey(journeyCurrentGroup?.groupKey);
  const journeyPreviousGroupKey = normalizeDriverPortalFocusGroupKey(journeyPreviousGroup?.groupKey);
  const [journeyQuantityDrafts, setJourneyQuantityDrafts] = React.useState({});
  const journeyQuantityContextKey = `${activeDriverPortalViewKey}::${journeyCurrentGroupKey}`;
  const [journeyMapPreviewKey, setJourneyMapPreviewKey] = React.useState("");
  const consumeJourneyMapPreview = React.useCallback(() => setJourneyMapPreviewKey(""), []);
  const previousJourneyQuantityContextRef = React.useRef({ key: "", defaults: {} });
  const [journeyActionState, setJourneyActionState] = React.useState({
    busyKey: "",
    busyLabel: "",
    errorMessage: "",
  });
  const [journeyPendingQuantityConfirm, setJourneyPendingQuantityConfirm] = React.useState(null);
  const previousJourneyCurrentGroupKeyRef = React.useRef(journeyCurrentGroup?.groupKey);
  const dispatchDisabledTruckIdsKey = dispatchDisabledTruckIds.join("::");
  const previousDispatchDisabledTruckIdsKeyRef = React.useRef(dispatchDisabledTruckIdsKey);
  const isDriverPortalSubjectDispatchDisabled = React.useCallback((subject = null) => {
    const truckId = typeof subject?.truckId === "string"
      ? subject.truckId.trim()
      : typeof subject?.truck_id === "string"
        ? subject.truck_id.trim()
        : "";
    return Boolean(truckId && dispatchDisabledTruckIdSet.has(truckId));
  }, [dispatchDisabledTruckIdSet]);

  React.useEffect(() => {
    const pendingSubject = journeyPendingQuantityConfirm?.kind === "loading"
      ? journeyPendingQuantityConfirm.action
      : journeyPendingQuantityConfirm?.command;
    if (!pendingSubject || !isDriverPortalSubjectDispatchDisabled(pendingSubject)) {
      return;
    }

    setJourneyPendingQuantityConfirm(null);
    setJourneyActionState({
      busyKey: "",
      busyLabel: "",
      errorMessage: "",
    });
  }, [isDriverPortalSubjectDispatchDisabled, journeyPendingQuantityConfirm]);

  React.useEffect(() => {
    if (previousDispatchDisabledTruckIdsKeyRef.current === dispatchDisabledTruckIdsKey) {
      return;
    }
    previousDispatchDisabledTruckIdsKeyRef.current = dispatchDisabledTruckIdsKey;

    setResetConfirmationsState((currentState) => (currentState.isOpen
      ? { ...currentState, isOpen: false }
      : currentState));
  }, [dispatchDisabledTruckIdsKey]);

  React.useEffect(() => {
    if (normalizeDriverPortalDispatchDate(pendingDispatchDate)) {
      return;
    }

    if (!fallbackDispatchDate) {
      return;
    }

    setPendingDispatchDate(fallbackDispatchDate);
    setPendingDispatchPeriod(fallbackDispatchPeriod);
  }, [fallbackDispatchDate, fallbackDispatchPeriod, pendingDispatchDate]);

  const dispatchDateOptions = React.useMemo(
    () => buildDriverDispatchDateOptions(dispatchDays, todayDispatchDate),
    [dispatchDays, todayDispatchDate],
  );
  const activePendingPreviewDispatchDayContextState = pendingPreviewDispatchDayContextState.driverUserId === pendingPreviewDriverUserId
    ? pendingPreviewDispatchDayContextState
    : {
        defaultDispatchDate: "",
        defaultDispatchPeriod: "",
        dispatchDays: [],
        driverUserId: pendingPreviewDriverUserId,
        errorMessage: "",
        publicationUnavailability: null,
        status: pendingPreviewDriverUserId ? "loading" : "idle",
      };
  const pendingPreviewDispatchDateOptions = React.useMemo(
    () => buildDriverDispatchDateOptions(activePendingPreviewDispatchDayContextState.dispatchDays, todayDispatchDate),
    [activePendingPreviewDispatchDayContextState.dispatchDays, todayDispatchDate],
  );
  const shouldUseSelectedPreviewDispatchDayContext = isActiveAdminPreview
    && !selectedDispatchDate
    && pendingPreviewDriverUserId === selectedPreviewDriverUserId;
  const previewDispatchDateOptions = shouldUseSelectedPreviewDispatchDayContext
    ? dispatchDateOptions
    : pendingPreviewDispatchDateOptions;
  const previewDispatchDateStatus = shouldUseSelectedPreviewDispatchDayContext
    ? activeDispatchDayContextState.status
    : activePendingPreviewDispatchDayContextState.status;
  const previewDispatchDateErrorMessage = shouldUseSelectedPreviewDispatchDayContext
    ? activeDispatchDayContextState.errorMessage
    : activePendingPreviewDispatchDayContextState.errorMessage;
  const previewPublicationUnavailability = shouldUseSelectedPreviewDispatchDayContext
    ? activeDispatchDayContextState.publicationUnavailability
    : activePendingPreviewDispatchDayContextState.publicationUnavailability;

  const normalizedPendingDispatchDate = normalizeDriverPortalDispatchDate(pendingDispatchDate) || fallbackDispatchDate;
  const normalizedPendingDispatchPeriod = normalizeDriverPortalDispatchPeriod(pendingDispatchPeriod || fallbackDispatchPeriod);
  const pendingDispatchSelectionKey = buildDriverPortalDispatchSelectionKey(
    normalizedPendingDispatchDate,
    normalizedPendingDispatchPeriod,
  );
  const showPreviewSelector = Boolean(selectedPreviewDriverUserId)
    || Boolean(payload?.can_select_preview_target)
    || Boolean(payload?.requires_preview_target_selection);
  const showDispatchDateSelector = dispatchDateOptions.length > 0;
  const shouldShowCombinedAdminPreviewForm = showPreviewSelector
    && !isDriverPortalSession
    && !selectedDispatchDate;
  const canApplyPreviewDriver = Boolean(pendingPreviewDriverUserId)
    && (previewDispatchDateStatus === "ready" || previewDispatchDateStatus === "refreshing")
    && previewDispatchDateOptions.some((dispatchDateOption) => dispatchDateOption.value === pendingDispatchSelectionKey);
  const showDriverTools = showPreviewSelector || showDispatchDateSelector;
  const shouldShowAdminPreviewDispatchSelectionScreen = shouldGateLandingOnDispatchDateSelection && isActiveAdminPreview;
  const shouldShowDispatchDateEntryScreen = shouldGateLandingOnDispatchDateSelection
    && isDriverPortalSession
    && showDispatchDateSelector;
  const hasVisibleDispatchDayContext = activeDispatchDayContextState.status === "ready"
    || activeDispatchDayContextState.status === "refreshing";
  const shouldShowNoPublishedDispatchScreen = shouldGateLandingOnDispatchDateSelection
    && hasVisibleDispatchDayContext
    && !showDispatchDateSelector
    && !activePublicationUnavailability;
  const shouldShowRouteUpdatePendingScreen = shouldGateLandingOnDispatchDateSelection
    && !showDispatchDateSelector
    && Boolean(activePublicationUnavailability);
  const shouldShowDispatchContextErrorScreen = shouldGateLandingOnDispatchDateSelection
    && activeDispatchDayContextState.status === "error";
  const shouldShowDispatchContextLoadingScreen = shouldGateLandingOnDispatchDateSelection
    && activeDispatchDayContextState.status === "loading"
    && !showDispatchDateSelector;
  const currentGroupTheme = getDriverPortalStepTheme(journeyCurrentGroup?.stepType);
  const nextGroupTheme = getDriverPortalStepTheme(journeyNextGroup?.stepType);

  React.useEffect(() => {
    const defaults = buildDriverPortalQuantityDefaults(journeyDeliveryCommands, journeyLoadingActions);
    const previous = previousJourneyQuantityContextRef.current;
    const sameContext = previous.key === journeyQuantityContextKey;
    const completedKeys = [
      ...journeyDeliveryCommands.filter((command) => command.isConfirmed),
      ...journeyLoadingActions.filter((action) => action.isEffectivelyCompleted),
    ].map((operation) => operation.key);
    previousJourneyQuantityContextRef.current = { key: journeyQuantityContextKey, defaults };
    setJourneyQuantityDrafts((drafts) => reconcileDriverPortalQuantityDrafts(
      drafts, previous.defaults, defaults, completedKeys, sameContext,
    ));
    setJourneyPendingQuantityConfirm((pending) => (
      sameContext && isDriverPortalQuantityConfirmationCurrent(pending, journeyDeliveryCommands, journeyLoadingActions)
        ? pending : null
    ));
  }, [journeyQuantityContextKey, journeyDeliveryCommands, journeyLoadingActions]);

  React.useEffect(() => {
    if (previousJourneyCurrentGroupKeyRef.current === journeyCurrentGroup?.groupKey) {
      return;
    }

    previousJourneyCurrentGroupKeyRef.current = journeyCurrentGroup?.groupKey;
    setJourneyActionState({
      busyKey: "",
      busyLabel: "",
      errorMessage: "",
    });
  }, [journeyCurrentGroup?.groupKey]);

  const portalBusyMessage = React.useMemo(() => {
    if (portalNavigationBusyMessage) {
      return portalNavigationBusyMessage;
    }
    if (resetConfirmationsState.isPending) {
      return "Réinitialisation en cours…";
    }
    return "";
  }, [
    portalNavigationBusyMessage,
    resetConfirmationsState.isPending,
  ]);

  React.useEffect(() => {
    if (!portalNavigationBusyMessage) {
      return undefined;
    }

    const clearNavigationBusy = () => {
      setPortalNavigationBusyMessage("");
    };

    // Clear after the destination screen has had a chance to paint.
    const frameId = typeof window !== "undefined" && typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame(() => {
        window.requestAnimationFrame(clearNavigationBusy);
      })
      : null;
    const timeoutId = typeof window !== "undefined"
      ? window.setTimeout(clearNavigationBusy, 400)
      : null;

    return () => {
      if (frameId != null && typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(frameId);
      }
      if (timeoutId != null && typeof window !== "undefined") {
        window.clearTimeout(timeoutId);
      }
    };
  }, [currentPortalPath, effectivePortalScreen, portalNavigationBusyMessage]);

  const navigateWithPortalBusy = React.useCallback((message, navigateFn) => {
    const resolvedMessage = typeof message === "string" && message.trim()
      ? message.trim()
      : "Ouverture…";
    setPortalNavigationBusyMessage(resolvedMessage);
    navigateFn();
  }, []);

  const openJourneyScreen = React.useCallback(() => {
    navigateWithPortalBusy("Ouverture de la tournée…", () => {
      navigate(buildDriverPortalScreenPath("journey", {
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: effectiveSelectedDispatchPeriod,
        previewDriverUserId: selectedPreviewDriverUserId,
      }), { replace: true, state: null });
    });
  }, [effectiveSelectedDispatchPeriod, navigate, navigateWithPortalBusy, selectedDispatchDate, selectedPreviewDriverUserId]);

  const openRoutesScreen = React.useCallback(() => {
    navigateWithPortalBusy("Ouverture des itinéraires…", () => {
      navigate(buildDriverPortalScreenPath("stops", {
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: effectiveSelectedDispatchPeriod,
        previewDriverUserId: selectedPreviewDriverUserId,
      }), { replace: true, state: null });
    });
  }, [effectiveSelectedDispatchPeriod, navigate, navigateWithPortalBusy, selectedDispatchDate, selectedPreviewDriverUserId]);

  const openHomeScreen = React.useCallback(() => {
    navigateWithPortalBusy("Retour à l’accueil…", () => {
      navigate(buildDriverPortalHomePath({
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: effectiveSelectedDispatchPeriod,
        previewDriverUserId: selectedPreviewDriverUserId,
      }), { replace: true, state: null });
    });
  }, [effectiveSelectedDispatchPeriod, navigate, navigateWithPortalBusy, selectedDispatchDate, selectedPreviewDriverUserId]);

  const resetPendingDriverPreviewSelection = React.useCallback(() => {
    pendingPreviewDispatchDayContextRequestIdRef.current += 1;
    routeUpdatePendingPreviewRef.current = false;
    setPendingPreviewDriverUserId("");
    setPendingDispatchDate("");
    setPendingDispatchPeriod("");
    setPendingPreviewDispatchDayContextState({
      defaultDispatchDate: "",
      defaultDispatchPeriod: "",
      dispatchDays: [],
      driverUserId: "",
      errorMessage: "",
      publicationUnavailability: null,
      status: "idle",
    });
  }, []);

  const loadPendingPreviewDispatchDays = React.useCallback(async (driverUserId = "") => {
    const normalizedDriverUserId = normalizeDriverPortalPreviewDriverUserId(driverUserId);
    const requestId = pendingPreviewDispatchDayContextRequestIdRef.current + 1;
    pendingPreviewDispatchDayContextRequestIdRef.current = requestId;
    if (!normalizedDriverUserId) {
      resetPendingDriverPreviewSelection();
      return null;
    }

    setPendingDispatchDate("");
    setPendingDispatchPeriod("");
    setPendingPreviewDispatchDayContextState((currentState) => ({
      defaultDispatchDate: "",
      defaultDispatchPeriod: "",
      dispatchDays: [],
      driverUserId: normalizedDriverUserId,
      errorMessage: "",
      publicationUnavailability: currentState.driverUserId === normalizedDriverUserId
        ? currentState.publicationUnavailability
        : null,
      status: "loading",
    }));

    try {
      const payload = await loadAllDriverPublishedDispatchPeriods({
        dispatchDate: "",
        previewDriverUserId: normalizedDriverUserId,
      });
      if (pendingPreviewDispatchDayContextRequestIdRef.current !== requestId) {
        return null;
      }

      const defaultSelection = resolveDriverPortalDefaultDispatchSelection(payload);
      const nextState = {
        defaultDispatchDate: normalizeDriverPortalDispatchDate(payload?.default_dispatch_date),
        defaultDispatchPeriod: normalizeDriverPortalDispatchPeriod(payload?.default_dispatch_period, { allowEmpty: true }),
        dispatchDays: Array.isArray(payload?.dispatch_days) ? payload.dispatch_days.filter(Boolean) : [],
        driverUserId: normalizedDriverUserId,
        errorMessage: "",
        publicationUnavailability: normalizeDriverPortalPublicationUnavailability(payload?.publication_unavailability),
        status: "ready",
      };
      setPendingPreviewDispatchDayContextState(nextState);
      setPendingDispatchDate(defaultSelection.dispatchDate);
      setPendingDispatchPeriod(defaultSelection.dispatchPeriod);
      return nextState;
    } catch (error) {
      if (pendingPreviewDispatchDayContextRequestIdRef.current !== requestId) {
        return null;
      }

      const nextState = {
        defaultDispatchDate: "",
        defaultDispatchPeriod: "",
        dispatchDays: [],
        driverUserId: normalizedDriverUserId,
        errorMessage: error instanceof Error ? error.message : "Impossible de charger les journées publiées.",
        publicationUnavailability: normalizeDriverPortalPublicationUnavailability(error),
        status: "error",
      };
      setPendingPreviewDispatchDayContextState(nextState);
      return nextState;
    }
  }, [resetPendingDriverPreviewSelection]);

  React.useEffect(() => {
    const publicationUnavailability = normalizeDriverPortalPublicationUnavailability(
      activePendingPreviewDispatchDayContextState.publicationUnavailability,
    );
    if (
      !pendingPreviewDriverUserId
      || isDriverPortalSession
      || shouldUseSelectedPreviewDispatchDayContext
    ) {
      routeUpdatePendingPreviewRef.current = false;
      return;
    }

    if (publicationUnavailability) {
      routeUpdatePendingPreviewRef.current = true;
      return;
    }

    if (
      !routeUpdatePendingPreviewRef.current
      || activePendingPreviewDispatchDayContextState.status !== "ready"
      || activePendingPreviewDispatchDayContextState.dispatchDays.length === 0
    ) {
      return;
    }

    const nextSelection = resolveDriverPortalDefaultDispatchSelection({
      default_dispatch_date: activePendingPreviewDispatchDayContextState.defaultDispatchDate,
      default_dispatch_period: activePendingPreviewDispatchDayContextState.defaultDispatchPeriod,
      dispatch_days: activePendingPreviewDispatchDayContextState.dispatchDays,
    });
    if (!nextSelection.dispatchDate) {
      return;
    }

    routeUpdatePendingPreviewRef.current = false;
    navigate(buildDriverPortalHomePath({
      dispatchDate: nextSelection.dispatchDate,
      dispatchPeriod: nextSelection.dispatchPeriod,
      previewDriverUserId: pendingPreviewDriverUserId,
    }), { replace: true, state: null });
  }, [
    activePendingPreviewDispatchDayContextState.defaultDispatchDate,
    activePendingPreviewDispatchDayContextState.defaultDispatchPeriod,
    activePendingPreviewDispatchDayContextState.dispatchDays,
    activePendingPreviewDispatchDayContextState.publicationUnavailability,
    activePendingPreviewDispatchDayContextState.status,
    isDriverPortalSession,
    navigate,
    pendingPreviewDriverUserId,
    shouldUseSelectedPreviewDispatchDayContext,
  ]);

  React.useEffect(() => {
    if (
      !pendingPreviewDriverUserId
      || !normalizeDriverPortalPublicationUnavailability(
        activePendingPreviewDispatchDayContextState.publicationUnavailability,
      )
      || typeof window === "undefined"
    ) {
      return undefined;
    }

    const probePendingPreviewDispatchDays = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        void loadPendingPreviewDispatchDays(pendingPreviewDriverUserId);
      }
    };
    const intervalId = window.setInterval(
      probePendingPreviewDispatchDays,
      DRIVER_PORTAL_LIVE_SYNC_POLL_INTERVAL_MS,
    );
    document.addEventListener("visibilitychange", probePendingPreviewDispatchDays);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", probePendingPreviewDispatchDays);
    };
  }, [
    activePendingPreviewDispatchDayContextState.publicationUnavailability,
    loadPendingPreviewDispatchDays,
    pendingPreviewDriverUserId,
  ]);

  const returnToDriverPreviewSelector = React.useCallback(() => {
    resetPendingDriverPreviewSelection();
    portalNavigationHistoryRef.current = [];
    navigate(buildDriverPortalHomePath(), { replace: true, state: null });
  }, [navigate, resetPendingDriverPreviewSelection]);

  const handlePortalExit = React.useCallback(async () => {
    if (isActiveAdminPreview) {
      returnToDriverPreviewSelector();
      return;
    }

    await signOut();
  }, [isActiveAdminPreview, returnToDriverPreviewSelector, signOut]);

  const openPreviousScreen = React.useCallback(() => {
    if (isActiveAdminPreview && isLandingScreen) {
      returnToDriverPreviewSelector();
      return;
    }

    if (effectivePortalScreen === "journey") {
      if (journeyPreviousGroupKey) {
        navigateWithPortalBusy("Chargement de l’étape précédente…", () => {
          navigate(buildDriverPortalScreenPath("journey", {
            dispatchDate: selectedDispatchDate,
            dispatchPeriod: effectiveSelectedDispatchPeriod,
            focusGroupKey: journeyPreviousGroupKey,
            previewDriverUserId: selectedPreviewDriverUserId,
          }), { replace: true, state: null });
        });
        return;
      }

      navigateWithPortalBusy("Retour à l’accueil…", () => {
        navigate(buildDriverPortalHomePath({
          dispatchDate: selectedDispatchDate,
          dispatchPeriod: effectiveSelectedDispatchPeriod,
          previewDriverUserId: selectedPreviewDriverUserId,
        }), { replace: true, state: null });
      });
      return;
    }

    const portalNavigationHistory = portalNavigationHistoryRef.current;
    if (portalNavigationHistory.length > 1) {
      portalNavigationHistory.pop();
      const previousPortalPath = portalNavigationHistory[portalNavigationHistory.length - 1];
      if (previousPortalPath) {
        navigateWithPortalBusy("Retour…", () => {
          navigate(previousPortalPath, { replace: true, state: null });
        });
        return;
      }
    }

    navigateWithPortalBusy("Retour à l’accueil…", () => {
      navigate(buildDriverPortalHomePath({
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: effectiveSelectedDispatchPeriod,
        previewDriverUserId: selectedPreviewDriverUserId,
      }), { replace: true, state: null });
    });
  }, [effectivePortalScreen, effectiveSelectedDispatchPeriod, isActiveAdminPreview, isLandingScreen, journeyPreviousGroupKey, navigate, navigateWithPortalBusy, returnToDriverPreviewSelector, selectedDispatchDate, selectedPreviewDriverUserId]);

  const handlePendingDispatchDateChange = React.useCallback((event) => {
    const {
      dispatchDate: nextDispatchDate,
      dispatchPeriod: nextDispatchPeriod,
    } = parseDriverPortalDispatchSelectionKey(event.target.value);
    setPendingDispatchDate(nextDispatchDate);
    setPendingDispatchPeriod(nextDispatchPeriod);
  }, []);

  const handleDispatchDateChange = React.useCallback((event) => {
    const {
      dispatchDate: nextDispatchDate,
      dispatchPeriod: nextDispatchPeriod,
    } = parseDriverPortalDispatchSelectionKey(event.target.value);
    setPendingDispatchDate(nextDispatchDate);
    setPendingDispatchPeriod(nextDispatchPeriod);

    if (
      !nextDispatchDate
      || (nextDispatchDate === selectedDispatchDate && nextDispatchPeriod === normalizeDriverPortalDispatchPeriod(selectedDispatchPeriod))
    ) {
      return;
    }

    navigate(buildDriverPortalHomePath({
      dispatchDate: nextDispatchDate,
      dispatchPeriod: nextDispatchPeriod,
      previewDriverUserId: selectedPreviewDriverUserId,
    }), { replace: true, state: null });
  }, [navigate, selectedDispatchDate, selectedDispatchPeriod, selectedPreviewDriverUserId]);

  const handleDispatchDateContinue = React.useCallback(() => {
    const {
      dispatchDate: nextDispatchDate,
      dispatchPeriod: nextDispatchPeriod,
    } = parseDriverPortalDispatchSelectionKey(pendingDispatchSelectionKey);
    if (!nextDispatchDate) {
      return;
    }

    navigateWithPortalBusy("Ouverture de la journée…", () => {
      navigate(buildDriverPortalHomePath({
        dispatchDate: nextDispatchDate,
        dispatchPeriod: nextDispatchPeriod,
        previewDriverUserId: selectedPreviewDriverUserId,
      }), { replace: true, state: null });
    });
  }, [navigate, navigateWithPortalBusy, pendingDispatchSelectionKey, selectedPreviewDriverUserId]);

  function handleDispatchDateReset() {
    setPendingDispatchDate(fallbackDispatchDate);
    setPendingDispatchPeriod(fallbackDispatchPeriod);
    navigate(buildDriverPortalHomePath({
      dispatchDate: "",
      dispatchPeriod: "",
      previewDriverUserId: selectedPreviewDriverUserId,
    }), { replace: true, state: null });
  }

  const handlePreviewDriverChange = React.useCallback((event) => {
    const nextPreviewDriverUserId = normalizeDriverPortalPreviewDriverUserId(event.target.value);
    if (!nextPreviewDriverUserId) {
      if (isActiveAdminPreview) {
        returnToDriverPreviewSelector();
      } else {
        resetPendingDriverPreviewSelection();
      }
      return;
    }

    setPendingPreviewDriverUserId(nextPreviewDriverUserId);
    void loadPendingPreviewDispatchDays(nextPreviewDriverUserId);
  }, [isActiveAdminPreview, loadPendingPreviewDispatchDays, resetPendingDriverPreviewSelection, returnToDriverPreviewSelector]);

  function handlePreviewDriverApply(event) {
    event.preventDefault();
    const {
      dispatchDate: nextDispatchDate,
      dispatchPeriod: nextDispatchPeriod,
    } = parseDriverPortalDispatchSelectionKey(pendingDispatchSelectionKey);
    if (!canApplyPreviewDriver || !nextDispatchDate) {
      return;
    }

    navigate(buildDriverPortalHomePath({
      dispatchDate: nextDispatchDate,
      dispatchPeriod: nextDispatchPeriod,
      previewDriverUserId: pendingPreviewDriverUserId,
    }), { replace: true, state: null });
  }

  function handlePreviewDriverReset() {
    if (isActiveAdminPreview) {
      returnToDriverPreviewSelector();
      return;
    }

    resetPendingDriverPreviewSelection();
  }

  const handlePreviewDispatchDatesRetry = React.useCallback(() => {
    if (shouldUseSelectedPreviewDispatchDayContext) {
      void loadDispatchDayContext({ dispatchDate: "", dispatchPeriod: "" });
      return;
    }

    void loadPendingPreviewDispatchDays(pendingPreviewDriverUserId);
  }, [loadDispatchDayContext, loadPendingPreviewDispatchDays, pendingPreviewDriverUserId, shouldUseSelectedPreviewDispatchDayContext]);

  const driverToolsPanel = showDriverTools ? (
    <section className="driver-tools">
      <DriverSectionHeading
        eyebrow={showPreviewSelector ? "Pilotage" : "Date"}
        title={showPreviewSelector ? "Aperçu et date" : "Journée à afficher"}
        subtitle={showPreviewSelector
          ? "Choisissez d'abord le chauffeur à prévisualiser, puis une de ses journées publiées."
          : "Sélectionnez une de vos journées publiées pour consulter votre tournée."}
      />
      <div className="mt-4 grid gap-3">
        {showPreviewSelector ? (
          <DriverPreviewSelectionForm
            canApply={canApplyPreviewDriver}
            dispatchDate={pendingDispatchSelectionKey}
            dispatchDateErrorMessage={previewDispatchDateErrorMessage}
            dispatchDateOptions={previewDispatchDateOptions}
            dispatchDateStatus={previewDispatchDateStatus}
            onApply={handlePreviewDriverApply}
            onChange={handlePreviewDriverChange}
            onDispatchDateChange={handlePendingDispatchDateChange}
            onReset={handlePreviewDriverReset}
            onRetryDispatchDates={handlePreviewDispatchDatesRetry}
            publicationUnavailability={previewPublicationUnavailability}
            previewDriverUserId={pendingPreviewDriverUserId}
            previewTargets={previewTargets}
            showDispatchDate={shouldShowCombinedAdminPreviewForm}
          />
        ) : null}
        {showDispatchDateSelector && !shouldShowCombinedAdminPreviewForm ? (
          <DriverDispatchDateForm
            dispatchDate={pendingDispatchSelectionKey}
            dispatchDateOptions={dispatchDateOptions}
            hasSelectedDispatchDate={Boolean(selectedDispatchDate)}
            onChange={handleDispatchDateChange}
            onReset={handleDispatchDateReset}
          />
        ) : null}
      </div>
    </section>
  ) : null;

  function handleOpenOperationalStop(step, { previewMap = false } = {}) {
    const deliveryId = normalizeDriverPortalFocusDeliveryId(step?.linked_delivery_id || step?.delivery_id || step?.linked_delivery?.delivery_id);
    const visit = homeModel.stopGroups.find((group) => group.steps.some((candidate) => (
      candidate === step || (step?.step_id && candidate?.step_id === step.step_id)
    )));
    const focusGroupKey = normalizeDriverPortalFocusGroupKey(visit?.groupKey);
    if (!deliveryId && !focusGroupKey) {
      return;
    }

    setJourneyMapPreviewKey(previewMap ? `${activeDriverPortalViewKey}::${focusGroupKey}` : "");
    navigateWithPortalBusy("Ouverture de la destination…", () => {
      navigate(buildDriverPortalScreenPath("journey", {
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: effectiveSelectedDispatchPeriod,
        focusDeliveryId: deliveryId,
        focusGroupKey,
        previewDriverUserId: selectedPreviewDriverUserId,
      }), { state: null });
    });
  }

  function handleDepartureContinue() {
    if (departureAction.pending || journeyCurrentGroupHasPendingConfirmations) return;
    const proceed = () => journeyNextGroup
      ? handleOpenOperationalStop(journeyNextGroup.primaryStep, { previewMap: true }) : openRoutesScreen();
    const inherited = journeyCurrentGroup?.steps?.every((step) => step.is_initial_preload || step.is_preloaded_previous_day || step.is_end_of_day_load_source_preload);
    if (currentDeparture?.departure || currentDeparture?.locked || inherited) proceed();
    else departureAction.requestDeparture(currentDeparture, journeyCurrentGroup?.title, proceed);
  }

  function refocusJourneyScreen({
    deliveryId = "",
    focusGroupKey = "",
  } = {}) {
    const normalizedDeliveryId = normalizeDriverPortalFocusDeliveryId(deliveryId);
    const normalizedFocusGroupKey = normalizeDriverPortalFocusGroupKey(focusGroupKey);

    if (!normalizedDeliveryId && !normalizedFocusGroupKey) {
      return;
    }

    navigate(buildDriverPortalScreenPath("journey", {
      dispatchDate: selectedDispatchDate,
      dispatchPeriod: effectiveSelectedDispatchPeriod,
      focusDeliveryId: normalizedDeliveryId,
      focusGroupKey: normalizedFocusGroupKey,
      previewDriverUserId: selectedPreviewDriverUserId,
    }), { replace: true, state: null });
  }

  function handleJourneyQuantityChange(commandKey, value) {
    setJourneyQuantityDrafts((currentDrafts) => ({
      ...currentDrafts,
      [commandKey]: value,
    }));
  }

  function openResetConfirmationsModal() {
    if (areAllPublishedTrucksDispatchDisabled) {
      return;
    }

    setResetConfirmationsState((currentState) => ({
      ...currentState,
      isFinalOpen: false,
      isOpen: true,
      successMessage: "",
    }));
    setViewState((currentState) => ({
      ...currentState,
      errorMessage: "",
    }));
  }

  function closeResetConfirmationsModal() {
    setResetConfirmationsState((currentState) => ({
      ...currentState,
      isFinalOpen: false,
      isOpen: false,
    }));
  }

  function openResetConfirmationsFinalModal() {
    setResetConfirmationsState((currentState) => ({
      ...currentState,
      isFinalOpen: true,
      isOpen: false,
    }));
  }

  function closeResetConfirmationsFinalModal() {
    setResetConfirmationsState((currentState) => ({
      ...currentState,
      isFinalOpen: false,
    }));
  }

  async function handleResetDriverConfirmations() {
    if (resetConfirmationsState.isPending || areAllPublishedTrucksDispatchDisabled) {
      return;
    }

    setResetConfirmationsState((currentState) => ({
      ...currentState,
      isFinalOpen: false,
      isOpen: false,
      isPending: true,
      successMessage: "",
    }));
    setViewState((currentState) => ({
      ...currentState,
      errorMessage: "",
    }));

    try {
      const response = await resetDriverConfirmations({
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: effectiveSelectedDispatchPeriod,
        previewDriverUserId: selectedPreviewDriverUserId,
        publicationRevisions,
        deliveryExecutionRevisions: executionRevisions.deliveryExecutionRevisions,
        loadingExecutionRevisions: executionRevisions.loadingExecutionRevisions,
      });
      setJourneyQuantityDrafts({});
      setJourneyActionState({
        busyKey: "",
        busyLabel: "",
        errorMessage: "",
      });
      const refreshedPayload = await loadView();
      if (!refreshedPayload) {
        setResetConfirmationsState((currentState) => ({
          ...currentState,
          isPending: false,
        }));
        return;
      }

      setResetConfirmationsState((currentState) => ({
        ...currentState,
        isPending: false,
        successMessage: typeof response?.message === "string" && response.message.trim()
          ? response.message.trim()
          : "Validations réinitialisées.",
      }));
      invalidateDriverPortalMutationCaches(selectedDispatchDate, effectiveSelectedDispatchPeriod);
      notifyDispatchPlanningPortalMutation("reset-confirmations");
      return;
    } catch (error) {
      await refreshAfterDriverPortalPublicationConflict(error);
      setViewState((currentState) => ({
        ...currentState,
        errorMessage: error instanceof Error ? error.message : "Impossible de réinitialiser les validations visibles.",
      }));
    }

    setResetConfirmationsState((currentState) => ({
      ...currentState,
      isPending: false,
    }));
  }

  async function handleConfirmJourneyDelivery(command) {
    if (!command?.deliveryId || command.isConfirmed || command.isBlocked || journeyActionState.busyKey || isDriverPortalSubjectDispatchDisabled(command)) {
      return;
    }

    const parsedQuantity = parseQuantityInput(journeyQuantityDrafts[command.key]);
    if (parsedQuantity == null || Number.isNaN(parsedQuantity)) {
      setJourneyActionState({
        busyKey: "",
        busyLabel: "",
        errorMessage: "Le tonnage réel de cette commande doit être un nombre valide.",
      });
      return;
    }

    const plannedQuantity = command.quantityTons;
    const hasQuantityDiff = hasDriverPortalQuantityDifference(plannedQuantity, parsedQuantity);
    const loadedQuantity = resolveDriverPortalDeliveryLoadedQuantityTons(command);
    const exceedsLoaded = loadedQuantity != null && parsedQuantity > loadedQuantity
      && hasDriverPortalQuantityDifference(loadedQuantity, parsedQuantity);

    if (hasQuantityDiff || exceedsLoaded) {
      setJourneyPendingQuantityConfirm({
        command,
        parsedQuantity,
        kind: "delivery",
        stage: hasQuantityDiff ? "mismatch" : "exceed_loaded",
        exceedsLoaded,
        loadedQuantity,
      });
      return;
    }

    setJourneyActionState({
      busyKey: command.key,
      busyLabel: "Confirmation en cours…",
      errorMessage: "",
    });

    try {
      await confirmDriverDelivery(command.deliveryId, {
        actual_delivered_quantity_tons: parsedQuantity,
        publication_revision: resolvePublicationRevision(command),
        expected_execution_revision: command.executionRevision || null,
      }, {
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: effectiveSelectedDispatchPeriod,
        previewDriverUserId: selectedPreviewDriverUserId,
      });
      const refreshedPayload = await loadView();
      if (refreshedPayload) {
        refocusJourneyScreen({
          deliveryId: command.deliveryId,
          focusGroupKey: journeyCurrentGroupKey,
        });
      }
      invalidateDriverPortalMutationCaches(selectedDispatchDate, effectiveSelectedDispatchPeriod);
      notifyDispatchPlanningPortalMutation("confirm-delivery", { deliveryId: command.deliveryId });
    } catch (error) {
      await refreshAfterDriverPortalPublicationConflict(error);
      setJourneyActionState({
        busyKey: "",
        busyLabel: "",
        errorMessage: error instanceof Error ? error.message : "Impossible de confirmer cette commande.",
      });
      return;
    }

    setJourneyActionState({
      busyKey: "",
      busyLabel: "",
      errorMessage: "",
    });
  }

  async function handleProceedJourneyQuantityConfirm() {
    const pending = journeyPendingQuantityConfirm;
    const pendingSubject = pending?.kind === "loading" ? pending?.action : pending?.command;
    if (!pending || journeyActionState.busyKey || isDriverPortalSubjectDispatchDisabled(pendingSubject)
      || !isDriverPortalQuantityConfirmationCurrent(pending, journeyDeliveryCommands, journeyLoadingActions)) {
      setJourneyPendingQuantityConfirm(null);
      return;
    }

    if (pending.kind === "delivery" && pending.stage === "mismatch" && pending.exceedsLoaded) {
      setJourneyPendingQuantityConfirm({
        ...pending,
        stage: "exceed_loaded",
      });
      return;
    }

    const { parsedQuantity } = pending;
    const busyKey = pending.kind === "loading" ? pending.action?.key : pending.command?.key;
    setJourneyPendingQuantityConfirm(null);
    setJourneyActionState({
      busyKey,
      busyLabel: "Confirmation en cours…",
      errorMessage: "",
    });

    try {
      if (pending.kind === "loading") {
        await confirmDriverLoading(pending.action.loadingStepId, {
          actual_loaded_quantity_tons: parsedQuantity,
          publication_revision: resolvePublicationRevision(pending.action),
          expected_execution_revision: pending.action.executionRevision || null,
        }, {
          dispatchDate: selectedDispatchDate,
          dispatchPeriod: effectiveSelectedDispatchPeriod,
          previewDriverUserId: selectedPreviewDriverUserId,
        });
        const refreshedPayload = await loadView();
        if (refreshedPayload) {
          refocusJourneyScreen({
            deliveryId: pending.action.deliveryId,
            focusGroupKey: journeyCurrentGroupKey,
          });
        }
        invalidateDriverPortalMutationCaches(selectedDispatchDate, effectiveSelectedDispatchPeriod);
        notifyDispatchPlanningPortalMutation("confirm-loading", {
          deliveryId: pending.action.deliveryId,
          loadingStepId: pending.action.loadingStepId,
        });
      } else {
        const { command } = pending;
        await confirmDriverDelivery(command.deliveryId, {
          actual_delivered_quantity_tons: parsedQuantity,
          publication_revision: resolvePublicationRevision(command),
          expected_execution_revision: command.executionRevision || null,
        }, {
          dispatchDate: selectedDispatchDate,
          dispatchPeriod: effectiveSelectedDispatchPeriod,
          previewDriverUserId: selectedPreviewDriverUserId,
        });
        const refreshedPayload = await loadView();
        if (refreshedPayload) {
          refocusJourneyScreen({
            deliveryId: command.deliveryId,
            focusGroupKey: journeyCurrentGroupKey,
          });
        }
        invalidateDriverPortalMutationCaches(selectedDispatchDate, effectiveSelectedDispatchPeriod);
        notifyDispatchPlanningPortalMutation("confirm-delivery", { deliveryId: command.deliveryId });
      }
    } catch (error) {
      await refreshAfterDriverPortalPublicationConflict(error);
      setJourneyActionState({
        busyKey: "",
        busyLabel: "",
        errorMessage: error instanceof Error ? error.message : (
          pending.kind === "loading"
            ? "Impossible de confirmer ce chargement."
            : "Impossible de confirmer cette commande."
        ),
      });
      return;
    }

    setJourneyActionState({
      busyKey: "",
      busyLabel: "",
      errorMessage: "",
    });
  }

  function handleCancelJourneyQuantityConfirm() {
    setJourneyPendingQuantityConfirm(null);
  }

  async function handleUnconfirmJourneyDelivery(command) {
    if (!command?.deliveryId || !command.isConfirmed || journeyActionState.busyKey || isDriverPortalSubjectDispatchDisabled(command)) {
      return;
    }

    setJourneyActionState({
      busyKey: command.key,
      busyLabel: "Déconfirmation en cours…",
      errorMessage: "",
    });

    try {
      await unconfirmDriverDelivery(command.deliveryId, {
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: effectiveSelectedDispatchPeriod,
        previewDriverUserId: selectedPreviewDriverUserId,
        publicationRevision: resolvePublicationRevision(command),
        expectedExecutionRevision: command.executionRevision,
      });
      const refreshedPayload = await loadView();
      if (refreshedPayload) {
        refocusJourneyScreen({
          deliveryId: command.deliveryId,
          focusGroupKey: journeyCurrentGroupKey,
        });
      }
      invalidateDriverPortalMutationCaches(selectedDispatchDate, effectiveSelectedDispatchPeriod);
      notifyDispatchPlanningPortalMutation("unconfirm-delivery", { deliveryId: command.deliveryId });
    } catch (error) {
      await refreshAfterDriverPortalPublicationConflict(error);
      setJourneyActionState({
        busyKey: "",
        busyLabel: "",
        errorMessage: error instanceof Error ? error.message : "Impossible de déconfirmer cette commande.",
      });
      return;
    }

    setJourneyActionState({
      busyKey: "",
      busyLabel: "",
      errorMessage: "",
    });
  }

  async function handleConfirmJourneyLoading(action) {
    if (!action?.loadingStepId || action.isConfirmed || journeyActionState.busyKey || isDriverPortalSubjectDispatchDisabled(action)) {
      return;
    }

    const parsedQuantity = parseQuantityInput(journeyQuantityDrafts[action.key]);
    if (parsedQuantity == null || Number.isNaN(parsedQuantity)) {
      setJourneyActionState({
        busyKey: "",
        busyLabel: "",
        errorMessage: "Le tonnage réel de ce chargement doit être un nombre valide.",
      });
      return;
    }

    const plannedQuantity = action.quantityTons;
    const hasQuantityDiff = hasDriverPortalQuantityDifference(plannedQuantity, parsedQuantity);

    if (hasQuantityDiff) {
      setJourneyPendingQuantityConfirm({ action, parsedQuantity, kind: "loading" });
      return;
    }

    setJourneyActionState({
      busyKey: action.key,
      busyLabel: "Confirmation en cours…",
      errorMessage: "",
    });

    try {
      await confirmDriverLoading(action.loadingStepId, {
        actual_loaded_quantity_tons: parsedQuantity,
        publication_revision: resolvePublicationRevision(action),
        expected_execution_revision: action.executionRevision || null,
      }, {
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: effectiveSelectedDispatchPeriod,
        previewDriverUserId: selectedPreviewDriverUserId,
      });
      const refreshedPayload = await loadView();
      if (refreshedPayload) {
        refocusJourneyScreen({
          deliveryId: action.deliveryId,
          focusGroupKey: journeyCurrentGroupKey,
        });
      }
      invalidateDriverPortalMutationCaches(selectedDispatchDate, effectiveSelectedDispatchPeriod);
      notifyDispatchPlanningPortalMutation("confirm-loading", {
        deliveryId: action.deliveryId,
        loadingStepId: action.loadingStepId,
      });
    } catch (error) {
      await refreshAfterDriverPortalPublicationConflict(error);
      setJourneyActionState({
        busyKey: "",
        busyLabel: "",
        errorMessage: error instanceof Error ? error.message : "Impossible de confirmer ce chargement.",
      });
      return;
    }

    setJourneyActionState({
      busyKey: "",
      busyLabel: "",
      errorMessage: "",
    });
  }

  async function handleUnconfirmJourneyLoading(action) {
    if (!action?.loadingStepId || !action.isConfirmed || journeyActionState.busyKey || isDriverPortalSubjectDispatchDisabled(action)) {
      return;
    }

    setJourneyActionState({
      busyKey: action.key,
      busyLabel: "Déconfirmation en cours…",
      errorMessage: "",
    });

    try {
      await unconfirmDriverLoading(action.loadingStepId, {
        dispatchDate: selectedDispatchDate,
        dispatchPeriod: effectiveSelectedDispatchPeriod,
        previewDriverUserId: selectedPreviewDriverUserId,
        publicationRevision: resolvePublicationRevision(action),
        expectedExecutionRevision: action.executionRevision,
      });
      const refreshedPayload = await loadView();
      if (refreshedPayload) {
        refocusJourneyScreen({
          deliveryId: action.deliveryId,
          focusGroupKey: journeyCurrentGroupKey,
        });
      }
      invalidateDriverPortalMutationCaches(selectedDispatchDate, effectiveSelectedDispatchPeriod);
      notifyDispatchPlanningPortalMutation("unconfirm-loading", {
        deliveryId: action.deliveryId,
        loadingStepId: action.loadingStepId,
      });
    } catch (error) {
      await refreshAfterDriverPortalPublicationConflict(error);
      setJourneyActionState({
        busyKey: "",
        busyLabel: "",
        errorMessage: error instanceof Error ? error.message : "Impossible de déconfirmer ce chargement.",
      });
      return;
    }

    setJourneyActionState({
      busyKey: "",
      busyLabel: "",
      errorMessage: "",
    });
  }

  return (
    <DriverPortalShell
      busyMessage={portalBusyMessage}
      datePanel={(isLandingScreen || isJourneyScreen) && !isOutOfServicePortalState && !shouldGateLandingOnDispatchDateSelection ? driverToolsPanel : null}
      datePanelKey={location.key}
      dateAccessory={isJourneyScreen && !isOutOfServicePortalState && departureAction.simulationAvailable
        ? <DriverDepartureTestToggle action={departureAction} dispatchDate={selectedDispatchDate} /> : null}
      inlineHeaderActions={(isLandingScreen || isJourneyScreen) && !isOutOfServicePortalState}
      iconHeaderActions={isJourneyScreen && !isOutOfServicePortalState}
      eyebrow={driverDisplayName}
      title={isOutOfServicePortalState ? outOfServiceTitle : isStopsScreen ? "Itinéraires" : isLandingScreen || isJourneyScreen ? dispatchDateLabel || "Ma journée" : ""}
      subtitle={isOutOfServicePortalState ? "Aucune tournée n’est à effectuer." : isStopsScreen ? "Consultez les arrêts du jour dans l’ordre de passage." : ""}
      actions={(
        <div className="flex flex-row items-center justify-end gap-1">
          {!isOutOfServicePortalState ? (
            <DriverToolbarButton
              compact
              type="button"
              onClick={openPreviousScreen}
              aria-label="Précédent"
              title="Précédent"
            >
              <ArrowLeft size={12} aria-hidden="true" />
              <span className="driver-toolbar-label">Précédent</span>
            </DriverToolbarButton>
          ) : null}
          {!isOutOfServicePortalState && !isLandingScreen ? (
            <DriverToolbarButton
              compact
              type="button"
              onClick={openHomeScreen}
              aria-label="Accueil"
              title="Accueil"
            >
              <House size={12} aria-hidden="true" />
              <span className="driver-toolbar-label">Accueil</span>
            </DriverToolbarButton>
          ) : null}
          <DriverToolbarButton
            compact
            type="button"
            onClick={handlePortalExit}
            aria-label={isActiveAdminPreview ? "Quitter l'aperçu" : "Se déconnecter"}
            title={isActiveAdminPreview ? "Quitter l'aperçu" : "Se déconnecter"}
          >
            <LogOut size={12} aria-hidden="true" />
            <span className="driver-toolbar-label">Quitter</span>
          </DriverToolbarButton>
        </div>
      )}
    >
      {portalPublicationMessage ? (
        <section className="rounded-[24px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800 shadow-[0_14px_32px_rgba(217,119,6,0.08)]">
          {portalPublicationMessage}
        </section>
      ) : null}

      {shouldShowAdminPreviewDispatchSelectionScreen ? (
        driverToolsPanel
      ) : shouldShowDispatchContextLoadingScreen ? (
        <DriverStateCard
          title="Chargement des journées publiées"
          message="Nous vérifions les tournées actuellement disponibles dans votre portail."
        />
      ) : shouldShowRouteUpdatePendingScreen ? (
        <DriverPublicationUnavailabilityCard publicationUnavailability={activePublicationUnavailability} />
      ) : shouldShowDispatchContextErrorScreen ? (
        <DriverStateCard
          tone="error"
          title="Chargement impossible"
          message={activeDispatchDayContextState.errorMessage || "Impossible de charger les journées publiées."}
          action={(
            <DriverActionButton
              className="bg-slate-950 text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)] hover:bg-slate-800"
              type="button"
              onClick={() => {
                void loadDispatchDayContext({ dispatchDate: "", dispatchPeriod: "" });
              }}
            >
              Réessayer
            </DriverActionButton>
          )}
        />
      ) : shouldShowNoPublishedDispatchScreen ? (
        <DriverPortalScenicStateCard
          tone="empty"
          title="Aucune journée publiée"
          message="Aucune tournée n’est disponible pour votre compte en ce moment."
          guidance="Il pourra vérifier votre affectation et publier votre prochaine journée."
        />
      ) : shouldShowDispatchDateEntryScreen ? (
        <DriverPortalDispatchDateEntryScreen
          dispatchDate={pendingDispatchSelectionKey}
          dispatchDateOptions={dispatchDateOptions}
          driverDisplayName={driverDisplayName}
          onChange={handlePendingDispatchDateChange}
          onContinue={handleDispatchDateContinue}
        />
      ) : (
        <>
          {activeViewState.status === "loading" && !activePublicationUnavailability ? (
            <DriverStateCard
              title="Chargement de la journée"
              message="Nous préparons les arrêts opérationnels affectés à votre camion."
            />
          ) : null}

          {activePublicationUnavailability ? (
            <DriverPublicationUnavailabilityCard publicationUnavailability={activePublicationUnavailability} />
          ) : activeViewState.status === "error" ? (
            <DriverStateCard
              tone="error"
              title="Chargement impossible"
              message={activeViewState.errorMessage}
              action={(
                <div className="grid gap-3">
                  {driverToolsPanel}
                  <div className="grid gap-2">
                    <DriverActionButton
                      className="bg-slate-950 text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)] hover:bg-slate-800"
                      type="button"
                      onClick={() => {
                        void loadView();
                      }}
                    >
                      Réessayer
                    </DriverActionButton>
                    {selectedPreviewDriverUserId ? (
                      <DriverActionButton
                        className="border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:text-slate-950"
                        type="button"
                        onClick={handlePreviewDriverReset}
                      >
                        Quitter l&apos;aperçu
                      </DriverActionButton>
                    ) : null}
                  </div>
                </div>
              )}
            />
          ) : null}

          {!activePublicationUnavailability && (activeViewState.status === "ready" || activeViewState.status === "refreshing") ? (
            <div className="grid gap-4">
              {activeViewState.errorMessage ? (
                <section className="rounded-[24px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700 shadow-[0_14px_32px_rgba(244,63,94,0.08)]">
                  {activeViewState.errorMessage}
                </section>
              ) : null}

              {successMessage ? (
                <section className="rounded-[24px] border border-border-accent-subtle bg-accent-soft px-4 py-3 text-sm leading-6 text-accent shadow-[0_14px_32px_rgba(15,92,87,0.10)]">
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 h-9 w-9 rounded-2xl ${DRIVER_PORTAL_BRAND_PILL_CLASS_NAME}`}>
                      <CheckCircle2 size={18} aria-hidden="true" />
                    </span>
                    <div>
                      <p className="font-semibold">{successMessage}</p>
                      <p className="mt-1 text-accent/90">La vue portail reste alignée sur l'état courant du dispatch.</p>
                    </div>
                  </div>
                </section>
              ) : null}

              {hasMixedDispatchDisabledScope ? (
                <DriverDispatchDisabledNotice
                  truckCount={dispatchDisabledTruckIds.length}
                  truckLabels={dispatchDisabledTruckLabels}
                />
              ) : null}

              {areAllPublishedTrucksDispatchDisabled ? (
                <DriverTruckOutOfServiceCard
                  truckCount={dispatchDisabledTruckIds.length}
                  truckLabels={dispatchDisabledTruckLabels}
                />
              ) : payload?.requires_preview_target_selection ? (
                <>
                  {driverToolsPanel}

                  <section className="rounded-2xl border border-sky-200 bg-sky-50 px-5 py-5 shadow-[0_18px_40px_rgba(14,165,233,0.08)]">
                    <p className="text-lg font-semibold tracking-[-0.03em] text-slate-950">Aucun chauffeur sélectionné</p>
                    <p className="mt-2 text-sm leading-6 text-slate-700">
                      Choisissez un chauffeur lié pour voir son portail.
                    </p>
                  </section>
                </>
              ) : isLandingScreen ? (
                <DriverPortalLandingScreen
                  dispatchDateLabel={dispatchDateLabel}
                  driverDisplayName={driverDisplayName}
                  heroMapsUrl={heroMapsUrl}
                  heroStop={heroStop}
                  heroStopState={heroStopState}
                  homeModel={homeModel}
                  onContinue={openJourneyScreen}
                  onOpenRoutes={openRoutesScreen}
                  onOpenResetConfirmations={openResetConfirmationsModal}
                  resetButtonDisabled={areAllPublishedTrucksDispatchDisabled || resetConfirmationsState.isPending || activeViewState.status === "refreshing"}
                />
              ) : isStopsScreen ? (
            <section className="grid w-full min-w-0 max-w-full grid-cols-1 gap-3 overflow-x-clip">
                {homeModel.chronologicalItems.length === 0 ? (
                  <div className="rounded-[28px] border border-dashed border-[#d8e2d4] bg-[#faf7f1] px-5 py-6">
                    <p className="text-lg font-semibold tracking-[-0.03em] text-slate-950">Aucun arrêt à afficher</p>
                    <p className="mt-2 text-sm leading-6 text-slate-600">Aucun itinéraire n'est actuellement disponible pour cette journée.</p>
                  </div>
                ) : (
                  homeModel.chronologicalItems.map((item) => {
                    if (item.type === "incident") {
                      return <DriverTruckIncidentCard key={item.key} incident={item.incident} />;
                    }
                    const group = item.group;
                    return (
                      <DriverOperationalStopCard
                        key={item.key}
                        isCurrent={group?.groupKey === homeModel.currentStopGroup?.groupKey}
                        occurrenceCount={group?.occurrenceCount || group?.steps?.length || 1}
                        displayStopNumber={item.stopNumber}
                        onOpen={group?.primaryStep?.linked_delivery_id ? () => handleOpenOperationalStop(group.primaryStep) : null}
                        sequenceSummaryLabel={group?.sequenceSummaryLabel || ""}
                        group={group}
                        mapsUrl={buildDriverPortalStepMapsUrl(group.primaryStep, {
                          originAddressOverride: resolveDriverPortalRouteOriginAddress(
                            resolveDriverPortalRouteContext(payload, group.primaryStep?.trip_id, group.primaryStep?.truck_id).assignment,
                            group.primaryStep,
                          ),
                        })}
                        step={group?.primaryStep}
                      />
                    );
                  })
                )}
            </section>
          ) : (
            <div className="grid w-full min-w-0 max-w-full grid-cols-1 gap-4 overflow-x-clip">
              <DriverDepartureSimulation action={departureAction} dispatchDate={selectedDispatchDate} />
              {journeyActionState.errorMessage ? (
                <section className="rounded-[24px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700 shadow-[0_14px_32px_rgba(244,63,94,0.08)]">
                  {journeyActionState.errorMessage}
                </section>
              ) : null}

              {journeyCurrentGroup ? (
                <section className={`driver-current-stop relative box-border w-full min-w-0 max-w-full overflow-hidden border ${currentGroupTheme.panelClassName}`}>
                  <DriverPortalStepVisual
                    stepType={journeyCurrentGroup.stepType}
                    stopNumber={journeyCurrentGroupIndex + 1}
                    stopCount={homeModel.stopGroups.length}
                  >
                    <div className="driver-step-status">
                      {renderDriverPortalProductBadge(journeyCurrentGroup.productLabel, journeyCurrentGroup.stepType)}
                      <span className={`shrink-0 rounded-full border px-3 py-1 text-[0.72rem] font-black uppercase tracking-[0.12em] ${journeyCurrentGroup.state.badgeClassName}`}>
                        {journeyCurrentGroup.state.title}
                      </span>
                    </div>
                  </DriverPortalStepVisual>
                  {journeyCurrentGroupIsInitialPreload ? <DriverPortalPreloadBanner /> : null}
                  <DriverRoutePreviewPanel
                    key={journeyQuantityContextKey}
                    autoPreview={journeyMapPreviewKey === journeyQuantityContextKey}
                    onPreviewConsumed={consumeJourneyMapPreview}
                    assignment={journeyCurrentGroupIsInitialPreload ? null : journeyRouteContext.assignment}
                    className="mt-2"
                    heading={journeyCurrentGroup.title || "Destination à traiter"}
                    address={journeyCurrentGroup.address || getOperationalStepAddress(journeyCurrentGroup.primaryStep)}
                    addressLabel={formatStreetCityAddress(journeyCurrentGroup.address || getOperationalStepAddress(journeyCurrentGroup.primaryStep))}
                    mapsUrl={buildDriverPortalStepMapsUrl(journeyCurrentGroup.primaryStep, { originAddressOverride: journeyRouteOriginAddress })}
                    routeDistanceLabel={journeyRoutePreviewDistanceLabel}
                    trip={journeyRoutePreviewMapTrip}
                  />

                  <DriverPortalStopMetricsGrid
                    group={journeyCurrentGroup}
                    hideRouteDistance={journeyCurrentGroupIsInitialPreload}
                    routeDistanceKmOverride={journeyResolvedRouteDistanceKm}
                    totalCompartments={homeModel.totalCompartments}
                  />

                  {journeyDeliveryCommands.length > 0 ? (
                    <DriverPortalOperationsPager
                      key={`${journeyQuantityContextKey}::delivery`}
                      operations={journeyDeliveryCommands}
                      kind="delivery"
                      focusedDeliveryId={requestedFocusGroupKey ? "" : focusedDeliveryId}
                      disabled={Boolean(journeyActionState.busyKey || journeyPendingQuantityConfirm) || activeViewState.status === "refreshing"}
                    >
                      {(command, index) => {
                        const isGroupActionPending = Boolean(journeyActionState.busyKey);
                        const isActionPending = journeyActionState.busyKey === command.key;
                        const isInteractionDisabled = isActionPending || isGroupActionPending || activeViewState.status === "refreshing";
                        const commandTheme = getDriverPortalStepTheme(command.stepType);
                        const commandSignalItems = buildDriverPortalCommandSignalItems(command);
                        const hasSignalHighlights = commandSignalItems.length > 0;
                        const isDeliveryScreen = journeyCurrentGroup.stepType === "delivery";
                        const deliveryActionState = resolveDriverPortalDeliveryActionState(command);

                        return (
                          <div key={command.key} className={`driver-command w-full min-w-0 max-w-full overflow-hidden border ${hasSignalHighlights ? commandTheme.panelClassName : "border-slate-200 bg-white"}`}>
                            <div className="flex items-start justify-end gap-3 overflow-hidden">
                              {!isDeliveryScreen ? (
                                <h3 className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden text-[1rem] font-black leading-none tracking-[-0.03em] text-slate-950">
                                  <span className="truncate">Commande {index + 1}</span>
                                </h3>
                              ) : null}
                              <div className="flex shrink-0 items-center gap-1.5 self-start">
                                <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[0.66rem] font-black uppercase tracking-[0.12em] ${deliveryActionState.status === "confirmed" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : command.isBlocked || command.isRevalidation ? "border-amber-200 bg-amber-50 text-amber-700" : "border-[#efcf77] bg-[#fff6df] text-[#9a6700]"}`}>
                                  {deliveryActionState.status === "confirmed" ? "Confirmée" : command.isBlocked ? "Bloquée" : command.isRevalidation ? "À revalider" : "À confirmer"}
                                </span>
                              </div>
                            </div>
                            <div className="driver-command-heading">
                              {isDeliveryScreen ? (
                                <DriverAddressLine
                                  className="text-slate-600"
                                  clientName={command.loadingClientName || command.clientName || ""}
                                  clientNameClassName={commandTheme.clientClassName}
                                  prefixLabel="Chargement"
                                  value=""
                                />
                              ) : (
                                <DriverAddressLine
                                  className="text-slate-600"
                                  clientName={command.clientName}
                                  clientNameClassName={commandTheme.clientClassName}
                                  mapsUrl={buildDriverPortalStepMapsUrl(journeyCurrentGroup.primaryStep, { originAddressOverride: journeyPreviousGroup?.address || getOperationalStepAddress(journeyPreviousGroup?.primaryStep) })}
                                  prefixLabel="Client"
                                  singleLine
                                  value={command.address || journeyCurrentGroup.address || ""}
                                />
                              )}
                              {renderDriverPortalPlannedQuantityRow({
                                ...resolveDriverPortalDeliveryPlannedDisplayQuantity(command),
                                className: "driver-command-planned",
                              })}
                            </div>

                            {renderDriverPortalSignalBadges(command)}

                            <div className="driver-command-details">
                              <div className="driver-command-product-row">
                                <p className="driver-product-code">
                                  <span className="font-semibold text-slate-700">Code produit :</span> {command.grainCode || journeyCurrentGroup.primaryStep?.grain_code || "--"}
                                </p>
                                {command.silo ? <p className="driver-command-silo"><span className="font-semibold text-slate-700">Silo :</span> {command.silo}</p> : null}
                              </div>
                              {command.building ? <p className="driver-command-building"><span className="font-semibold text-slate-700">Bâtiment :</span> <span>{command.building}</span></p> : null}
                              {renderDriverPortalOrderReferenceRow({
                                workOrderNumber: command.workOrderNumber,
                                orderId: command.orderId,
                              })}
                              {renderCompartmentAllocations(command.compartmentAllocations) ? (
                                <div className="driver-compartments">
                                  <div className="driver-compartments-heading">Compartiments :</div>
                                  <dl className="driver-compartments-grid" aria-label="Compartiments à livrer et quantités prévues">
                                    {renderCompartmentAllocations(command.compartmentAllocations, { isDelivery: command.stepType === "delivery" })}
                                  </dl>
                                </div>
                              ) : null}
                              {command.comment ? <p className="min-w-0"><span className="font-semibold text-slate-700">Commentaires :</span> <span>{command.comment}</span></p> : null}
                            </div>

                            <div className="driver-quantity-form grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
                              <label className="grid gap-1.5">
                                <span className="text-[0.68rem] font-black uppercase tracking-[0.16em] text-slate-500">Tonnage réel</span>
                                <input
                                  className="min-h-10 rounded-lg border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-900 outline-hidden transition duration-200 placeholder:text-slate-400 focus:border-accent disabled:cursor-not-allowed disabled:bg-slate-100"
                                  disabled={isInteractionDisabled || isDriverPortalSubjectDispatchDisabled(command) || command.isConfirmed || command.isBlocked}
                                  inputMode="decimal"
                                  type="text"
                                  value={journeyQuantityDrafts[command.key] || ""}
                                  onChange={(event) => handleJourneyQuantityChange(command.key, event.target.value)}
                                />
                              </label>
                              <div className="grid justify-items-stretch gap-1">
                                {doesDriverPortalDeliveryExceedLoadedQuantity(command) ? (
                                  <p className="driver-quantity-notice text-rose-700" title="Quantité livrée supérieure à la quantité chargée">
                                    Qté chargée dépassée
                                  </p>
                                ) : null}
                                <DriverActionButton
                                  compact
                                  aria-busy={isActionPending || undefined}
                                  className={deliveryActionState.bottomButtonClassName}
                                  disabled={isInteractionDisabled || isDriverPortalSubjectDispatchDisabled(command) || (command.isBlocked && !command.isConfirmed)}
                                  type="button"
                                  title={deliveryActionState.buttonTitle}
                                  onClick={() => {
                                    void (command.isConfirmed ? handleUnconfirmJourneyDelivery(command) : handleConfirmJourneyDelivery(command));
                                  }}
                                >
                                  {isActionPending ? <LoaderCircle className="animate-spin" size={14} aria-hidden="true" /> : null}
                                  {deliveryActionState.buttonLabel}
                                </DriverActionButton>
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    </DriverPortalOperationsPager>
                  ) : null}

                  {journeyLoadingActions.length > 0 ? (
                    <DriverPortalOperationsPager
                      key={`${journeyQuantityContextKey}::loading`}
                      operations={journeyLoadingActions}
                      kind="loading"
                      focusedDeliveryId={requestedFocusGroupKey ? "" : focusedDeliveryId}
                      disabled={Boolean(journeyActionState.busyKey || journeyPendingQuantityConfirm) || activeViewState.status === "refreshing"}
                    >
                      {(action) => {
                        const isActionPending = journeyActionState.busyKey === action.key;
                        const isInteractionDisabled = Boolean(journeyActionState.busyKey) || activeViewState.status === "refreshing";
                        const actionTheme = getDriverPortalStepTheme("loading");
                        const hasSignalHighlights = buildDriverPortalCommandSignalItems(action).length > 0;
                        const loadingActionState = action.loadingActionState;

                        return (
                          <div key={action.key} className={`driver-command box-border w-full min-w-0 max-w-full overflow-hidden border ${hasSignalHighlights ? actionTheme.panelClassName : "border-slate-200 bg-white"}`}>
                              {loadingActionState.status === "settled" && !loadingActionState.isExplicitlyConfirmed ? (
                                <div className="flex justify-end">
                                  <DriverActionButton
                                    compact
                                    aria-busy={isActionPending || undefined}
                                    className={loadingActionState.buttonClassName}
                                    disabled
                                    type="button"
                                    title={loadingActionState.buttonTitle}
                                  >
                                    {loadingActionState.buttonLabel}
                                  </DriverActionButton>
                                </div>
                              ) : null}
                            <DriverPortalClientAddress
                              clientName={action.clientName}
                              address={action.address || journeyCurrentGroup.address || ""}
                              quantity={renderDriverPortalPlannedQuantityRow({
                                showAdjustedQuantity: action.isConfirmed,
                                plannedQuantityTons: action.quantityTons,
                                actualQuantityTons: action.actualLoadedQuantityTons,
                                className: "driver-command-planned",
                              })}
                            />

                            {renderDriverPortalSignalBadges(action)}

                            <div className="driver-command-details">
                              <div className="driver-command-product-row">
                                <p className="driver-product-code text-slate-600" title={action.grainCode || undefined}>
                                  <span className="font-semibold text-slate-700">Code produit :</span> {action.grainCode || "--"}
                                </p>
                                {action.silo ? <p className="driver-command-silo"><span className="font-semibold text-slate-700">Silo :</span> {action.silo}</p> : null}
                              </div>
                              {action.building ? <p className="driver-command-building"><span className="font-semibold text-slate-700">Bâtiment :</span> <span>{action.building}</span></p> : null}
                              {renderDriverPortalOrderReferenceRow({
                                workOrderNumber: action.workOrderNumber,
                                orderId: action.orderId,
                              })}
                              {renderCompartmentAllocations(action.compartmentAllocations) ? (
                                <div className="driver-compartments">
                                  <div className="driver-compartments-heading">Compartiments :</div>
                                  <dl className="driver-compartments-grid" aria-label="Compartiments à charger et quantités prévues">
                                    {renderCompartmentAllocations(action.compartmentAllocations)}
                                  </dl>
                                </div>
                              ) : null}
                              {action.comment ? <p className="min-w-0"><span className="font-semibold text-slate-700">Commentaires :</span> <span>{action.comment}</span></p> : null}
                            </div>

                            {loadingActionState.status !== "settled" || loadingActionState.isExplicitlyConfirmed ? (
                              <div className="driver-quantity-form grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
                                <label className="grid gap-1.5">
                                  <span className="text-[0.68rem] font-black uppercase tracking-[0.16em] text-slate-500">Tonnage réel</span>
                                  <input
                                    className="min-h-10 rounded-lg border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-900 outline-hidden transition duration-200 placeholder:text-slate-400 focus:border-accent disabled:cursor-not-allowed disabled:bg-slate-100"
                                    disabled={isInteractionDisabled || isDriverPortalSubjectDispatchDisabled(action) || action.isConfirmed || loadingActionState.status === "settled"}
                                    inputMode="decimal"
                                    type="text"
                                    value={journeyQuantityDrafts[action.key] || ""}
                                    onChange={(event) => handleJourneyQuantityChange(action.key, event.target.value)}
                                  />
                                </label>
                                <DriverActionButton
                                  compact
                                  aria-busy={isActionPending || undefined}
                                  className={loadingActionState.bottomButtonClassName}
                                  disabled={isInteractionDisabled || isDriverPortalSubjectDispatchDisabled(action) || (loadingActionState.status === "settled" && !loadingActionState.isExplicitlyConfirmed)}
                                  type="button"
                                  title={loadingActionState.buttonTitle}
                                  onClick={() => {
                                    void (loadingActionState.isExplicitlyConfirmed ? handleUnconfirmJourneyLoading(action) : handleConfirmJourneyLoading(action));
                                  }}
                                >
                                  {isActionPending ? <LoaderCircle className="animate-spin" size={14} aria-hidden="true" /> : null}
                                  {loadingActionState.buttonLabel}
                                </DriverActionButton>
                              </div>
                            ) : null}
                          </div>
                        );
                      }}
                    </DriverPortalOperationsPager>
                  ) : null}
                </section>
              ) : (
                <section className="rounded-4xl border border-dashed border-[#d8e2d4] bg-[#faf7f1] px-4 py-5 shadow-[0_18px_42px_rgba(17,24,39,0.05)]">
                  <p className="text-lg font-semibold tracking-[-0.03em] text-slate-950">Aucune destination courante</p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">La tournée du jour n'a pas encore d'arrêt actif à afficher.</p>
                </section>
              )}

              <DriverPortalDepartureStatus stop={currentDeparture} pending={departureAction.pending}
                onCancel={() => departureAction.change("cancel", currentDeparture)} />
              {!currentDeparture?.departure ? <DriverPortalDepartureStatus stop={previousDeparture}
                label={journeyPreviousGroup?.title} pending={departureAction.pending}
                onCancel={() => departureAction.change("cancel", previousDeparture, () => handleOpenOperationalStop(journeyPreviousGroup.primaryStep))} /> : null}
              {departureAction.error && !departureAction.simulation ? <p role="alert" className="rounded-[4px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{departureAction.error}</p> : null}

              <DriverPortalNextStop
                key={`${journeyQuantityContextKey}::${journeyNextGroup?.groupKey || "last"}`}
                label={journeyNextGroup ? "Prochaine destination" : "Fin de tournée"}
                title={journeyNextGroup?.title || (journeyNextGroup ? "Étape suivante" : isJourneyCompleted ? "Tournée terminée" : "Dernier arrêt")}
                kind={journeyNextGroup ? (journeyNextGroup.stepType === "loading" ? "Chargement" : "Livraison") : ""}
                locked={journeyCurrentGroupHasPendingConfirmations}
                ready={!journeyCurrentGroupHasPendingConfirmations && Boolean(journeyCurrentGroup)}
                disabled={departureAction.pending || Boolean(journeyActionState.busyKey)}
                readyLabel={departureAction.pending ? "Enregistrement du départ…" : departureLabel}
                onContinue={handleDepartureContinue}
              >
                {(closeDetails) => journeyNextGroup ? (
                  <div className={`driver-next-preview ${nextGroupTheme.panelClassName}`}>
                    <div className="driver-next-heading">
                      <p className="driver-next-kind">
                        {journeyNextGroup.stepType === "loading" ? "Chargement" : "Livraison"}
                      </p>
                      <div className="driver-next-status">
                        {renderDriverPortalProductBadge(journeyNextGroup.productLabel, journeyNextGroup.stepType)}
                        <span className={`shrink-0 rounded-full border px-3 py-1 text-[0.72rem] font-black uppercase tracking-[0.12em] ${journeyNextGroup.state.badgeClassName}`}>
                          {journeyNextGroup.state.title}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold tracking-[-0.03em] text-slate-950">{journeyNextGroup.title || "Étape suivante"}</h3>
                    </div>

                    <DriverAddressLine
                      className="mt-2 text-sm leading-6 text-slate-600"
                      mapsUrl={buildDriverPortalStepMapsUrl(journeyNextGroup.primaryStep, {
                        originAddressOverride: resolveDriverPortalRouteOriginAddress(
                          resolveDriverPortalRouteContext(payload, journeyNextGroup.primaryStep?.trip_id, journeyNextGroup.primaryStep?.truck_id).assignment,
                          journeyNextGroup.primaryStep,
                        ) || (journeyNextGroup.primaryStep?.trip_id === journeyCurrentGroup?.primaryStep?.trip_id ? normalizeDriverPortalMapsAddress(journeyCurrentGroup?.address) : ""),
                      })}
                      singleLine
                      value={journeyNextGroup.address || getOperationalStepAddress(journeyNextGroup.primaryStep)}
                    />

                    {renderDriverPortalSignalBadges(journeyNextGroup.commandRows?.[0] || journeyNextGroup.primaryStep || null)}

                    <DriverPortalStopMetricsGrid
                      group={journeyNextGroup}
                      totalCompartments={homeModel.totalCompartments}
                    />

                    {journeyCurrentGroupHasPendingConfirmations ? (
                      <div className="driver-locked-note">
                        <p className="driver-locked-title"><LockKeyhole size={14} aria-hidden="true" />Destination suivante verrouillée</p>
                        <p className="truncate text-[0.74rem] leading-5">
                          {journeyCurrentGroupHasPendingLoadingActions
                            ? "Confirmez les chargements de cet arrêt avant de continuer."
                            : "Confirmez le tonnage courant avant de continuer."}
                        </p>
                      </div>
                    ) : journeyNextGroup.primaryStep?.linked_delivery_id ? (
                      <DriverActionButton
                        compact
                        className={`mt-4 min-h-10 w-full px-4 text-[0.95rem] font-semibold ${DRIVER_PORTAL_BRAND_ACTION_CLASS_NAME}`}
                        type="button"
                        disabled={departureAction.pending}
                        onClick={() => { closeDetails(); handleDepartureContinue(); }}
                      >
                        {departureLabel}
                        <ArrowRight size={16} aria-hidden="true" />
                      </DriverActionButton>
                    ) : null}
                  </div>
                ) : (
                  <div className="rounded-[4px] border border-dashed border-[#d8e2d4] bg-[#faf7f1] px-4 py-5">
                    <p className="text-base font-semibold tracking-[-0.03em] text-slate-950">{isJourneyCompleted ? "Tournée terminée" : "Dernier arrêt de la tournée"}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{isJourneyCompleted ? "Tous les arrêts de cette tournée sont complétés." : "Des opérations restent à confirmer avant de terminer la tournée."}</p>
                    <DriverActionButton
                      compact
                      className={`mt-4 min-h-10 w-full px-4 text-[0.95rem] font-semibold ${DRIVER_PORTAL_BRAND_ACTION_CLASS_NAME}`}
                      type="button"
                      disabled={departureAction.pending || journeyCurrentGroupHasPendingConfirmations}
                      onClick={() => { closeDetails(); handleDepartureContinue(); }}
                    >
                      {departureLabel}
                      <Route size={16} aria-hidden="true" />
                    </DriverActionButton>
                  </div>
                )}
              </DriverPortalNextStop>
            </div>
              )}
            </div>
          ) : null}

          <ConfirmationModal
            cancelLabel="Annuler"
            confirmLabel="Confirmer le tonnage"
            description={`Le tonnage saisi (${journeyPendingQuantityConfirm ? formatQuantity(journeyPendingQuantityConfirm.parsedQuantity) : "—"}) est différent du tonnage prévu (${journeyPendingQuantityConfirm?.kind === "loading"
              ? (journeyPendingQuantityConfirm?.action?.quantityTons != null ? formatQuantity(journeyPendingQuantityConfirm.action.quantityTons) : "—")
              : (journeyPendingQuantityConfirm?.command?.quantityTons != null ? formatQuantity(journeyPendingQuantityConfirm.command.quantityTons) : "—")}). Voulez-vous confirmer cette valeur ?`}
            eyebrow="Tonnage différent"
            isOpen={!areAllPublishedTrucksDispatchDisabled && Boolean(journeyPendingQuantityConfirm) && journeyPendingQuantityConfirm?.stage !== "exceed_loaded"}
            onCancel={handleCancelJourneyQuantityConfirm}
            onConfirm={() => {
              void handleProceedJourneyQuantityConfirm();
            }}
            title="Confirmer le tonnage réel"
            tone="accent"
          />

          <ConfirmationModal
            cancelLabel="Annuler"
            confirmLabel="Confirmer le tonnage"
            description={`La quantité saisie (${journeyPendingQuantityConfirm ? formatQuantity(journeyPendingQuantityConfirm.parsedQuantity) : "—"}) est plus élevée que la quantité chargée (${journeyPendingQuantityConfirm?.loadedQuantity != null ? formatQuantity(journeyPendingQuantityConfirm.loadedQuantity) : "—"}). Voulez-vous quand même confirmer ?`}
            eyebrow="Quantité supérieure au chargé"
            isOpen={!areAllPublishedTrucksDispatchDisabled && journeyPendingQuantityConfirm?.stage === "exceed_loaded"}
            onCancel={handleCancelJourneyQuantityConfirm}
            onConfirm={() => {
              void handleProceedJourneyQuantityConfirm();
            }}
            title="Quantité supérieure à la quantité chargée"
            tone="danger"
          />

          <ConfirmationModal
            cancelLabel="Annuler"
            confirmLabel="Oui, réinitialiser"
            description={`Remet au départ la journée de ${driverDisplayName} et retire les validations déjà enregistrées.`}
            eyebrow="Réinitialisation"
            isOpen={!areAllPublishedTrucksDispatchDisabled && resetConfirmationsState.isOpen}
            onCancel={closeResetConfirmationsModal}
            onConfirm={openResetConfirmationsFinalModal}
            panelSize="compact"
            title="Réinitialiser les validations"
            tone="warning"
            warningMessage="Les tonnages réels confirmés seront aussi remis à zéro sur la journée visible."
          />

          <ConfirmationModal
            cancelLabel="Annuler"
            confirmLabel="Confirmer la réinitialisation"
            description="Cette action est définitive pour la journée visible. Les validations et tonnages réels saisis seront effacés."
            eyebrow="Confirmation finale"
            isOpen={!areAllPublishedTrucksDispatchDisabled && resetConfirmationsState.isFinalOpen}
            onCancel={closeResetConfirmationsFinalModal}
            onConfirm={() => {
              void handleResetDriverConfirmations();
            }}
            panelSize="compact"
            title="Confirmer définitivement ?"
            tone="danger"
            warningMessage="Une fois confirmée, la réinitialisation ne pourra pas être annulée depuis le portail."
          />
        </>
      )}
    </DriverPortalShell>
  );
}
